/**
 * serve.ts — WebSocket agent server.
 *
 * Exposes the agent runtime over a pluggable transport. Accepts AgentCommand
 * messages and streams AgentEvent responses. Uses WebSocketServerTransport for
 * the default transport (pass opts.transport to override for testing/stdio).
 *
 * Usage:
 *   skaile serve --project-dir /project --port 8080
 *
 * The platform's session manager connects via AgentClient + WebSocketClientTransport
 * to drive agent execution inside a local subprocess or Docker container.
 *
 * ─── Session Startup Pattern ───────────────────────────────────────────────────
 *
 * All three runner modes (REPL, flow, serve) follow the same 5-step setup:
 *   1. resolveSettings()          → driver, model, provider, API key
 *   2. loadAgentManifest()         → manifest (model/maxTurns/tools); optionally systemPrompt
 *   3. buildAgentResources()      → ConnectorManager, resourcePromptSection, mcpServers
 *   4. createDriver()             → AgentDriver with assembled systemPrompt
 *   5. driver.start()             → subprocess spawn / SDK import
 *
 * Differences between modes:
 *   REPL    — identity native (agentDir/agentName); systemPrompt = resource docs only; eager start
 *   Flow    — identity injected (full SOUL+RULES+knowledge); no transport; single orchestrator prompt
 *   Serve   — identity injected + external (SKAILE_SYSTEM_PROMPT_FILE); lazy start (first prompt cmd);
 *             adds EventNormalizer, resource watch callbacks, lifecycle command handling
 *
 * The shared `createAgentSession(opts)` helper in session-builder.ts extracts the
 * common setup sequence. serve.ts retains: EventNormalizer, lazy start (driverStarted flag),
 * transport setup, and lifecycle command handling.
 */

import {
  parseCodexCredential,
  parseCodexRefreshInput,
  parseCodexRefreshResult,
  validateCodexInit,
} from "./codex-init.js";
import {
  bindManagedCodexInit,
  validateManagedCodexHost,
  type ManagedCodexHostOptions,
} from "./managed-codex-bootstrap.js";
import {
  existsSync,
  type FSWatcher,
  watch as fsWatch,
  chmodSync,
  mkdirSync,
  readFileSync,
  writeFileSync,
} from "node:fs";
import { mkdtemp, rm } from "node:fs/promises";
import { homedir } from "node:os";
import { dirname, join } from "node:path";
import {
  deployCatalogEntry,
  findAiAssetsRoot,
  undeployCatalogEntry,
} from "@skaile/workspaces/asset-manager/catalog-deployer";
import {
  type AgentEvent as BridgeAgentEvent,
  EventNormalizer,
  CodexNativeStateUnavailable,
  probeResumeTranscript,
  SUPPORTED_CLOUDS,
} from "@skaile/workspaces/bridge";
import {
  buildSdkConnectorTools,
  buildSdkFlowTools,
  type ConnectorDeclaration,
  type ConnectorHandle,
  ConnectorManager,
  type FlowAdapter,
  InMemorySecretProvider,
  PreMintedSecretProvider,
  registerBuiltinConnectors,
  resolvedConfigToDeclarations,
  type TokenMediator,
  type TokenMediatorResult,
} from "@skaile/workspaces/connectors";
import {
  type AgentConfigProfile,
  type CatalogEntry,
  cloudConfigFromProfile,
  type CloudTransportConfig,
  type McpServerDeclaration,
  parseAssetRef,
  providerEnvKey,
  resolveAgentDir,
  resolveDriverPaths,
  resolveSkWorkspaceConfig,
  stageMaterializedSkills,
} from "@skaile/workspaces/core";
import { createLogger, WsLogSink } from "@skaile/workspaces/core/logging";
import {
  type FlowDefinition,
  loadFlow,
} from "@skaile/workspaces/factory-assets/connectors/flow/engine";
import { parseFlowDefinition } from "@skaile/workspaces/factory-assets/connectors/flow/contract";
import {
  driveOrchestratorTurn,
  SessionInlineSubFlowRunner,
  SessionNodeExecutor,
  SessionSubpromptRunner,
} from "@skaile/workspaces/factory-assets/connectors/flow";
import { resolveFlowPath } from "@skaile/workspaces/factory-assets/connectors/flow/run-flow";
import {
  renderStimulusPrompt,
  type TurnStimulus,
} from "@skaile/workspaces/factory-assets/connectors/flow/prompt-fragments";
import { WebSocketServerTransport } from "@skaile/workspaces/transport";
import type {
  AgentCommand,
  AgentEvent,
  ConnectorInfo,
  ConnectorMutateResponseEvent,
  ConnectorsAvailableEvent,
  FlowExecution,
  FlowExecutionStatus,
  FlowHydrateReconciliation,
  InputSchema,
  Logger,
  MountInfo,
  PendingGateDecision,
  ResolvedAgentConfig,
  ServerTransport,
  SessionInitAckEvent,
  StateChangedEvent,
} from "@skaile/workspaces/types";
import { PROTOCOL_VERSION } from "@skaile/workspaces/types";
// Static import so bun --compile bundles zod into the binary. Without this,
// dynamic import("zod") fails at runtime inside the /$bunfs/ virtual filesystem.
import { z as _z } from "zod";
import { createAiCredentialRefreshScheduler } from "./ai-credential-refresh.js";
import {
  buildRefreshCredentialInput,
  isTransientMintFailure,
  retryTransientMint,
} from "./ai-credential-retry.js";
import { createAiProviderConfigSwitcher } from "./ai-provider-switch.js";
import {
  handleCredentialRotatedSignal,
  isCredentialRotatedSignal,
  readCredentialRotatedConfigId,
} from "./credential-rotated.js";
import { planRuntimeSessionEmit, type RuntimeSessionSnapshot } from "./runtime-session-emit.js";
import { bootstrapCapabilityRegistry } from "./builtin-capabilities.js";
import { cleanupCloudCredentialFiles, resolveCloudSecretBundle } from "./cloud-bundle.js";
import {
  CompactionGate,
  type CompactionHold,
  guardTurnAgainstCompaction,
} from "./compaction-gate.js";
import { DriverSwapGate } from "./driver-swap-gate.js";
import { routeReply } from "./reply-routing.js";
import { CapabilityRegistry, computeCapabilitySignature } from "./capability-registry.js";
import {
  buildClientCapabilityHandler as buildClientCapabilityRoundtripHandler,
  DEFAULT_CAPABILITY_CALL_TIMEOUT_MS,
  dispatchRunnerCapabilityInvocation,
  type PendingCallEntry,
  rejectCapabilityOnApprovalDeny,
  resolveCapabilityCallTimeoutMs,
  resolveCapabilityResult,
} from "./capability-roundtrip.js";
import { CompactionOrchestrator } from "./compaction/index.js";
import { registerCompositionCapabilities } from "./composition/bootstrap.js";
import { type MountPoint, rewriteFileChangedPath } from "./file-changed-rewriter.js";
import { bootstrapRunnerLogStore } from "./logging-bootstrap.js";
import { installProcessSafetyNet, watchOutputStream } from "./process-safety-net.js";
import { buildCapabilitiesPromptSection } from "./prompt-assembly.js";
import { dispatchFleetHealthFlag } from "./fleet-health-flag-dispatcher.js";
import { dispatchRefreshToExposedGitMounts } from "./refresh-flag-dispatcher.js";
import { handleMountResourceRequest, handleResourceRequest } from "./resource-handler.js";
import {
  CLAUDE_CODE_CREDENTIALS_KEY,
  ensureClaudeSettingsDisablesConnectors,
  extractClaudeAiOauthExpiresAt,
  mergeAndRewriteCredentialsFile,
  readCurrentClaudeAccessToken,
  writeClaudeCodeCredentialsFile,
} from "./serve-credentials.js";
import {
  type AgentSession,
  type AgentSessionConfig,
  createAgentSession,
  createManagedCodexSubpromptDriver,
} from "./session-builder.js";
import { registerCodexFlowTools } from "./codex-resource-tools.js";
import { wireSessionCapabilities } from "./session-capability-wiring.js";
import { createSessionStimulusBus, type SessionStimulusBus } from "./session-stimulus.js";
import { TurnStatusTracker } from "./turn-status-replay.js";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Pull catalog entries from the active session's runtime assets, with a safe
 * fallback to empty when the session was not yet created or the resolver
 * failed. The session-builder caches the same `RuntimeAssetsResult` across
 * the session lifetime so live-update paths see a stable view.
 */
function getSessionCatalogEntries(session: AgentSession | undefined): CatalogEntry[] {
  return session?.runtimeAssets?.catalogEntries ?? [];
}

/**
 * Emit a `system_prompt_composed` event carrying the labeled section
 * breakdown from the freshly-built {@link AgentSession}. Called from every
 * `createAgentSession` consumer in serve.ts (initial bootstrap, configure
 * re-runs, add_resource flow-connector path, aiResources skill changes,
 * session_init provisioned path) so the platform's debug panel always sees
 * the prompt the agent is actually receiving.
 *
 * Keeping the emission centralised in serve.ts (rather than inside
 * session-builder.ts) preserves the builder's transport-agnostic contract —
 * forge apps and the CLI consume the same builder without a transport.
 *
 * Spec: `docs/superpowers/specs/2026-05-13-platform-agent-prompt-design.md`
 * § 3.3.2 Re-emission requirement.
 * @since 2026-05
 */
export function emitSystemPromptComposed(
  agentSession: AgentSession,
  sendEvent: (e: AgentEvent) => void,
): void {
  sendEvent({
    type: "system_prompt_composed",
    sections: agentSession.systemPromptSections,
    composedAt: new Date().toISOString(),
  });
}

/** Passes `ensureDriverStarted` makes before it gives up losing to a swap. */
const DRIVER_START_ATTEMPTS = 3;

/** Collect installed @skaile/* package versions. Cached per process. */
const FRAMEWORK_PACKAGES = [
  "@skaile/workspaces/runner",
  "@skaile/workspaces/bridge",
  "@skaile/workspaces/core",
  "@skaile/workspaces/types",
  "@skaile/workspaces/factory-assets",
  "@skaile/workspaces/connectors",
  "@skaile/workspaces/resolver",
  "@skaile/workspaces/transport",
  "@skaile/workspaces/workspace-plugin",
] as const;

let _frameworkVersions: Record<string, string> | null = null;
function getFrameworkVersions(): Record<string, string> {
  if (_frameworkVersions) return _frameworkVersions;
  const versions: Record<string, string> = {};
  for (const pkg of FRAMEWORK_PACKAGES) {
    try {
      const pjPath = require.resolve(`${pkg}/package.json`);
      const pj = JSON.parse(readFileSync(pjPath, "utf-8"));
      versions[pkg.replace("@skaile/", "")] = pj.version;
    } catch {
      // not installed
    }
  }
  _frameworkVersions = versions;
  return versions;
}

// ---------------------------------------------------------------------------
// Resource lifecycle helpers
// ---------------------------------------------------------------------------

/**
 * Split a unified `ConnectorManager`'s connectors into the wire-shape
 * `mounts` / `connectors` arrays the `resources_available` event expects.
 *
 * Filesystem-face connectors (those with a `mountPath`) are reported as
 * mounts; the rest are reported as connectors. The unified manager owns both,
 * so this is purely a wire-format projection.
 */
function splitConnectorsForWire(manager: ConnectorManager | null): {
  mounts: MountInfo[];
  connectors: ConnectorInfo[];
} {
  const mounts: MountInfo[] = [];
  const connectors: ConnectorInfo[] = [];
  if (!manager) return { mounts, connectors };
  for (const c of manager.listConnectors()) {
    if (typeof c.mountPath === "string") {
      let source = "";
      try {
        source = manager.get(c.id).declaration.mount?.source ?? "";
      } catch {
        /* connector vanished between list and get — best-effort */
      }
      mounts.push({
        id: c.id,
        driver: c.driver,
        ...(c.label ? { label: c.label } : {}),
        access: c.access,
        mountPath: c.mountPath,
        // A deferred (background) mount is pre-registered with its path but
        // status "connecting" — report it as not-yet-mounted; the platform
        // flips it on the subsequent connector_status "connected" event.
        mounted: c.status === "connected",
        source,
      });
    } else {
      connectors.push({
        id: c.id,
        driver: c.driver,
        ...(c.label ? { label: c.label } : {}),
        access: c.access,
        operations: manager.getOperations(c.id).map((op) => op.name),
      });
    }
  }
  return { mounts, connectors };
}

/** Minimal view of `ExternalMcpManager` needed to render the wire payload. */
export type McpServerLister = {
  listServers(): Array<{ id: string; transport: "stdio" | "sse" | "http"; toolCount: number }>;
};

/**
 * Which ConnectorManager the outer `resourceManager` should point at after a
 * dispose+recreate. `disposed` (captured pre-dispose) distinguishes the session's
 * own dead manager from a live lazily-created one that must survive.
 * Branches, known gap and rationale: `_devlog/notes/2026-07-17-recreate-resource-manager-repoint.md`.
 */
export function pickRepointedManager<T>(
  next: T | null,
  current: T | null,
  disposed: T | null,
): T | null {
  if (next !== null) return next;
  if (current !== null && current === disposed) return null;
  return current;
}

/** Preserve standalone SDK compatibility; managed Codex refreshes its native tool snapshot. */
export function supportsLiveToolUpdates(driverType?: string, managedCodex = false): boolean {
  return driverType !== "claude-sdk" && !(driverType === "codex" && managedCodex);
}

/**
 * Build the `resources_available` wire payload: connector/mount split plus the
 * live MCP server list. Each MCP server is stamped with `live` — whether the
 * agent reaches its tools on the next turn without a session restart.
 *
 * A server is live when `driverLive` (bridge drivers recompose tools per turn)
 * OR it is in `bakedServerIds` — baked into the current driver at its
 * (re)creation, or stamped by `registerLiveAttachedMcp` for a hot-add whose
 * rebuild is already queued. A server not in the set gets `live: driverLive`;
 * omitting the argument is equivalent to an empty set.
 *
 * Exported for unit testing.
 */
export function buildResourcesAvailablePayload(
  manager: ConnectorManager | null,
  mcpManager: McpServerLister | null,
  driverLive: boolean,
  bakedServerIds?: ReadonlySet<string>,
): ConnectorsAvailableEvent {
  const { mounts, connectors } = splitConnectorsForWire(manager);
  const mcp_servers = (mcpManager?.listServers() ?? []).map((s) => ({
    ...s,
    live: driverLive || (bakedServerIds?.has(s.id) ?? false),
  }));
  return { type: "resources_available", mounts, connectors, mcp_servers };
}

/**
 * Whether a flow run has reached a status it can never leave.
 *
 * `failed` is deliberately NOT terminal: `retryNode` walks a recoverable
 * failure back to `available` and the adapter's recompute rolls the flow back
 * to `running`. `paused` resumes the same way. Only `complete` and `cancelled`
 * are one-way.
 *
 * Exported for unit testing.
 */
export function isTerminalFlowStatus(status: FlowExecutionStatus): boolean {
  return status === "complete" || status === "cancelled";
}

/** The slice of a registered run the terminal-status readers need. */
type FlowStatusProbe = { adapter: Pick<FlowAdapter, "getExecution">; handle: ConnectorHandle };

/**
 * Whether any registered run is still live. Gates `flow.start` — a finished
 * run must not block the next one.
 *
 * Exported for unit testing.
 */
export function hasNonTerminalFlow(flows: Iterable<FlowStatusProbe>): boolean {
  for (const f of flows) {
    if (!isTerminalFlowStatus(f.adapter.getExecution(f.handle).status)) return true;
  }
  return false;
}

/**
 * Register a run, dropping any finished ones it supersedes. Returns the keys
 * removed (for logging).
 *
 * Registration is the ONLY point that ages the map out, and deliberately so: a
 * finished run must stay addressable (the bare-`"flow"` id rewrite, and the
 * replay that delivers its terminal snapshot to a host that was detached when
 * it settled) right up until a new run actually replaces it. Deleting on the
 * terminal transition instead would drop both.
 *
 * Exported for unit testing.
 */
export function registerActiveFlow<T extends FlowStatusProbe>(
  flows: Map<string, T>,
  runId: string,
  entry: T,
): string[] {
  const removed: string[] = [];
  for (const [key, existing] of flows) {
    if (isTerminalFlowStatus(existing.adapter.getExecution(existing.handle).status)) {
      flows.delete(key);
      removed.push(key);
    }
  }
  flows.set(runId, entry);
  return removed;
}

/** The order-critical steps of a flow-connector bring-up, in the order they run. */
export interface FlowConnectorSetupDeps<M> {
  /** Recreate the agent session when the driver has already started. */
  recreateForFlowTools: () => Promise<boolean>;
  /** The ConnectorManager the flow connector belongs on, lazily created if absent. */
  resolveManager: () => M;
  /** Register the flow connector on that manager. */
  connect: (manager: M) => Promise<ConnectorHandle>;
}

/**
 * Bring up a flow connector on the manager that SURVIVES a driver recreate.
 *
 * The recreate disposes the session's ConnectorManager (`dispose()` →
 * `disconnectAll()` clears `active`) and repoints the runner at the new
 * session's own. Connecting first left the run registered on a torn-down
 * manager — and `buildSdkFlowTools` closes over it, so its tool handlers were
 * bound to the dead one too, wedging the run at `running` forever
 * (workspaces#484). Hence: recreate, THEN resolve, THEN connect.
 *
 * Exported for unit testing — this ordering is the fix.
 */
export async function setUpFlowConnector<M>(
  deps: FlowConnectorSetupDeps<M>,
): Promise<{ manager: M; handle: ConnectorHandle; recreated: boolean }> {
  const recreated = await deps.recreateForFlowTools();
  const manager = deps.resolveManager();
  const handle = await deps.connect(manager);
  return { manager, handle, recreated };
}

/**
 * Build the `state_changed` events that replay the current snapshot of every
 * live state-bearing store, for delivery when a subscriber (re)attaches.
 *
 * `sendEvent` is fire-and-forward: state emitted before the host's sync loop
 * attaches is lost. Rather than buffer the lost events, we re-emit current
 * snapshots — idempotent latest-wins on the host, so a late subscriber
 * converges and an already-current one re-applies identical state.
 *
 * Flow stores read the authoritative FlowExecution straight off the adapter
 * (same shape `onStateChange` emits) so there is no per-type read-op guessing.
 * Shared-state stores are identified by the xstate-store `onStoreChange` hook
 * and read via `get`; git/mount/MCP connectors have no state op and are skipped.
 *
 * A run that already reached a terminal status is replayed too, deliberately
 * (workspaces#413): the turn loop keeps running with no host attached, so
 * `complete`/`cancelled` can be reached mid-detach — and this replay is then
 * the ONLY path by which the host ever learns. Filtering it would leave the
 * platform's persisted row stuck non-terminal and later re-hydrate a finished
 * run. Registration prunes the settled entry instead, so the re-emit is
 * bounded, not forever.
 *
 * Exported for unit testing.
 */
export async function buildStoreSnapshotReplay(
  manager: ConnectorManager | null,
  activeFlows: Iterable<{ runId: string; adapter: FlowAdapter; handle: ConnectorHandle }>,
): Promise<StateChangedEvent[]> {
  const events: StateChangedEvent[] = [];

  for (const { runId, adapter, handle } of activeFlows) {
    events.push({
      type: "state_changed",
      store: `flow:${runId}`,
      state: adapter.getExecution(handle) as unknown as Record<string, unknown>,
    });
  }

  for (const { id } of manager?.listConnectors?.() ?? []) {
    // Everything after listConnectors runs inside the try: the loop awaits, so
    // a concurrent disconnect can make get()/executeOp() throw mid-iteration —
    // that must drop only this store, never the already-collected snapshots.
    try {
      if (!manager?.has(id)) continue;
      const connector = manager.get(id).connector as { onStoreChange?: unknown };
      if (typeof connector.onStoreChange !== "function") continue;
      const snapshot = await manager.executeOp(id, "get", {});
      events.push({
        type: "state_changed",
        store: id,
        state: snapshot ? JSON.parse(snapshot) : {},
      });
    } catch {
      // Store disappeared or is unreadable now — it re-emits on next mutation.
    }
  }

  return events;
}

/**
 * Phase 4.8 — Claude Code subscription credentials provisioning.
 *
 * The constant + the atomic rewrite helper now live in `./serve-credentials.ts`
 * so the AI 401 mediation helper can reuse them without importing from this
 * (large) module. Re-exported here for back-compat with existing test
 * imports of `serve.js`.
 *
 * @docLink packages/runner/dev-guide#environment-variables
 */
export {
  CLAUDE_CODE_CREDENTIALS_KEY,
  ensureClaudeSettingsDisablesConnectors,
  extractClaudeAiOauthExpiresAt,
  writeClaudeCodeCredentialsFile,
};

/**
 * Write the `disableClaudeAiConnectors` guard into the session's user-scope
 * Claude settings, logging instead of throwing: the driver's
 * `ENABLE_CLAUDEAI_MCP_SERVERS=false` env flag is the independent fallback,
 * so a failed settings write must never take the runner down.
 *
 * Called at every site that puts a subscription credential on disk, and only
 * there: outside a container (`mode=local`, most runner tests) `HOME` is the
 * developer's real home, and a bare-startup write would silently switch off
 * the connectors of their own interactive Claude Code.
 */
async function guardClaudeAiConnectors(
  home: string,
  serverLog: ReturnType<typeof createLogger>,
): Promise<void> {
  try {
    const path = await ensureClaudeSettingsDisablesConnectors(home);
    serverLog.info("claude.ai connectors disabled in user settings", { path });
  } catch (err) {
    serverLog.warn(
      "failed to disable claude.ai connectors in user settings — relying on env only",
      {
        home,
        error: err instanceof Error ? err.message : String(err),
      },
    );
  }
}

/**
 * Idempotently ensure `~/.gitconfig` includes the runner-managed gitconfig
 * via `[include] path = <managedGitconfigPath>`. Fallback path for shells
 * that strip `GIT_CONFIG_GLOBAL`. The function is idempotent — running it
 * twice produces the same file content.
 *
 * Exported for unit tests.
 * @docLink packages/runner/capabilities#ensure-git-config-include
 */
export function ensureGitConfigInclude(managedGitconfigPath: string): void {
  const home = process.env.HOME ?? homedir();
  if (!home) return;
  const gitconfigPath = join(home, ".gitconfig");
  let existing = "";
  try {
    existing = readFileSync(gitconfigPath, "utf-8");
  } catch {
    // file doesn't exist — fall through and create
  }
  // Bail if any include block already references our managed file. Walk the
  // gitconfig line-by-line tracking the current section header so a stray
  // `path = …` key inside an unrelated section (e.g. `[some-other] path = …`)
  // does not trigger a false-positive match. Git only honours `path` keys
  // under `[include]` and `[includeIf "<cond>"]`; mirror that contract here.
  const sectionRe = /^\s*\[([^\]\s]+)(?:\s+"[^"]*")?\]\s*$/;
  const pathKeyRe = /^\s*path\s*=\s*(.+?)\s*$/;
  let currentSection: string | null = null;
  for (const line of existing.split(/\r?\n/)) {
    const sectionMatch = line.match(sectionRe);
    if (sectionMatch) {
      currentSection = sectionMatch[1]?.toLowerCase() ?? null;
      continue;
    }
    if (currentSection !== "include" && currentSection !== "includeif") continue;
    const pathMatch = line.match(pathKeyRe);
    if (pathMatch?.[1] === managedGitconfigPath) return;
  }
  if (!existsSync(dirname(gitconfigPath))) {
    mkdirSync(dirname(gitconfigPath), { recursive: true });
  }
  const block =
    `${existing.trimEnd()}\n` +
    `# skaile-runner: managed-gitconfig include\n` +
    `[include]\n\tpath = ${managedGitconfigPath}\n`;
  writeFileSync(gitconfigPath, block, { mode: 0o644 });
}

// v3 protocol — handleRemountMount removed alongside the RemountMountCommand
// wire type. Hot remounting is now reachable through the runner.add_mount
// capability (the platform's invokeRunnerCapability dispatch path), which
// handles the disconnect+connect semantics via the ConnectorManager surface.

/**
 * Tier-2 managed-gitconfig env propagation (git credential tiers, Step 1A).
 *
 * The runner owns one path — `<projectDir>/.skaile/managed-gitconfig` — and
 * exposes it to every spawned agent shell via `GIT_CONFIG_GLOBAL`. The git
 * mount driver writes per-mount tagged blocks into this file when (and only
 * when) `exposeAccessToken: true` is set on the mount declaration. Bridge
 * drivers inherit the runner's env when spawning agent processes, so no
 * change is required on their side.
 *
 * Belt-and-suspenders: also write `[include] path = …` into `~/.gitconfig`
 * so shells that strip `GIT_CONFIG_GLOBAL` still pick the file up. The
 * include is idempotent — a second run will not append a duplicate line.
 *
 * Spec: `_devlog/specs/2026-05-05-git-credential-tiers.md`.
 */
function setupManagedGitconfig(
  projectDir: string,
  serverLog: ReturnType<typeof createLogger>,
): void {
  const managedGitconfigPath = join(projectDir, ".skaile", "managed-gitconfig");
  process.env.GIT_CONFIG_GLOBAL = managedGitconfigPath;
  try {
    ensureGitConfigInclude(managedGitconfigPath);
  } catch (err) {
    serverLog.warn("failed to write ~/.gitconfig include — relying on env only", {
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Configuration for {@link startAgentServer}.
 * @docLink packages/runner/dev-guide#flow-execution-turn-based-model
 */
export interface ServeOptions {
  /** Trusted launcher inputs only; credentials arrive through validated session_init. */
  managedCodex?: ManagedCodexHostOptions;
  /**
   * WebSocket server port. Defaults to `8080`.
   * Ignored when a custom {@link transport} is provided.
   */
  port?: number;
  /** Absolute path to the project working directory. */
  projectDir: string;
  /**
   * GitAgent definition directory (`agent.yaml`, `SOUL.md`, `RULES.md`,
   * `knowledge/`). When omitted, resolved from `skaile.yaml` via
   * `resolveAgentDir`. If unresolvable, no agent definition is loaded and the
   * agent starts without an identity prompt.
   */
  agentDir?: string;
  /**
   * Exit the process with 141 when stdout's pipe dies. Defaults to `false`,
   * because `startAgentServer` is a library entry point and an embedder's
   * stdout is not ours to kill. The `skaile serve` CLI, which does own its
   * process, opts in — see the runner's `process-safety-net`.
   *
   * The runaway-logging fix does not depend on this: the sinks go quiet
   * either way. Only the exit is gated.
   */
  exitOnBrokenPipe?: boolean;
  /** Agent driver backend override (e.g. `"omp"`, `"claude-sdk"`). */
  driver?: string;
  /** LLM model name override. */
  model?: string;
  /** LLM provider override (e.g. `"anthropic"`, `"openrouter"`). */
  provider?: string;
  /** Directory of deployed prompt/command files appended to the system prompt. */
  promptsDir?: string;
  /** Stable session ID for conversation continuity across agent turns. */
  sessionId?: string;
  /**
   * OMP driver session ID to resume. When set, OMP is spawned with
   * `--session <id>` to restore its native conversation context without
   * needing `<conversation_history>` injection. For OMP driver only.
   */
  resumeSessionId?: string;
  /** Called for each diagnostic log line. Defaults to `console.log`. */
  onLog?: (line: string) => void;
  /**
   * Pre-built transport to use instead of the default
   * `WebSocketServerTransport`. Useful for testing (in-process transport,
   * stdio transport) or embedding the server in another process.
   */
  transport?: ServerTransport;
  /**
   * Bearer token the default WebSocket server requires from connecting clients.
   * Falls back to `SKAILE_WS_AUTH_TOKEN`. When neither is set the server accepts
   * unauthenticated connections (back-compatible default). Ignored when a custom
   * {@link transport} is provided.
   */
  authToken?: string;
}

// ---------------------------------------------------------------------------
// Server
// ---------------------------------------------------------------------------

/**
 * Map a connector driver to the platform `host.refresh_credential` `kind`, or
 * `null` when the driver has no in-container refresh path. The platform handler
 * mints `git-mount`, `sharepoint-mount`, and `webdav-mount`, and the matching
 * rclone drivers carry the full apply path (proactive scheduler + reactive
 * `onAuthError` → re-render INI → stop+respawn), so all three refresh in place.
 * `googledrive` is intentionally excluded: its driver has no refresh controller
 * yet, so mapping it would mint tokens nothing applies — wire the driver first.
 */
export function connectorRefreshKind(driver?: string): string | null {
  switch (driver) {
    case "git":
      return "git-mount";
    case "sharepoint":
      return "sharepoint-mount";
    case "webdav":
      return "webdav-mount";
    default:
      return null;
  }
}

/**
 * Build the connector token-refresh mediator. The `ConnectorManager`'s pre-mint
 * wrapper serves `reason:"initial"` from the pre-minted credentials and forwards
 * every `refresh` / `retry-401` here, so a backend-auth git mount rotates its
 * token through `host.refresh_credential` instead of going stale. The platform
 * handler resolves the credential by **provider link id**, so this passes
 * `id: providerLinkId` (NOT the connector id). Returns a structured
 * `not-configured` mint — never throwing — when there is no transport, the
 * driver has no backend refresh path, or the declaration carries no
 * `providerLinkId` (standalone/CLI and non-git connectors degrade as before).
 */
export function buildConnectorTokenMediator(deps: {
  isConnected: () => boolean;
  invokeRemote: (name: string, input: unknown) => Promise<unknown>;
  log: (line: string) => void;
}): TokenMediator {
  return async ({ connectorId, reason, driver, providerLinkId }) => {
    if (!deps.isConnected()) {
      return {
        ok: false,
        code: "not-configured",
        message: "standalone runner cannot mediate connector refresh",
      };
    }
    const kind = connectorRefreshKind(driver);
    if (!kind) {
      return {
        ok: false,
        code: "not-configured",
        message: `connector '${connectorId}' (driver=${driver ?? "unknown"}) has no backend refresh path`,
      };
    }
    if (!providerLinkId) {
      return {
        ok: false,
        code: "not-configured",
        message: `connector '${connectorId}' is missing a providerLinkId; cannot refresh`,
      };
    }
    try {
      // No `rejection`: the field is scoped to the `ai-credentials` input and
      // these are mount kinds. Nothing on this path classified a rejection
      // anyway — the driver picked the `reason`, not a classifier.
      const raw = (await deps.invokeRemote("host.refresh_credential", {
        kind,
        id: providerLinkId,
        reason,
      })) as TokenMediatorResult | null | undefined;
      // Validate the discriminator before handing the result to the git driver:
      // a malformed `{ ok: true, token: null }` would otherwise surface as a
      // confusing failure deep in the credential helper.
      if (typeof raw?.ok !== "boolean" || (raw.ok && typeof raw.token !== "string")) {
        deps.log(
          `[serve] connector refresh returned a malformed mint (id=${connectorId}, kind=${kind})`,
        );
        return { ok: false, code: "backend-error", message: "malformed mint from host" };
      }
      if (!raw.ok) {
        deps.log(
          `[serve] connector refresh failed (id=${connectorId}, kind=${kind}, code=${raw.code})`,
        );
      }
      return raw;
    } catch (err) {
      const m = err instanceof Error ? err.message : String(err);
      return { ok: false, code: "backend-error", message: m };
    }
  };
}

/**
 * Decision the runner makes for a `connector_mutate` that targets the `flow`
 * connector. Pure so it can be unit-tested without a live server. See
 * `MIGRATION-flow-connector.md` for the wire contract this encodes.
 *
 * - `create-start`  — fresh run: auto-create `flow:<runId>` from `{ flow, seed }`.
 * - `create-hydrate`— cold-container rehydration (Protocol 3.5): auto-create
 *                      `flow:<runId>` directly from the `{ state, flow }` snapshot.
 * - `dispatch`      — route to `ConnectorManager.executeOp(dispatchId, op, payload)`,
 *                     rewriting a bare `"flow"` id to the singleton active run.
 * - `reject`        — malformed / conflicting flow op; caller logs `reason` loudly
 *                     instead of silently falling through.
 */
export type FlowMutatePlan =
  | {
      kind: "create-start";
      flowDef: FlowDefinition;
      seed: { runId: string; startedBy: string; autonomousMode?: boolean };
    }
  | { kind: "create-hydrate"; flowDef: FlowDefinition; execution: FlowExecution }
  | { kind: "dispatch"; dispatchId: string }
  | { kind: "reject"; reason: string };

function normalizeFlowMutateDefinition(
  authored: unknown,
): { ok: true; flowDef: FlowDefinition } | { ok: false; reason: string } {
  const parsed = parseFlowDefinition(authored);
  if (parsed.ok) return { ok: true, flowDef: parsed.data };
  const details = parsed.errors
    .map((issue) => `${issue.path || "<root>"}: ${issue.message}`)
    .join("; ");
  return { ok: false, reason: `invalid flow definition: ${details}` };
}

/**
 * Resolve a `connector_mutate` into a {@link FlowMutatePlan}.
 *
 * Hosts address the flow connector by its bare driver id `"flow"`; the runner
 * registers the per-run connector at `flow:<runId>` and enforces one *running*
 * flow per session. `start` and (on a cold container) `hydrate` auto-create
 * that connector; every other op dispatches to the existing run.
 */
export function planFlowMutate(
  cmd: { id: string; op: string; payload?: Record<string, unknown> },
  ctx: {
    /** `resourceManager.has(cmd.id)` — a connector is registered under this exact id. */
    targetExists: boolean;
    /** The registered target is a `flow` connector. */
    isExistingFlow: boolean;
    /**
     * `activeFlows.size > 0` — any run is registered, terminal or not. Gates
     * the cold-bootstrap `hydrate` branch: re-creating a connector at an id
     * that already exists silently overwrites the live handle.
     */
    hasActiveFlow: boolean;
    /**
     * A registered run is still in a non-terminal status. Gates `start` — a
     * finished run must not block the next one.
     */
    hasRunningFlow: boolean;
    /** Connector id of the singleton active run, or null when none. */
    onlyActiveConnectorId: string | null;
  },
): FlowMutatePlan {
  const isBareFlowId = cmd.id === "flow";
  const flowIntercept = ctx.isExistingFlow || isBareFlowId;
  const payload = cmd.payload ?? {};

  if (cmd.op === "start" && flowIntercept) {
    const authoredFlow = payload.flow;
    const seed = payload.seed as
      | { runId: string; startedBy: string; autonomousMode?: boolean }
      | undefined;
    if (!authoredFlow || !seed?.runId || !seed?.startedBy) {
      return {
        kind: "reject",
        reason:
          "flow.start payload missing required fields (need payload.flow.id, payload.seed.runId, payload.seed.startedBy)",
      };
    }
    const normalized = normalizeFlowMutateDefinition(authoredFlow);
    if (!normalized.ok) {
      return { kind: "reject", reason: `flow.start ${normalized.reason}` };
    }
    if (ctx.hasRunningFlow) {
      return {
        kind: "reject",
        reason:
          "flow.start ignored: a flow is still running (one active flow per session; cancel it, then start the next)",
      };
    }
    return { kind: "create-start", flowDef: normalized.flowDef, seed };
  }

  if (cmd.op === "hydrate" && flowIntercept && !ctx.hasActiveFlow) {
    // Bootstrap a cold container: no run exists yet, so create the connector
    // directly from the snapshot. runId/startedBy/autonomousMode are read from
    // `state` itself; `flow` is required because the runner keeps its own copy
    // of the flow definition rather than resolving it from disk.
    const state = payload.state as FlowExecution | undefined;
    if (
      !state ||
      typeof state !== "object" ||
      typeof (state as { runId?: unknown }).runId !== "string"
    ) {
      return {
        kind: "reject",
        reason:
          "flow.hydrate payload missing required field payload.state (FlowExecution snapshot)",
      };
    }
    const authoredFlow = payload.flow;
    if (!authoredFlow) {
      return {
        kind: "reject",
        reason:
          "flow.hydrate cannot bootstrap a cold container without payload.flow (FlowDefinition required when no flow is active)",
      };
    }
    const normalized = normalizeFlowMutateDefinition(authoredFlow);
    if (!normalized.ok) {
      return { kind: "reject", reason: `flow.hydrate ${normalized.reason}` };
    }
    return { kind: "create-hydrate", flowDef: normalized.flowDef, execution: state };
  }

  // Generic dispatch. Rewrite a bare "flow" id to the singleton active run so
  // the ConnectorManager lookup (keyed by `flow:<runId>`) succeeds.
  let dispatchId = cmd.id;
  if (!ctx.targetExists && isBareFlowId && ctx.onlyActiveConnectorId) {
    dispatchId = ctx.onlyActiveConnectorId;
  }
  return { kind: "dispatch", dispatchId };
}

// ---------------------------------------------------------------------------
// Flow-connector readiness (Protocol 3.7)
// ---------------------------------------------------------------------------

/** Fallback bound for {@link resolveFlowMutateConnectorWaitMs}. */
export const DEFAULT_FLOW_MUTATE_CONNECTOR_WAIT_MS = 8000;

/**
 * Read the flow-connector readiness bound from
 * `SKAILE_FLOW_MUTATE_CONNECTOR_WAIT_MS`, falling back to
 * {@link DEFAULT_FLOW_MUTATE_CONNECTOR_WAIT_MS} on unset / unparseable /
 * non-positive input.
 *
 * Gotcha: the platform's `connector_mutate` ack timeout is deliberately sized
 * *above* this value, so raising it without raising the host side turns a slow
 * wait into a host-visible timeout.
 */
export function resolveFlowMutateConnectorWaitMs(
  env: Record<string, string | undefined> = process.env,
): number {
  const parsed = Number.parseInt(env.SKAILE_FLOW_MUTATE_CONNECTOR_WAIT_MS ?? "", 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_FLOW_MUTATE_CONNECTOR_WAIT_MS;
}

/** Bounded readiness gate for the flow connector. See {@link createFlowConnectorReadiness}. */
export interface FlowConnectorReadiness {
  /**
   * `true` when a flow connector exists already or registers before the bound.
   *
   * `isSatisfied` overrides the gate's default predicate for this wait only.
   * Pass one when the caller needs a *specific* run rather than "any active
   * flow" — a terminal run lingers in `activeFlows` until the next registers
   * (see `registerActiveFlow`), so the default would resolve early for a
   * caller addressing an explicit `flow:<runId>`.
   */
  wait(timeoutMs: number, isSatisfied?: () => boolean): Promise<boolean>;
  /** Release every parked waiter whose predicate now holds. Called as soon as a run registers. */
  drain(): void;
  /** Waiters still parked — test seam for the no-leak assertion. */
  pending(): number;
}

/**
 * Narrower sibling of the session-wide `sessionReady` gate, for the flow
 * connector specifically: a flow-targeted mutate that loses the wake race to
 * the bootstrap `hydrate` (observed 2 ms apart in production) parks here
 * instead of failing permanently.
 *
 * Invariants: `wait` clears its timer when drained and removes its own waiter
 * when it times out — this runs on every gated mutate, so either leak is real.
 * `drain` splices the list before invoking, so a waiter registered *during* a
 * drain is not dropped.
 */
export function createFlowConnectorReadiness(isReady: () => boolean): FlowConnectorReadiness {
  interface Waiter {
    satisfied: () => boolean;
    release: () => void;
  }
  const waiters: Waiter[] = [];
  return {
    wait(timeoutMs: number, isSatisfied?: () => boolean): Promise<boolean> {
      const satisfied = isSatisfied ?? isReady;
      if (satisfied()) return Promise.resolve(true);
      return new Promise<boolean>((resolve) => {
        let timer: ReturnType<typeof setTimeout>;
        const waiter: Waiter = {
          satisfied,
          release: () => {
            clearTimeout(timer);
            resolve(true);
          },
        };
        timer = setTimeout(() => {
          const idx = waiters.indexOf(waiter);
          if (idx >= 0) waiters.splice(idx, 1);
          resolve(false);
        }, timeoutMs);
        // Never hold the process open on a gate nobody will drain.
        (timer as unknown as { unref?: () => void }).unref?.();
        waiters.push(waiter);
      });
    },
    drain(): void {
      // Splice first so a waiter parked *during* this drain survives to the
      // next one; re-park the ones whose own predicate does not hold yet.
      for (const waiter of waiters.splice(0)) {
        if (waiter.satisfied()) waiter.release();
        else waiters.push(waiter);
      }
    },
    pending: () => waiters.length,
  };
}

// ---------------------------------------------------------------------------
// Hydrate reconciliation (Protocol 3.7)
// ---------------------------------------------------------------------------

/**
 * Shallow structural check of a replayed gate response against the node's own
 * {@link InputSchema}.
 *
 * Invariants: never throws, returns a boolean, and never reads the response
 * into a log line, an error message or the acknowledgement — it is unbounded
 * user content. An unrecognised or absent schema is permissive on purpose: a
 * schema the runner does not understand must not wedge a legitimate replay.
 */
export function validateInputResponse(schema: InputSchema | undefined, response: unknown): boolean {
  if (!schema || typeof schema !== "object") return true;
  switch (schema.kind) {
    case "text":
      return typeof response === "string";
    case "choice": {
      const ids = new Set((Array.isArray(schema.options) ? schema.options : []).map((o) => o?.id));
      if (schema.multiple) {
        return (
          Array.isArray(response) && response.every((v) => typeof v === "string" && ids.has(v))
        );
      }
      return typeof response === "string" && ids.has(response);
    }
    case "form": {
      if (!response || typeof response !== "object" || Array.isArray(response)) return false;
      const value = response as Record<string, unknown>;
      for (const field of Array.isArray(schema.fields) ? schema.fields : []) {
        if (!field?.id) continue;
        const v = value[field.id];
        if (field.required && (v === undefined || v === null || v === "")) return false;
        if (v === undefined || v === null) continue;
        if (field.type === "number" && typeof v !== "number") return false;
        if (field.type === "boolean" && typeof v !== "boolean") return false;
        if (field.type === "choice" && Array.isArray(field.options) && field.options.length > 0) {
          if (!field.options.some((o) => o?.id === v)) return false;
        }
      }
      // Unknown extra keys are tolerated — the schema may have shrunk since.
      return true;
    }
    case "file":
      // File refs are host-shaped; stay permissive.
      return Array.isArray(response) || (typeof response === "object" && response !== null);
    default:
      return true;
  }
}

/**
 * Classify each `payload.pendingDecisions` entry of a flow `hydrate` against
 * the snapshot the host sent in the same command, splitting them into the ones
 * the caller should apply and a structured report for the acknowledgement.
 *
 * Pure — no I/O, no logging. `pending` is untrusted wire data of unknown shape.
 *
 * Invariants:
 * - Parked-ness is checked *before* decided-ness, so a re-parked node reads as
 *   `not_parked` rather than `already_decided`.
 * - An entry with no usable `nodeId` is dropped silently; there is nothing to
 *   report it against. Everything else lands in exactly one of the two lists.
 * - A skip entry carries only `{ nodeId, reason }` — never the decision payload.
 */
export function reconcilePendingDecisions(
  execution: FlowExecution,
  pending: unknown,
): { toApply: PendingGateDecision[]; reconciled: FlowHydrateReconciliation } {
  const toApply: PendingGateDecision[] = [];
  const reconciled: FlowHydrateReconciliation = { applied: [], skipped: [] };
  if (!Array.isArray(pending)) return { toApply, reconciled };

  const nodes = execution?.nodes ?? {};
  for (const raw of pending) {
    if (!raw || typeof raw !== "object") continue;
    const entry = raw as Record<string, unknown>;
    const nodeId = typeof entry.nodeId === "string" && entry.nodeId ? entry.nodeId : null;
    const kind = entry.kind;
    if (kind !== "approval" && kind !== "input") {
      if (nodeId) reconciled.skipped.push({ nodeId, reason: "payload_invalid" });
      continue;
    }
    if (!nodeId) continue;

    const node = nodes[nodeId];
    if (!node) {
      reconciled.skipped.push({ nodeId, reason: "unknown_node" });
      continue;
    }

    const wantStatus = kind === "approval" ? "awaiting_approval" : "awaiting_input";
    if (node.status !== wantStatus) {
      reconciled.skipped.push({ nodeId, reason: "not_parked" });
      continue;
    }
    const decidedAt = kind === "approval" ? node.approval?.decidedAt : node.input?.providedAt;
    if (decidedAt != null) {
      reconciled.skipped.push({ nodeId, reason: "already_decided" });
      continue;
    }

    if (kind === "approval") {
      // Never guess a decision — an absent or unrecognised one is structural.
      const decision = entry.decision;
      if (decision !== "approved" && decision !== "rejected") {
        reconciled.skipped.push({ nodeId, reason: "payload_invalid" });
        continue;
      }
      toApply.push({
        kind: "approval",
        nodeId,
        decision,
        decidedBy: typeof entry.decidedBy === "string" ? entry.decidedBy : "user",
        ...(typeof entry.feedback === "string" ? { feedback: entry.feedback } : {}),
      });
    } else {
      if (!validateInputResponse(node.input?.schema, entry.response)) {
        reconciled.skipped.push({ nodeId, reason: "payload_invalid" });
        continue;
      }
      toApply.push({
        kind: "input",
        nodeId,
        response: entry.response,
        providedBy: typeof entry.providedBy === "string" ? entry.providedBy : "user",
      });
    }
    reconciled.applied.push(nodeId);
  }
  return { toApply, reconciled };
}

/** Adapter op + payload for one {@link PendingGateDecision} replay. */
function pendingDecisionOp(decision: PendingGateDecision): {
  op: "applyApproval" | "applyInput";
  payload: Record<string, unknown>;
} {
  return decision.kind === "approval"
    ? {
        op: "applyApproval",
        payload: {
          nodeId: decision.nodeId,
          decision: decision.decision,
          decidedBy: decision.decidedBy,
          ...(decision.feedback !== undefined ? { feedback: decision.feedback } : {}),
        },
      }
    : {
        op: "applyInput",
        payload: {
          nodeId: decision.nodeId,
          response: decision.response,
          providedBy: decision.providedBy,
        },
      };
}

/** Whether a durable snapshot contains parked rejection feedback that needs an agent revision. */
function hasRejectedParkedApproval(execution: FlowExecution): boolean {
  return Object.values(execution.nodes ?? {}).some(
    (node) =>
      node.status === "awaiting_approval" &&
      node.approval?.decision === "rejected" &&
      node.approval.decidedAt != null,
  );
}

/**
 * Map a host-op `connector_mutate` (applyApproval/applyInput/retryNode) to the
 * typed {@link TurnStimulus} that must explicitly wake the agent; `null` for
 * ops that don't resume a turn (cancel, setAutonomousMode, hydrate, start).
 *
 * These need an explicit kick because the generic mutate→computeStimulus path
 * only emits `state_changed` and can miss an approved-only change (status/focus
 * unchanged), so the gate would hang.
 *
 * Pure — depends only on its inputs.
 *
 * @docLink packages/runner/concepts#host-op-resume-stimulus
 */
export function hostOpResumeStimulus(
  op: string,
  payload: Record<string, unknown>,
): TurnStimulus | null {
  switch (op) {
    case "applyApproval":
      return {
        kind: "approval_received",
        nodeId: String(payload.nodeId ?? ""),
        decision: payload.decision === "rejected" ? "rejected" : "approved",
        feedback: typeof payload.feedback === "string" ? payload.feedback : undefined,
        decidedBy: String(payload.decidedBy ?? "user"),
      };
    case "applyInput":
      return {
        kind: "input_received",
        nodeId: String(payload.nodeId ?? ""),
        response: payload.response,
        providedBy: String(payload.providedBy ?? "user"),
      };
    case "retryNode":
      return {
        kind: "retry_requested",
        nodeId: String(payload.nodeId ?? ""),
        requestedBy: String(payload.requestedBy ?? "user"),
      };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// connector_mutate handling (Protocol 3.7)
// ---------------------------------------------------------------------------

/** The `connector_mutate` fields {@link executeConnectorMutate} reads. */
export type ConnectorMutateInput = {
  id: string;
  op: string;
  payload?: Record<string, unknown>;
  /** Opt-in ack correlation. Absent ⇒ no `connector_mutate_response`, ever. */
  requestId?: string;
};

/** A registered flow run, as {@link executeConnectorMutate} needs to address it. */
export type ActiveFlowTarget = {
  runId: string;
  connectorId: string;
  /** Whether hydrate compatibility preparation changed the host snapshot. */
  hydratedStateChanged?: boolean;
};

/**
 * Everything {@link executeConnectorMutate} needs from the `serve` closure.
 * Injected rather than captured so the handler's terminal paths — including the
 * ones that used to be swallowed — are directly testable.
 */
export interface ConnectorMutateDeps {
  /** The session-wide readiness gate; rejects when the session build failed. */
  sessionReady: Promise<void>;
  sendEvent: (event: AgentEvent) => void;
  /** Must *throw* when no ConnectorManager exists yet (3.6 behaviour). */
  assertResourcesReady: () => void;
  hasConnector: (id: string) => boolean;
  /** Whether the connector registered at `id` is a flow connector. */
  isFlowConnector: (id: string) => boolean;
  executeOp: (id: string, op: string, args: Record<string, unknown>) => Promise<string>;
  /** Re-read on every (re-)plan so a run registered during the wait is seen. */
  flowState: () => {
    hasActiveFlow: boolean;
    hasRunningFlow: boolean;
    activeFlow: ActiveFlowTarget | null;
  };
  createActiveFlow: (
    flowDef: FlowDefinition,
    seedOrExecution:
      | { seed: { runId: string; startedBy: string; autonomousMode?: boolean } }
      | { execution: FlowExecution },
  ) => Promise<ActiveFlowTarget>;
  /**
   * Bounded flow-connector readiness wait; `true` when one registered in time.
   * `isSatisfied` narrows the gate to a specific connector id — see
   * {@link FlowConnectorReadiness.wait}.
   */
  waitForFlowConnector: (timeoutMs: number, isSatisfied?: () => boolean) => Promise<boolean>;
  connectorWaitMs: number;
  /** Fire-and-forget turn kick — must not block the acknowledgement. */
  signalStimulus: (target: ActiveFlowTarget, stimulus: TurnStimulus) => void;
  log: Pick<Logger, "info" | "warn" | "debug">;
}

/**
 * Body of the runner's `connector_mutate` handler.
 *
 * Emission contract (Protocol 3.7): **exactly one** `connector_mutate_response`
 * per command that carried a `requestId`, on every terminal path including the
 * ones that previously only logged — and **never** for a command without one.
 * That opt-in is the entire 3.6 back-compat story, so the `respond` helper below
 * is the only place an ack is produced.
 *
 * Gotcha: the turn kicks stay fire-and-forget and stay *after* the ack decision.
 * The ack means "the mutation applied", not "the turn ran".
 */
export async function executeConnectorMutate(
  cmd: ConnectorMutateInput,
  deps: ConnectorMutateDeps,
): Promise<void> {
  let acked = false;
  const respond = (r: Omit<ConnectorMutateResponseEvent, "type" | "requestId">) => {
    if (!cmd.requestId || acked) return;
    acked = true;
    deps.sendEvent({ type: "connector_mutate_response", requestId: cmd.requestId, ...r });
  };

  // session_init acks before the connector/MCP bring-up finishes, so this can
  // arrive before resourceManager exists. Gate on the readiness promise; a build
  // failure surfaces a fatal error instead of a confusing "before resources ready" throw.
  try {
    await deps.sessionReady;
  } catch (err) {
    const message = `Session not available: ${(err as Error).message}`;
    deps.sendEvent({ type: "error", message, fatal: true });
    respond({ ok: false, code: "session_unavailable", error: message });
    return;
  }

  const payload = cmd.payload ?? {};
  const planContext = () => {
    const targetExists = deps.hasConnector(cmd.id);
    const state = deps.flowState();
    return {
      targetExists,
      isExistingFlow: targetExists && deps.isFlowConnector(cmd.id),
      hasActiveFlow: state.hasActiveFlow,
      hasRunningFlow: state.hasRunningFlow,
      onlyActiveConnectorId: state.activeFlow?.connectorId ?? null,
    };
  };

  try {
    deps.assertResourcesReady();
    // Flow-connector host contract (see MIGRATION-flow-connector.md). Hosts
    // address the flow connector by its bare driver id ("flow"); the runner
    // registers each run at `flow:<runId>` and enforces one active flow per
    // session. `planFlowMutate` (pure, unit-tested) decides whether to
    // auto-create a run (`start`, or `hydrate` on a cold container), dispatch
    // to the existing run, or reject a malformed/conflicting op loudly.
    let plan = planFlowMutate(cmd, planContext());

    // A dispatch against a connector that is not registered yet is the wake
    // race: park on the readiness gate, then re-plan from scratch so the bare
    // "flow" rewrite picks up the run that registered while we waited.
    //
    // Only flow-targeted ids park. `createActiveFlow` is the sole drain, so a
    // non-flow id could never be satisfied by waiting — it would just pay the
    // full timeout where 3.6 failed instantly.
    const flowTargeted = cmd.id === "flow" || cmd.id.startsWith("flow:");
    if (plan.kind === "dispatch" && flowTargeted && !deps.hasConnector(plan.dispatchId)) {
      // A bare "flow" id needs *any* run to register — the rewrite picks it up.
      // An explicit `flow:<runId>` needs that exact one: a terminal run lingers
      // in `activeFlows`, so "any active flow" would resolve early and mislabel
      // the failure `op_failed` instead of `connector_unavailable`.
      const ready = await deps.waitForFlowConnector(
        deps.connectorWaitMs,
        cmd.id === "flow" ? undefined : () => deps.hasConnector(cmd.id),
      );
      if (!ready) {
        deps.log.warn("connector_mutate timed out waiting for the flow connector", {
          id: cmd.id,
          op: cmd.op,
          waitedMs: deps.connectorWaitMs,
        });
        respond({
          ok: false,
          code: "connector_unavailable",
          error: `Connector not found: "${cmd.id}" (waited ${deps.connectorWaitMs}ms)`,
        });
        return;
      }
      plan = planFlowMutate(cmd, planContext());
    }

    switch (plan.kind) {
      case "create-start": {
        // Legacy parity with the removed `start_flow` typed handler:
        // createActiveFlow wires the activeFlows map, stimulus bus, and the
        // SDK driver's MCP tool surface for the new run.
        const entry = await deps.createActiveFlow(plan.flowDef, { seed: plan.seed });
        deps.log.info("connector_mutate flow.start", {
          runId: entry.runId,
          flowId: plan.flowDef.id,
        });
        respond({ ok: true });
        // Kick the initial `flow_started` turn (workspaces#399). `connect()`
        // seeds the execution but performs no mutation, so — unlike the
        // `dispatch` path, where the adapter's `mutate()` wakes the next turn
        // via the bus — nothing prompts the agent to begin.
        deps.signalStimulus(entry, { kind: "flow_started" });
        return;
      }
      case "create-hydrate": {
        // Protocol 3.5 bootstrap gap fix: rehydrate a persisted run on a cold
        // container in a single op. Seeds runId/startedBy/autonomousMode from
        // the snapshot itself; the connect() execution path restores state.
        const entry = await deps.createActiveFlow(plan.flowDef, { execution: plan.execution });
        deps.log.info("connector_mutate flow.hydrate (bootstrap)", {
          runId: entry.runId,
          flowId: plan.flowDef.id,
        });
        // Absent `reconciled` means "not attempted", which is not the same as
        // "nothing applied" — so only report when the host supplied the key.
        if (payload.pendingDecisions === undefined) {
          respond({ ok: true });
          if (entry.hydratedStateChanged || hasRejectedParkedApproval(plan.execution)) {
            deps.signalStimulus(entry, { kind: "resumed_after_hibernation" });
          }
          return;
        }
        const { toApply, reconciled } = reconcilePendingDecisions(
          plan.execution,
          payload.pendingDecisions,
        );
        let appliedAny = false;
        // Rebuilt from what actually landed rather than filtered by nodeId, so
        // two decisions on one node report per-entry instead of collapsing.
        const applied: string[] = [];
        // Membership view of `applied`, so a repeated nodeId reads as sequencing
        // rather than as a bad payload.
        const appliedNodeIds = new Set<string>();
        for (const decision of toApply) {
          const { op, payload: opPayload } = pendingDecisionOp(decision);
          try {
            await deps.executeOp(entry.connectorId, op, opPayload);
            applied.push(decision.nodeId);
            appliedNodeIds.add(decision.nodeId);
            appliedAny = true;
          } catch (err) {
            // The adapter refusing a replay means it is no longer replayable,
            // not that the hydrate failed — downgrade and keep going.
            //
            // `reconcilePendingDecisions` is pure over the pre-replay snapshot and
            // does not collapse repeated nodeIds, so a refusal here has two very
            // different causes. An earlier entry in *this* batch already decided
            // the node — sequencing, and the payload was fine. Otherwise the node
            // was parked when classified and still is, so what the adapter rejected
            // is the decision itself (an approved check with no override reason, an
            // override the node's own output contract forbids, …).
            reconciled.skipped.push({
              nodeId: decision.nodeId,
              reason: appliedNodeIds.has(decision.nodeId) ? "already_decided" : "payload_invalid",
            });
            deps.log.warn("flow hydrate reconciliation refused", {
              nodeId: decision.nodeId,
              op,
              error: err instanceof Error ? err.message : String(err),
            });
          }
        }
        reconciled.applied = applied;
        respond({ ok: true, reconciled });
        // One kick for the whole batch: the runner enforces one active flow, so
        // N kicks would drive N turns.
        if (appliedAny || entry.hydratedStateChanged || hasRejectedParkedApproval(plan.execution)) {
          deps.signalStimulus(entry, { kind: "resumed_after_hibernation" });
        }
        return;
      }
      case "reject": {
        // Surface the rejection to the host, not just container logs, so the
        // platform can tell "runner rejected the op" from "op succeeded". The
        // non-fatal `error` event stays verbatim — 3.6 hosts still rely on it.
        deps.log.warn("connector_mutate rejected", {
          id: cmd.id,
          op: cmd.op,
          reason: plan.reason,
        });
        deps.sendEvent({
          type: "error",
          message: `connector_mutate rejected (id=${cmd.id}, op=${cmd.op}): ${plan.reason}`,
          fatal: false,
          category: "validation",
        });
        respond({ ok: false, code: "rejected", error: plan.reason });
        return;
      }
      case "dispatch": {
        // Generic dispatch — the adapter's mutate() pipeline emits
        // state_changed via onChange and wakes the next turn through the bus.
        const result = await deps.executeOp(plan.dispatchId, cmd.op, payload);
        // A hydrate that overwrites a live run ignores pendingDecisions:
        // replaying onto a live run is the double-apply this design avoids.
        const ignoredReplay = cmd.op === "hydrate" && payload.pendingDecisions !== undefined;
        respond({
          ok: true,
          ...(result !== undefined && result !== null ? { result } : {}),
          ...(ignoredReplay ? { reconciled: { applied: [], skipped: [] } } : {}),
        });

        // Resume ops need an explicit typed kick — the generic
        // mutate→computeStimulus signal can miss an approved gate and hang it.
        const resumeStimulus = hostOpResumeStimulus(cmd.op, payload);
        if (resumeStimulus) {
          const active = deps.flowState().activeFlow;
          if (active) deps.signalStimulus(active, resumeStimulus);
        } else if (
          cmd.op === "hydrate" &&
          typeof result === "string" &&
          JSON.stringify(payload.state) !== result
        ) {
          const active = deps.flowState().activeFlow;
          if (active) deps.signalStimulus(active, { kind: "resumed_after_hibernation" });
        }
        return;
      }
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    deps.log.warn("connector_mutate failed", { id: cmd.id, op: cmd.op, error: message });
    respond({ ok: false, code: "op_failed", error: message });
  }
}

/**
 * Reconstruct a typed {@link TurnStimulus} from a single bus-signal `meta`, or
 * `null` when the meta carries no specific kind (a generic `state_changed`, or
 * an unknown/malformed kind). The adapter's auto-signal always uses
 * `kind: "state_changed"`; explicit kicks (op:start, host-op resume, user
 * message) carry the specific kind and its fields.
 */
function reconstructTypedStimulus(meta: Record<string, unknown>): TurnStimulus | null {
  switch (meta.kind) {
    case "flow_started":
      return { kind: "flow_started" };
    case "resumed_after_hibernation":
      return { kind: "resumed_after_hibernation" };
    case "user_message":
      return typeof meta.text === "string"
        ? {
            kind: "user_message",
            text: String(meta.text),
            senderId: String(meta.senderId ?? "user"),
          }
        : null;
    case "approval_received":
      return {
        kind: "approval_received",
        nodeId: String(meta.nodeId),
        decision: meta.decision === "rejected" ? "rejected" : "approved",
        feedback: typeof meta.feedback === "string" ? meta.feedback : undefined,
        decidedBy: String(meta.decidedBy ?? "user"),
      };
    case "input_received":
      return {
        kind: "input_received",
        nodeId: String(meta.nodeId),
        response: meta.response,
        providedBy: String(meta.providedBy ?? "user"),
      };
    case "retry_requested":
      return {
        kind: "retry_requested",
        nodeId: String(meta.nodeId),
        requestedBy: String(meta.requestedBy ?? "user"),
      };
    default:
      return null;
  }
}

/**
 * Pick the stimulus for a coalesced batch of bus signals. A host-op resume kick
 * rides in the same batch as the adapter's generic `state_changed`; scanning
 * from the most recent, the most-specific typed stimulus wins so a trailing
 * `state_changed` can't mask it. Falls back to `state_changed` for an
 * all-generic batch.
 *
 * Pure — depends only on its inputs.
 *
 * @docLink packages/runner/concepts#stimulus-from-metas
 */
export function stimulusFromMetas(metas: Array<Record<string, unknown>>): TurnStimulus {
  for (let i = metas.length - 1; i >= 0; i--) {
    const typed = reconstructTypedStimulus(metas[i] ?? {});
    if (typed) return typed;
  }
  return { kind: "state_changed" };
}

/**
 * Start a WebSocket agent server that the Skaile platform connects to for
 * host-driven agent execution inside a local subprocess or Docker container.
 *
 * The server accepts {@link AgentCommand} messages over a
 * {@link ServerTransport} (default: `WebSocketServerTransport` on `opts.port`)
 * and streams {@link AgentEvent} responses back to the connected client.
 *
 * Handled commands:
 * - `provision_secrets` — injects API keys and Claude Code credentials; in
 *   `provisioned` secrets mode, triggers deferred session creation.
 * - `configure` — registers shared state stores and applies mid-session skill
 *   catalog changes. There is no envelope-level flow rehydration; hosts restore
 *   a persisted run with a single `connector_mutate { id: "flow", op: "hydrate",
 *   payload: { state, flow } }` (auto-creates the connector on a cold container,
 *   Protocol 3.5). See `MIGRATION-flow-connector.md`.
 * - `prompt` — routes free-chat messages through the SessionStimulusBus when
 *   a flow is active, otherwise forwards directly to the driver; **driver is
 *   lazily started on the first `prompt` command**.
 * - `connector_mutate` / `connector_query` — generic connector dispatch.
 *   Replaces the 6 typed flow commands removed in Protocol 2.2 (`start_flow`,
 *   `approve_flow_node`, `provide_flow_input`, `cancel_flow`,
 *   `set_flow_autonomous_mode`, `retry_flow_node`); see
 *   `MIGRATION-flow-connector.md` for the per-op mapping table.
 * - `add_resource` / `remove_resource` — mount/unmount mounts or
 *   connect/disconnect connectors mid-session. When the added connector's
 *   driver is `flow`, the runner injects the per-session stimulus bus into
 *   the connect options.
 *
 * The function resolves after the transport starts listening. It does not
 * resolve until the process exits — shutdown is driven by SIGINT/SIGTERM or
 * a `shutdown` command from the connected client.
 *
 * @param opts - Server configuration.
 * @docLink packages/runner/dev-guide#flow-execution-turn-based-model
 */
export async function startAgentServer(opts: ServeOptions): Promise<void> {
  const managedHost = opts.managedCodex
    ? validateManagedCodexHost(opts.managedCodex, opts.projectDir)
    : undefined;
  if (managedHost && opts.sessionId && opts.sessionId !== managedHost.sessionId)
    throw new Error("CODEX_MANAGED_BOOTSTRAP_INVALID");
  const authToken = opts.authToken ?? process.env.SKAILE_WS_AUTH_TOKEN;
  if (managedHost && !opts.transport && !authToken)
    throw new Error("CODEX_MANAGED_BOOTSTRAP_INVALID");
  const supportedRuntimeFeatures = managedHost ? ["codex-app-server-v1"] : [];
  let managedBootstrapClosed = false;
  const port = opts.port ?? 8080;
  const sessionId =
    managedHost?.sessionId ?? opts.sessionId ?? process.env.SKAILE_SESSION_ID ?? "session-unknown";
  const resumeSessionId = opts.resumeSessionId ?? process.env.SKAILE_RESUME_SESSION_ID;

  // Read platform maxTurns from workspace config (before createAgentSession)
  const wsConfig = resolveSkWorkspaceConfig(opts.projectDir);
  // Project extension loaders run in the trusted host process, outside native path isolation.
  if (managedHost && wsConfig.agent?.["prompt-extensions"]?.length)
    throw new Error("CODEX_MANAGED_PROMPT_EXTENSIONS_UNSUPPORTED");

  // Bootstrap structured logging — registers a process-singleton LogStore.
  // OnLogBridgeSink mirrors entries to opts.onLog when caller provides it,
  // preserving back-compat with hosts that consume plain text lines.
  const { store: logStore, dispose: disposeLogStore } = bootstrapRunnerLogStore({
    sessionId,
    projectDir: opts.projectDir,
    yamlBlock: (wsConfig as { logging?: unknown }).logging,
    onLog: opts.onLog,
  });
  const serverLog = createLogger({
    kind: "runner",
    subkind: "session",
    instance: sessionId,
  });
  // Back-compat: keep `log()` as a string alias so existing call sites continue
  // to work; new diagnostics should call serverLog.{info,warn,error,debug} directly.
  const log = (line: string) => serverLog.info(line);
  // Wake-path phase timing. A tier-1 resume takes minutes and the wall-clock has
  // never been attributable from logs (skaile-ai/workspaces#421); every phase the
  // queued prompt waits behind reports its own elapsed ms under one `phase` field.
  const logPhase = (phase: string, startedAt: number, data?: Record<string, unknown>) =>
    serverLog.info(`[serve] phase ${phase}`, { ...data, phase, elapsedMs: Date.now() - startedAt });

  // Claim stdout's `error` event before the rest of setup logs through it.
  // Not the uncaughtException listeners — those install only once startup has
  // succeeded. Read once so the watch and the handler cannot disagree.
  // See `_devlog/notes/2026-09-07-stdout-epipe-log-loop.md`.
  const exitOnBrokenPipe = opts.exitOnBrokenPipe ?? false;
  const stopOutputWatch = watchOutputStream({ exitOnBrokenPipe });
  // Starts as the watch alone so the `session_init` bail-outs release it too.
  // A boot that throws never releases; the watch is ref-counted, so that
  // leaves one residual listener, not one per attempt.
  let teardownProcessHandlers: () => void = stopOutputWatch;

  const profile = wsConfig.agent_config?.default;
  const maxTurns =
    wsConfig.agent?.permissions?.max_turns ??
    ((profile as Record<string, unknown> | undefined)?.max_turns as number | undefined);

  setupManagedGitconfig(opts.projectDir, serverLog);

  // Resolve the agent definition directory. CLI flag wins; otherwise fall back
  // to skaile.yaml `agent.definition` via resolveAgentDir. Without this, the
  // agent identity (SOUL+RULES+knowledge) is rendered at install time, not loaded at runtime.
  const agentDir = opts.agentDir ?? resolveAgentDir(opts.projectDir);
  if (agentDir) {
    serverLog.info("agent dir resolved", { agentDir });
  } else {
    serverLog.warn("no agent definition found in skaile.yaml");
  }

  // ---------------------------------------------------------------------------
  // Transport setup (must be before resource init — sync callbacks use sendEvent)
  // ---------------------------------------------------------------------------

  const transport: ServerTransport =
    opts.transport ??
    new WebSocketServerTransport({ port, onLog: log, ...(authToken ? { authToken } : {}) });

  // Wire structured log forwarding: every LogEntry recorded by the in-container
  // LogStore is emitted as a `log_entry` AgentEvent over the same transport
  // the gateway already subscribes to. The transport drops sends when no client
  // is connected, so no buffering is needed here.
  logStore.addSink(
    new WsLogSink({
      send: (event) => {
        if (transport.connected) transport.send(event);
      },
    }),
  );

  // Forward agent events to the connected client
  const turnStatus = new TurnStatusTracker();
  function sendEvent(event: AgentEvent) {
    // Observed before the connected check: a turn end that lands on a dead
    // transport must still clear the state the next connect replays.
    turnStatus.observe(event);
    if (transport.connected) {
      transport.send(event);
    }
  }

  // Whether an init has been acknowledged — the fact a gateway holds only in its
  // own memory. Replayed on every (re)connect so a host that re-adopts this
  // container after a restart can keep it; cleared once the runner tears down.
  // The generation lets a superseded init's late build failure leave a newer
  // init's acknowledgement alone.
  let initAcknowledged = false;
  let initGeneration = 0;
  function emitInitAck(ack: SessionInitAckEvent): void {
    initAcknowledged = true;
    sendEvent(ack);
  }

  // ---------------------------------------------------------------------------
  // Capability registry (Protocol v2)
  // ---------------------------------------------------------------------------
  //
  // Single per-session registry. Built-in `__capabilities.list/search/describe`
  // are registered at boot. Client-side capabilities arrive via
  // ConfigureCommandV2.capabilities in the v2 configure handler below.
  //
  // The registry is constructed eagerly (before session creation) so the
  // bridge can pull tool definitions through `BridgeCapabilityHooks` on its
  // first turn. Built-ins are registered immediately; downstream registrations
  // (skills/connectors/mounts/client) happen at their own lifecycles.

  const capabilityRegistry = new CapabilityRegistry(
    createLogger({
      kind: "runner",
      subkind: "capability-registry",
      instance: sessionId,
    }),
  );
  bootstrapCapabilityRegistry(capabilityRegistry);

  // ---------------------------------------------------------------------------
  // Client-side capability round-trip (Protocol v2)
  // ---------------------------------------------------------------------------
  //
  // When the LLM invokes a `side: 'client'` capability, the bridge calls the
  // registry's invoke() which delegates to the per-capability handler. For
  // client-side caps the handler emits a `capability_invoked` event over the
  // transport, registers a pending promise keyed by `callId`, and waits for
  // a `capability_result` command from the platform. `fireAndForget` caps
  // bypass the pending map (the bridge also short-circuits in
  // `dispatchCapability`, so this is defence-in-depth).
  const pendingCapabilityCalls = new Map<string, PendingCallEntry>();

  // ---------------------------------------------------------------------------
  // Outbound capability invocation (Protocol v3)
  // ---------------------------------------------------------------------------
  //
  // The runner invokes `host.*` capabilities on the platform through the same
  // `capability_invoked` / `capability_result` round-trip already used for
  // client-side capabilities — just in the opposite direction. The pending
  // map is shared (`pendingCapabilityCalls`) since the wire shape is identical.
  //
  // Spec: `_devlog/specs/2026-05-10-deterministic-session-bootstrap.md`
  // § "Bidirectional capability registry as RPC backbone".
  const remoteCapabilityInvoker = (
    name: string,
    input: unknown,
    timeoutMs?: number,
    signal?: AbortSignal,
  ): Promise<unknown> => {
    const callId =
      typeof crypto?.randomUUID === "function"
        ? crypto.randomUUID()
        : `rcap-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
    return new Promise<unknown>((resolve, reject) => {
      if (!transport.connected) {
        reject(new Error(`Transport disconnected; cannot invoke remote capability ${name}`));
        return;
      }
      if (signal?.aborted) {
        reject(signal.reason);
        return;
      }
      const finish = (error: unknown, value?: unknown) => {
        clearTimeout(timer);
        signal?.removeEventListener("abort", abort);
        pendingCapabilityCalls.delete(callId);
        if (error) reject(error);
        else resolve(value);
      };
      const abort = () => finish(signal?.reason ?? new Error("Remote capability aborted"));
      const timer = setTimeout(
        () => finish(new Error(`Remote capability ${name} (callId=${callId}) timed out`)),
        timeoutMs ?? DEFAULT_CAPABILITY_CALL_TIMEOUT_MS,
      );
      pendingCapabilityCalls.set(callId, {
        resolve: (value) => finish(null, value),
        reject: (error) => finish(error),
        timer,
      });
      signal?.addEventListener("abort", abort, { once: true });
      try {
        transport.send({ type: "capability_invoked", callId, name, input, invokedBy: "agent" });
      } catch (error) {
        finish(error);
      }
    });
  };

  /**
   * Per-session AI provider config id, threaded in via `ConfigureCommandV2.config.aiProviderConfigId`.
   * Read by the auth-error retry handler to fill `request_access_token { kind: 'ai-credentials', configId }`.
   * Standalone runners (CLI / forge / Claude plugin) never see a configure
   * command with this field, so it stays `undefined` and the auth-error
   * handler falls back to surfacing the error.
   *
   * Spec: `_devlog/specs/2026-05-07-unified-credential-mediation.md`
   * § "Wire `aiProviderConfigId` end-to-end".
   */
  let aiProviderConfigId: string | undefined;

  // Backoff sleep for the reactive 401 bounded retry. `unref` so a pending
  // retry never pins the event loop alive during shutdown.
  const sleep = (ms: number): Promise<void> =>
    new Promise((resolve) => {
      const t = setTimeout(resolve, ms);
      (t as unknown as { unref?: () => void }).unref?.();
    });

  /**
   * Inner credential-mediation helper shared by the reactive
   * `onAuthError` callback and the proactive refresh timer. Acquires a
   * fresh access token through `host.refresh_credential`, rewrites
   * `<home>/.claude/.credentials.json`, and returns the typed mint.
   * Successful mints re-arm the proactive scheduler against the freshly
   * issued token's expiry.
   *
   * Declared before `aiCredentialRefreshScheduler` even though the
   * scheduler's `refresh` callback references this — keeps the textual
   * order matching the call graph (mint → schedule re-arm) instead of
   * relying on hoisting semantics that would break under a future
   * arrow-function refactor.
   */
  async function mintAndPersistAiCredential(args: {
    configId?: string;
    reason: "refresh" | "retry-401";
    rejection?: import("@skaile/workspaces/types").CredentialRejection;
  }): Promise<import("@skaile/workspaces/types").CredentialMint> {
    if (!transport.connected || aiProviderConfigId === undefined) {
      return {
        ok: false,
        code: "not-configured",
        message: "no transport or aiProviderConfigId; standalone runner cannot mediate refresh",
      };
    }
    const targetConfigId = args.configId || aiProviderConfigId;
    try {
      // `||` (not `??`) so an empty `HOME` falls back to homedir() rather than
      // being used as "". Inside the try so a homedir() throw becomes a
      // backend-error mint rather than an unhandled rejection.
      const home = process.env.HOME || homedir();
      // On a retry-401 the credential currently on disk is the one the provider
      // just rejected. Fingerprint it (truncated SHA-256, non-reversible) so the
      // platform single-flight mediator can coalesce concurrent rotations of the
      // same dead token. Older platforms ignore the optional field.
      const refreshInput = buildRefreshCredentialInput({
        configId: targetConfigId,
        reason: args.reason,
        rejectedToken: args.reason === "retry-401" ? readCurrentClaudeAccessToken(home) : null,
        ...(args.rejection ? { rejection: args.rejection } : {}),
      });
      const result = (await capabilityRegistry.invokeRemote(
        "host.refresh_credential",
        refreshInput,
      )) as import("@skaile/workspaces/types").CredentialMint;
      if (!result?.ok) {
        return result ?? { ok: false, code: "backend-error", message: "no result from host" };
      }
      await mergeAndRewriteCredentialsFile(home, result.token, result.expiresAt ?? null);
      await guardClaudeAiConnectors(home, serverLog);
      // Re-arm proactive timer for the freshly issued token. The reactive
      // (retry-401) path calls into here too, so retry success also chains
      // the next proactive fire.
      aiCredentialRefreshScheduler.schedule(result.expiresAt ?? null);
      return result;
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      return { ok: false, code: "backend-error", message: errMsg };
    }
  }

  /**
   * Single-flight AI-credential re-pull that also repoints a running session at
   * another AI provider config when the platform hands one back. Shared by the
   * `credentialRotated` broadcast, the mint-result restash, and the reactive
   * limit failover — all three can race, so they coalesce onto one round trip.
   *
   * Spec: skaile-ai/workspaces#567 (§ "Guards").
   */
  const aiProviderConfigSwitcher = createAiProviderConfigSwitcher({
    mint: mintAndPersistAiCredential,
    getConfigId: () => aiProviderConfigId,
    setConfigId: (next) => {
      aiProviderConfigId = next;
      // `sessionConfig` is definite-assigned but still undefined during the
      // session_init fast path (the heavy build assigns it), and a broadcast
      // can land in that window. Read it as optional rather than trusting the
      // declaration.
      const live = sessionConfig as AgentSessionConfig | undefined;
      if (live) live.aiProviderConfigId = next;
    },
    scheduleRestart: (reason) => scheduleDriverRestartForToolChanges(reason),
    log,
    logWarn: (line: string) => serverLog.warn(line),
  });

  /**
   * Driver-facing seat failover (`AgentConfig.onLimitBlocked`). Invoked once per
   * logical turn when a turn is blocked by an upstream usage limit, AFTER that
   * attempt has already failed — so it never runs mid-turn.
   *
   * A different config back means the credentials file now holds another seat's
   * token and the driver should respawn its CLI and replay. The same config
   * back (or any mint failure) means no peer was available, and the driver
   * surfaces the original limit error unchanged.
   *
   * Standalone runners short-circuit before any capability round trip: with no
   * transport and no `aiProviderConfigId` there is no mediator to ask.
   */
  const onLimitBlocked = async (
    ctx: import("@skaile/workspaces/bridge").LimitBlockedContext,
  ): Promise<import("@skaile/workspaces/bridge").LimitFailoverResult> => {
    if (!transport.connected || aiProviderConfigId === undefined) {
      return { switched: false, reason: "standalone" };
    }
    // WARN, not info: prod filters at WARN+, and a session going dead on a
    // parked seat is precisely what was previously unreconstructible from a
    // production log (skaile-ai/platform#3540).
    // `resetsAt` is documented as epoch SECONDS. Render it only when it lands
    // in a plausible window, so an SDK that ever switches to milliseconds shows
    // the raw value instead of a silent year-57000 timestamp.
    const resetsAtIso =
      ctx.resetsAt && ctx.resetsAt > 1_000_000_000 && ctx.resetsAt < 100_000_000_000
        ? new Date(ctx.resetsAt * 1000).toISOString()
        : ctx.resetsAt
          ? `raw ${ctx.resetsAt}`
          : undefined;
    const limitWindow = [ctx.limitType, resetsAtIso ? `resets ${resetsAtIso}` : undefined]
      .filter(Boolean)
      .join(", ");
    serverLog.warn(
      `[serve] usage limit blocked the turn on config ${ctx.configId || aiProviderConfigId}${limitWindow ? ` (${limitWindow})` : ""}`,
    );
    // `retry-401`, not `refresh`, and the difference is the whole feature.
    // The platform's mint caches per config and keys the policy off this
    // field: `refresh` means "serve the cached token if it has >5 min left",
    // which returns BEFORE the resolver runs — and the resolver is the only
    // thing that notices the seat is parked (`isActive: false` → the
    // `not-configured` that trips re-resolution). A `refresh` here would
    // therefore hand back the exhausted seat's own cached token and report
    // success, leaving this path inert in exactly the incident it exists for.
    // `retry-401` bypasses the cache and reaches the resolver.
    // `rejection` is the other half: `retry-401` alone reads as "credential
    // rejected", so the platform rotates a fine token and leaves a config
    // without `autoActivate` parked. Only this value names the seat.
    const outcome = await aiProviderConfigSwitcher.repull({
      trigger: "limit-blocked",
      reason: "retry-401",
      rejection: "usage-limit",
    });
    if (outcome.switched) {
      return { switched: true, ...(outcome.configId ? { configId: outcome.configId } : {}) };
    }
    return {
      switched: false,
      ...(outcome.configId ? { configId: outcome.configId } : {}),
      // A platform that does not yet return `configId` on the mint is
      // indistinguishable from one that re-resolved — say so rather than
      // claiming the seat was unchanged.
      reason: !outcome.mint.ok
        ? outcome.mint.code
        : outcome.mint.configId
          ? "same-config"
          : "platform-omitted-config-id",
    };
  };

  /**
   * Driver-facing live-health telemetry (`AgentConfig.onProviderResponse`).
   * Forwards an observed 401 / 429 to the platform so the owning
   * `AIProviderConfig`'s counters can be bumped — the cron probe samples auth,
   * never capacity (skaile-ai/workspaces#368, skaile-ai/platform#3539).
   *
   * `configId` comes from the runner's stash, not the driver's copy: after a
   * failover the driver is one generation stale until its queued restart lands,
   * and attributing the rejection to the seat it just left would be wrong.
   * `sendEvent` no-ops on a disconnected transport, so standalone runners emit
   * nothing.
   */
  const onProviderResponse = (
    observation: import("@skaile/workspaces/bridge").ProviderResponseObservation,
  ): void => {
    const configId = aiProviderConfigId ?? observation.configId;
    sendEvent({
      type: "provider_response_seen",
      status: observation.status,
      ...(configId ? { configId } : {}),
      ...(observation.limitType ? { limitType: observation.limitType } : {}),
      ...(typeof observation.utilization === "number"
        ? { utilization: observation.utilization }
        : {}),
      ...(typeof observation.resetsAt === "number" ? { resetsAt: observation.resetsAt } : {}),
      observedAt: new Date().toISOString(),
    });
  };

  // Connector token-refresh mediator (the previously-missing wire). Routes a
  // backend git/sharepoint/webdav mount's `refresh`/`retry-401` through
  // `host.refresh_credential` (see connectorRefreshKind for the driver→kind map).
  const connectorTokenMediator: TokenMediator = buildConnectorTokenMediator({
    isConnected: () => transport.connected,
    invokeRemote: (name, input) => capabilityRegistry.invokeRemote(name, input),
    log,
  });

  /**
   * Proactive AI-credential refresh scheduler. Holds a single timer that
   * fires `PROACTIVE_AI_REFRESH_MARGIN_MS` before the current token
   * expires, calls `host.refresh_credential { reason: 'refresh' }`, and
   * re-arms against the new expiry. Pairs with the bridge-side
   * `failTurn` deferral that suppresses downstream auth-error events
   * when a self-heal is about to run: this timer is the primary
   * prevention of in-container 401s, the reactive 401 path is the
   * safety net.
   *
   * Schedule is re-armed on every fresh credential (initial provision,
   * a reactive retry-401 success, or a previous proactive fire). Tokens
   * with `expiresAt === null` (static PATs) do not schedule.
   *
   * Spec: `_devlog/specs/2026-05-07-unified-credential-mediation.md`
   * § "Proactive refresh" (added 2026-05-20).
   */
  const aiCredentialRefreshScheduler = createAiCredentialRefreshScheduler({
    refresh: async () => {
      // Through the switcher, not the bare mint: a platform that re-resolved a
      // parked seat announces it only by minting for a different one, so the
      // proactive refresh is also the no-broadcast self-heal path.
      const { mint: result } = await aiProviderConfigSwitcher.repull({
        trigger: "proactive-refresh",
      });
      if (result.ok) {
        return { status: "ok", expiresAt: result.expiresAt ?? null };
      }
      // A `backend-error` is a transient mediator blip (bounded retry); any
      // other code is a terminal credential/account state (stop re-arming).
      return isTransientMintFailure(result.code) ? { status: "transient" } : { status: "terminal" };
    },
    log,
    // Scheduler/refresh failures must be visible in `skaile session logs` and
    // the debug panel, so route them through the error-level structured logger.
    logError: (line: string) => serverLog.error(line),
  });

  /**
   * Auth-refresh callback handed to the bridge driver via
   * `AgentConfig.onAuthError`. Invoked when the underlying agent surfaces
   * an `authentication_error`. In v3, the runner mediates the fresh
   * credential through `host.refresh_credential` (a `runtime`-audience
   * capability the platform registered at session_init) and returns the
   * typed `CredentialMint` to the driver so it can branch on the
   * discriminator.
   *
   * Standalone CLI / forge sessions never register `host.refresh_credential`
   * (no platform peer), so the invocation fails synchronously and we
   * surface a structured `not_available` mint — preserving the legacy
   * "no mediator -> surface AuthError" behaviour.
   *
   * Spec: `_devlog/specs/2026-05-10-deterministic-session-bootstrap.md`
   * § "Refresh-on-401 routes through host.refresh_credential".
   */
  const onAuthError = async (args: {
    configId: string;
  }): Promise<import("@skaile/workspaces/types").CredentialMint> => {
    log("[serve] auth error: invoking host.refresh_credential capability");
    // The runner's stash is the source of truth for which seat this session is
    // on. A live failover repoints it before the driver has been rebuilt, so
    // the id the driver was constructed with can be one generation stale —
    // honouring it would re-pin the refresh to the seat we just left.
    const configId = aiProviderConfigId ?? args.configId;
    if (args.configId && configId !== args.configId) {
      log(`[serve] auth error: driver holds stale config ${args.configId}; refreshing ${configId}`);
    }
    // A single mediation run used to hard-fail the user on one `backend-error`
    // (e.g. the platform backend mid-redeploy, a ~7s window). Bounded-retry the
    // transient failure with backoff before surfacing; terminal codes
    // (not-configured / revoked / provider-error) surface immediately. The
    // retry is of a FAILED (non-rotating) mint, so it adds no rotations.
    // `rejection` says only that the rejection was auth-shaped, never that
    // capacity is fine — Anthropic mislabels OAuth quota exhaustion as
    // `authentication_error` (see bridge `error-classifier.ts`).
    const result = await retryTransientMint(
      () => mintAndPersistAiCredential({ configId, reason: "retry-401", rejection: "auth" }),
      {
        sleep,
        onRetry: ({ attempt, nextDelayMs, code }) =>
          serverLog.error(
            `[serve] auth error: transient mint failure (code=${code}); retry ${attempt} in ${Math.round(nextDelayMs / 1000)}s`,
          ),
      },
    );
    if (!result.ok) {
      log(`[serve] auth error: mint failed (code=${result.code}); surfacing`);
    } else {
      log("[serve] auth error: rewrote .credentials.json with mediated accessToken");
    }
    // A 401 on a parked seat is exactly how the platform's re-resolution
    // surfaces today: the mint succeeds, but for a different config. Applied
    // here rather than through `repull` because the bounded transient retry
    // above wraps the mint itself.
    aiProviderConfigSwitcher.applyMint(result, {
      trigger: "retry-401",
      ...(configId ? { requestedConfigId: configId } : {}),
    });
    return result;
  };

  /**
   * Approval-required client caps (Protocol v2.1) park on the platform until
   * a human responds. Human approvals routinely take minutes, so the default
   * 60-second timeout would cause the runner to abandon the pending call
   * while the platform later still dispatches the side effect. Use a long
   * (15-minute) timeout for `requiresApproval: true` capabilities so the
   * platform's parked-approval lifetime stays inside the runner's window.
   */
  const APPROVAL_REQUIRED_CAPABILITY_TIMEOUT_MS = 15 * 60 * 1000;

  function buildClientCapabilityHandler(
    wire: import("@skaile/workspaces/types").Capability,
  ): (input: unknown) => Promise<unknown> {
    // Precedence: an explicit per-capability `callTimeoutMs` wins (e.g.
    // `platform.ask_session` blocks for minutes); otherwise approval-required
    // caps get the long 15-minute window; everything else uses the default.
    const timeoutMs = resolveCapabilityCallTimeoutMs(wire, APPROVAL_REQUIRED_CAPABILITY_TIMEOUT_MS);
    return buildClientCapabilityRoundtripHandler({
      wire,
      pending: pendingCapabilityCalls,
      emit: (event) => sendEvent(event),
      timeoutMs,
    });
  }

  /**
   * Build the bridge-facing capability hooks. Drivers consume this through
   * `AgentConfig.capabilities`. The runner owns:
   *   - tool list composition (registry.composeLLMTools)
   *   - per-handler logging + Zod validation (registry.invoke)
   *   - render-event forwarding to the WebSocket client
   *   - text fallback emission for clients without a render layer
   */
  function buildCapabilityHooks(): import("@skaile/workspaces/bridge").BridgeCapabilityHooks {
    return {
      sessionId,
      composeTools: () => capabilityRegistry.composeLLMTools(),
      resolve: (name) => {
        const cap = capabilityRegistry.resolve(name);
        if (!cap) return null;
        // Strip runtime-only fields by going through the wire-format `list`.
        return capabilityRegistry.list().find((c) => c.name === name) ?? null;
      },
      invoke: (name, input, signal) =>
        capabilityRegistry.invoke(name, input, { sessionId, signal }),
      onToolsChanged: (listener) => capabilityRegistry.onToolsChanged(listener),
      emitRender: (event) => sendEvent(event),
      emitText: (text) =>
        sendEvent({
          type: "text",
          content: text,
        }),
    };
  }

  // ---------------------------------------------------------------------------
  // Composition → capability registration helper
  // ---------------------------------------------------------------------------

  /**
   * Register discoverable composition entries from a freshly created session
   * into the capability registry. Inline blocks are already injected into the
   * system prompt by session-builder; this handles only the tool-list entries.
   */
  function registerSessionComposition(session: AgentSession): void {
    if (session.composition?.discoverableEntries.length) {
      registerCompositionCapabilities(capabilityRegistry, session.composition.discoverableEntries);
      log(
        `[serve] registered ${session.composition.discoverableEntries.length} composition capabilities`,
      );
    }
  }

  // ---------------------------------------------------------------------------
  // Secret provisioning mode
  // ---------------------------------------------------------------------------

  const secretsMode = managedHost ? "provisioned" : (wsConfig.secrets?.provider ?? "env");
  const secretsTimeoutMs = wsConfig.secrets?.timeoutMs ?? 30_000;

  // Effective cloud transport for this session — from skaile.yaml
  // agent-config.default. Snake→camel conversion happens exactly once here
  // (cloudConfigFromProfile is the shared mapping used by the settings
  // cascade too). An unrecognized value normalizes to "default" so this layer
  // agrees with the driver's own fallback — otherwise we would skip BOTH the
  // provider-key pull and the bundle pick and start a credential-less session.
  const rawCloud = profile?.cloud ?? "default";
  const effectiveCloud = (SUPPORTED_CLOUDS as readonly string[]).includes(rawCloud)
    ? rawCloud
    : "default";
  if (effectiveCloud !== rawCloud) {
    log(
      `[serve] WARNING: unknown cloud "${rawCloud}" in agent-config — falling back to the default transport`,
    );
  }
  const effectiveCloudConfig = cloudConfigFromProfile(profile);

  // Disk-first gate for the wire agentConfig: any profile with at least one
  // agent setting disables the wire fallback outright (no per-field merge).
  const diskProfileHasAgentSettings = (p: AgentConfigProfile | undefined): boolean =>
    !!p &&
    !!(p.driver || p.provider || p.model || p.cloud || p.cloud_config || p.thinking || p.effort);

  // Disk cloud wins; the wire value engages only when disk resolved "default"
  // and a wire agentConfig is applicable. Same unknown→default normalization.
  function resolveEffectiveCloudForSession(): {
    cloud: string;
    cloudConfig?: CloudTransportConfig;
  } {
    if (managedCodex) return { cloud: "default" };
    if (effectiveCloud !== "default" || !wireAgentConfig) {
      return { cloud: effectiveCloud, cloudConfig: effectiveCloudConfig };
    }
    const raw = wireAgentConfig.cloud ?? "default";
    const cloud = (SUPPORTED_CLOUDS as readonly string[]).includes(raw) ? raw : "default";
    return { cloud, cloudConfig: wireAgentConfig.cloudConfig };
  }

  // Build the session config factory (secretProvider injected after provisioning)
  function buildSessionConfig(
    secretProvider?: unknown,
    apiKey?: string,
    cloudSecrets?: Record<string, string>,
    fallbackConnectorDeclarations?: ConnectorDeclaration[],
  ): AgentSessionConfig {
    // CLI flags win over the wire agentConfig — same slot they occupy today.
    const { cloud: sessionCloud, cloudConfig: sessionCloudConfig } =
      resolveEffectiveCloudForSession();
    return {
      projectDir: opts.projectDir,
      agentDir,
      driver: managedCodex ? "codex" : (opts.driver ?? wireAgentConfig?.driver),
      model: managedCodex ? wireAgentConfig?.model : (opts.model ?? wireAgentConfig?.model),
      provider: managedCodex ? "openai" : (opts.provider ?? wireAgentConfig?.provider),
      promptsDir: opts.promptsDir,
      sessionId,
      resumeSessionId: managedCodex ? undefined : resumeSessionId,
      ...(managedCodex ? { managedCodex } : {}),
      maxTurns,
      wsConfig,
      externalPromptFile: managedCodex ? undefined : process.env.SKAILE_SYSTEM_PROMPT_FILE,
      secretProvider,
      onAuthError: managedCodex ? undefined : onAuthError,
      onLimitBlocked,
      onProviderResponse,
      ...(apiKey ? { apiKey } : {}),
      // Non-default cloud transport from skaile.yaml agent-config.default,
      // plus the platform-resolved credential bundle (Vertex SA JSON already
      // materialized to a file path by resolveCloudSecretBundle). Omitted
      // entirely for `default` so the session config stays byte-identical.
      ...(sessionCloud !== "default" ? { cloud: sessionCloud } : {}),
      ...(sessionCloudConfig ? { cloudConfig: sessionCloudConfig } : {}),
      ...(cloudSecrets ? { cloudSecrets } : {}),
      ...(wireAgentConfig?.thinking ? { thinking: wireAgentConfig.thinking } : {}),
      ...(wireAgentConfig?.effort
        ? { effort: wireAgentConfig.effort as AgentSessionConfig["effort"] }
        : {}),
      // Protocol v2 `<CAPABILITIES>` block — joins each registered capability's
      // `promptFragment` (framework first, then client, then agent-side
      // surfaces). Built lazily inside `buildSessionConfig` so newly registered
      // capabilities (e.g. via mid-session `capability_register` commands) are
      // reflected on the next session rebuild.
      additionalPromptSections: (() => {
        const sections: string[] = [];
        const capSection = buildCapabilitiesPromptSection(capabilityRegistry);
        if (capSection) sections.push(capSection);
        return sections.length > 0 ? sections : undefined;
      })(),
      watch: {
        onSyncStatus: (mountId, status) => {
          sendEvent({
            type: "sync_status",
            mountId,
            ...status,
            phase: status.phase as
              | "enumerating"
              | "stubs_ready"
              | "downloading"
              | "initial_sync_complete"
              | "error",
          });
          if (status.phase === "stubs_ready") {
            log(
              `[serve] ${mountId}: file tree ready (${status.totalFiles} files, ${status.stubFiles} stubs)`,
            );
          } else if (status.phase === "initial_sync_complete") {
            log(
              `[serve] ${mountId}: sync complete (${status.downloadedFiles}/${status.totalFiles} downloaded)`,
            );
          }
        },
        onFileChanged: (connectorId, event) => {
          sendEvent({
            type: "file_changed",
            path: event.path,
            action: event.action,
            mountId: connectorId,
          });
        },
        // Forward connector lifecycle transitions to the platform. This is the
        // channel deferred (rclone-backed) filesystem mounts report progress on
        // after session_init_ack — a wedged remote shows as "still syncing".
        onConnectorStatus: (event) => {
          sendEvent(event);
        },
      },
      // Bring rclone-backed filesystem mounts up in the background so a slow
      // remote can't blow the platform's session_init_ack deadline and trigger
      // the container restart loop.
      deferFilesystemMounts: true,
      onLog: (line) => log(`[serve] ${line}`),
      capabilities: buildCapabilityHooks(),
      // Hand the registry to the session builder so external (declarative /
      // recipe-backed) MCP servers are spawned by the RUNNER and their tools
      // registered as `mcp`-origin capabilities — works on every driver, no
      // dependence on the claude-agent-sdk `mcpServers` query option.
      capabilityRegistry,
      // v3: pre-minted credentials satisfy the initial mint of `auth: backend`
      // connectors in-process; connector refresh (refresh / retry-401) now routes
      // through `host.refresh_credential` via this mediator.
      tokenMediator: connectorTokenMediator,
      ...(preMintedSecrets ? { preMintedSecrets } : {}),
      ...(aiProviderConfigId ? { aiProviderConfigId } : {}),
      // Disk-first fallback: only consumed when skaile.yaml + materialized
      // assets yield zero connector declarations (broker mode has no yaml).
      ...(fallbackConnectorDeclarations?.length ? { fallbackConnectorDeclarations } : {}),
      // Read per session build, not snapshotted here: this config object is
      // built once and reused for every recreate, and a server may be attached
      // at any point after that.
      getExtraMcpDeclarations: () => [...liveAttachedMcpDeclarations.values()],
    };
  }

  // Session-readiness gate — other command handlers await this promise before
  // accessing agentSession, driver, resourceManager, etc. In env mode it
  // resolves synchronously; in provisioned mode it resolves after
  // createAgentSession completes (which may involve slow mount operations).
  let resolveSessionReady!: () => void;
  let rejectSessionReady!: (err: Error) => void;
  // The gate is created once and never replaced, so its first settlement is final: a
  // rejection means no init, past or future, can make this runner serve a turn.
  let sessionReadyOutcome: "pending" | "resolved" | "rejected" = "pending";
  const sessionReady = new Promise<void>((resolve, reject) => {
    resolveSessionReady = () => {
      if (sessionReadyOutcome === "pending") sessionReadyOutcome = "resolved";
      resolve();
    };
    rejectSessionReady = (err) => {
      if (sessionReadyOutcome === "pending") sessionReadyOutcome = "rejected";
      reject(err);
    };
  });

  // In provisioned mode we defer session creation until secrets arrive.
  // In env mode we create the session immediately (existing behavior).
  let sessionConfig: AgentSessionConfig;
  let agentSession: AgentSession;
  let secretProvider: unknown;

  // v3 protocol — populated by the `session_init` handler. Used for the
  // identity-mismatch shutdown on successive inits and for capability
  // handlers that resolve project-scoped state.
  let runtimeIdentity:
    | import("@skaile/workspaces/types").SessionInitCommand["identity"]
    | undefined;

  // v3 protocol — pre-minted credentials delivered with `session_init`.
  // Wired into ConnectorManager via the `preMintedSecrets` option so the
  // initial mint of `auth: backend` connectors is satisfied in-process.
  let preMintedSecrets: PreMintedSecretProvider | undefined;

  // MCP servers attached after boot, keyed by id. This session's memory of what
  // `skaile.yaml` does not know about — replayed into every session build via
  // `getExtraMcpDeclarations`. See `registerLiveAttachedMcp`.
  const liveAttachedMcpDeclarations = new Map<string, McpServerDeclaration>();

  // Wire agentConfig fallback (disk-first, mirrors the connector fallback):
  // set per session_init when the disk profile carries no agent settings; the
  // applied key keeps replayed identical envelopes from disposing a live session.
  let wireAgentConfig: ResolvedAgentConfig | undefined;
  let managedCodex: AgentSessionConfig["managedCodex"];
  let managedInitFingerprint: string | undefined;
  let managedInitComplete = false;
  let appliedWireAgentConfigKey: string | undefined;

  // Dir names already staged into the driver's skills dir, so a late-materialized
  // skill triggers a driver restart exactly once. Never cleared: a dir that
  // vanishes then reappears (re-publish) won't re-trigger — fine for the static
  // declared/builtin set this serves.
  const stagedSkillDirs = new Set<string>();

  // Stage materialized skills BEFORE the session/driver is built so they are on
  // disk no matter when the driver discovers them (claude-sdk reads .claude/skills/
  // lazily today, but a future eager-discovery driver must still see them).
  await restageMaterializedSkills(false);

  /**
   * Stage each skaile.yaml-declared materialized skill from `.skaile/assets/skill/`
   * into the driver's skills dir — at boot AND per prompt, so a skill the platform
   * materializes after the container booted still loads. Idempotent; never throws.
   */
  async function restageMaterializedSkills(allowRestart: boolean): Promise<void> {
    // Managed native instructions/skills use only the explicit private instance inputs.
    if (managedHost) return;
    try {
      const { skillsDir } = resolveDriverPaths({
        driver:
          opts.driver ??
          wsConfig.agent_config?.default?.driver ??
          wireAgentConfig?.driver ??
          "claude-sdk",
      });
      // Canonical-contract invariant: the runner activates ONLY skaile.yaml-
      // declared skills — undeclared on-disk ("ghost") skills are never staged.
      const declared = (wsConfig.dependencies ?? []).flatMap((dep) => {
        try {
          const ref = parseAssetRef(dep);
          return ref.kind === "skill" ? [ref.name] : [];
        } catch {
          return [];
        }
      });
      if (declared.length === 0) return;
      const staged = await stageMaterializedSkills(opts.projectDir, skillsDir, { declared });
      // NOT protected by serial prompt processing — `handleCommand` is dispatched
      // fire-and-forget, so two prompts can reach this read-then-add at once and
      // both see the same skill as newly staged. Harmless today (a duplicate
      // restart, not a lost one) but do not build on the serial assumption.
      const newlyStaged = staged.filter((s) => !stagedSkillDirs.has(s));
      for (const s of staged) stagedSkillDirs.add(s);
      if (newlyStaged.length === 0) return;
      log(`[serve] staged ${newlyStaged.length} materialized skill(s): ${newlyStaged.join(", ")}`);
      // allowRestart is false at boot — the driver isn't up yet (and `driverStarted`
      // is still in its TDZ), so `&&` short-circuits and skills load on first start.
      // A skill landing after the driver started needs a restart to be discovered.
      if (allowRestart && driverStarted) {
        await restartDriverForSkillChanges(
          newlyStaged.map((s) => ({ name: s, action: "add" as const })),
        );
      }
    } catch (err) {
      // Never crash the container or block a turn, but surface it: a driver
      // restart failure (session dispose+recreate) is a real operational event.
      log(
        `[serve] restageMaterializedSkills failed (non-fatal): ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  // MCP server ids baked into the driver at its last (re)creation. The claude-sdk
  // driver fixes its tool set at creation, so these are live; only servers
  // hot-added afterwards are `live:false` until the next restart re-bakes them.
  let bakedMcpServerIds: ReadonlySet<string> = new Set();
  const captureBakedMcpServers = (): void => {
    const servers = agentSession?.getExternalMcpManager()?.listServers() ?? [];
    bakedMcpServerIds = new Set(servers.map((s) => s.id));
  };

  /**
   * Record a live-attached MCP server (`runner.attach_instance` /
   * `runner.add_mcp_server`) so every later session rebuild respawns it. The
   * platform never folds a live attach into `skaile.yaml`, and the rebuild reads
   * `skaile.yaml` only — without this the server is dropped for good on the next
   * recreate (skill attach, reconnect, restage).
   */
  const registerLiveAttachedMcp = (decl: McpServerDeclaration): void => {
    liveAttachedMcpDeclarations.set(decl.id, decl);
    // Its tools are live from the next turn on either way, so stamp it baked now
    // — otherwise this attach's `resources_available` would contradict the
    // `live: true` the capability just returned.
    bakedMcpServerIds = new Set([...bakedMcpServerIds, decl.id]);
  };

  if (secretsMode === "provisioned") {
    log("[serve] secrets mode: provisioned — waiting for provision_secrets command");
  } else {
    sessionConfig = buildSessionConfig();
    agentSession = await createAgentSession(sessionConfig);
    emitSystemPromptComposed(agentSession, sendEvent);

    registerSessionComposition(agentSession);
    resolveSessionReady();
    // env mode builds its session here (not via session_init), so snapshot the
    // declared MCP set now or emitAvailability would report live:false.
    captureBakedMcpServers();
  }

  // Mutable references — updated after provisioning in provisioned mode
  let resourceManager: any | null = secretsMode === "env" ? agentSession!.resourceManager : null;

  // claude-sdk bakes its tool set at driver creation; bridge drivers
  // recompose tools per turn, so they pick up a hot-plugged MCP server live.
  const driverPicksUpToolsLive = () =>
    supportsLiveToolUpdates(agentSession?.driverType, !!sessionConfig?.managedCodex);
  const emitResourcesAvailable = () =>
    sendEvent(
      buildResourcesAvailablePayload(
        resourceManager,
        agentSession?.getExternalMcpManager(),
        driverPicksUpToolsLive(),
        bakedMcpServerIds,
      ),
    );

  if (secretsMode === "env") {
    if (!agentSession!.provider) {
      log(
        `[serve] WARNING: no API key found for ${agentSession!.provider ?? "anthropic"} — agent calls will fail`,
      );
      log(`[serve] Pass via env var (e.g. ANTHROPIC_API_KEY) or .skaile/settings.json`);
    }

    if (resourceManager) {
      const watched: string[] = resourceManager.watchedResources ?? [];
      if (watched.length > 0) {
        log(`[serve] watching ${watched.length} resource(s): ${watched.join(", ")}`);
      }
    }
  }

  // Surface runtime-asset resolution warnings (missing drivers, unavailable
  // repositories) as validation_warning events so the platform frontend can
  // show actionable hints. Same event shape as the legacy skill validator.
  // Skip in provisioned mode — the session does not exist yet at this point;
  // warnings will be emitted after createAgentSession runs in the
  // provision_secrets handler.
  if (secretsMode === "env" && agentSession!.runtimeAssets) {
    emitRuntimeAssetWarnings(agentSession!.runtimeAssets);
  }

  // Validate top-level skaile.yaml dependencies against deployed skills
  // (graceful degradation). The legacy ai_resources[] block is gone — deps are
  // a flat top-level list of canonical refs now.
  validateSkaileYamlDependencies();

  /**
   * Emit `validation_warning` events for each runtime-asset resolution warning
   * (missing drivers, unavailable repositories). Same event shape as the
   * legacy skill validator.
   */
  function emitRuntimeAssetWarnings(
    runtimeAssets: NonNullable<AgentSession["runtimeAssets"]>,
  ): void {
    for (const w of runtimeAssets.warnings) {
      const asset =
        w.code === "missing_driver" && w.declarationId
          ? `${w.declarationId} → ${w.name ?? "unknown"}`
          : (w.name ?? w.code);
      log(`[serve] validation_warning: ${w.code}: ${w.message}`);
      sendEvent({
        type: "validation_warning",
        asset,
        reason: w.code,
        suggestion: w.message,
      });
    }
  }

  /**
   * Validate top-level skaile.yaml dependencies against deployed skills,
   * emitting a `validation_warning` for each declared skill dependency that is
   * not present on disk. Graceful degradation — non-fatal; validation failure
   * should never crash the container.
   */
  function validateSkaileYamlDependencies(): void {
    try {
      const wsConfig = resolveSkWorkspaceConfig(opts.projectDir);
      for (const dep of wsConfig.dependencies ?? []) {
        let ref: string | undefined;
        try {
          const parsed = parseAssetRef(dep);
          if (parsed.kind !== "skill") continue;
          ref = parsed.name;
        } catch {
          continue;
        }
        const skillDir = join(opts.projectDir, ".claude", "skills", ref);
        if (!existsSync(skillDir)) {
          log(`[serve] validation_warning: dependency "${dep}" not found — continuing without it`);
          sendEvent({
            type: "validation_warning",
            asset: dep,
            reason: "not_found",
            suggestion: "Sync the catalog and re-add this asset",
          });
        }
      }
    } catch {
      // Non-fatal — validation failure should never crash the container
    }
  }

  // EventNormalizer deduplicates and normalizes events from the driver (e.g. subagent detection).
  // Only applied in serve mode — REPL and flow modes receive raw events.
  // TODO: candidate to move into createAgentSession() if REPL/flow are found to need normalisation.
  const normalizer = new EventNormalizer();

  /**
   * Forward a normalized agent event, rewriting file_changed paths so they
   * are mounts-relative and tagged with their mountId.
   *
   * See file-changed-rewriter.ts for the reasoning — the short version is
   * that Claude SDK Write/Edit tool results carry cwd-relative filePaths,
   * and the frontend's file-change listener assumes they are mounts-relative.
   */
  // Protocol v2 — `[CUSTOM:type:json]`, `[REACT:emoji]`, `[PASS]` text-marker
  // parsing and the AAP raw-JSON-events-as-text robustness path are removed.
  // The agent now invokes `platform.react`, `platform.pass`, and
  // `platform.custom_message` capability tools instead.

  function forwardNormalizedEvent(event: AgentEvent): void {
    // Suppress event forwarding during compaction turns -- the orchestrator's
    // captureHandler captures text independently and the host should not see
    // the compaction turn's intermediate events.
    if (compacting) return;

    // Track durable events for compaction seq counter
    const isTransient =
      event.type === "status" ||
      event.type === "sync_status" ||
      event.type === "resources_available" ||
      event.type === "resource_response" ||
      event.type === "commands_available";
    if (!isTransient) {
      eventSeq++;
      compactionOrchestrator?.trackMessage(eventSeq);
    }

    if (event.type === "finished") {
      // Check auto-compaction after turn completes (skip if already compacting).
      // Deferred to a macrotask: this runs inside the driver's own result emit,
      // and a compaction prompt enqueued there lands on a query that is still
      // unwinding the turn that triggered it. Re-checked at fire time because
      // the state can change while we wait.
      if (!compacting && compactionOrchestrator?.shouldCompact()) {
        const compactionTimer = setTimeout(() => {
          if (compacting || !compactionOrchestrator?.shouldCompact()) return;
          compactionOrchestrator.compact("threshold").catch((err: unknown) => {
            log(
              `[serve] auto-compaction failed: ${err instanceof Error ? err.message : String(err)}`,
            );
          });
        }, 0);
        compactionTimer.unref?.();
      }
      // Surface the SDK session id (tier-1 resume token) + model + capability
      // signature so the platform can persist them on the Session independently
      // of compaction. Re-emitted whenever any of the three drifts. Short-
      // circuit on a missing session id BEFORE hashing the signature so early
      // no-session turns don't pay for the compute.
      const driverSessionId = driver?.runtimeSessionId;
      if (driverSessionId) {
        const runtimeSessionEvent = planRuntimeSessionEmit({
          driverSessionId,
          model: driver?.getModel?.() ?? null,
          capabilitySignature: computeCapabilitySignature(capabilityRegistry.list()),
          lastEmitted: lastEmittedRuntimeSession,
        });
        if (runtimeSessionEvent) {
          lastEmittedRuntimeSession = {
            driverSessionId: runtimeSessionEvent.driverSessionId,
            model: runtimeSessionEvent.model,
            capabilitySignature: runtimeSessionEvent.capabilitySignature,
          };
          sendEvent(runtimeSessionEvent);
        }
      }
    }

    if (!resourceManager) {
      sendEvent(event);
      return;
    }
    // Filesystem-face connectors expose a `mountPath`; tool-only connectors do not.
    const mounts: MountPoint[] = (
      resourceManager.listConnectors() as Array<{ id: string; mountPath?: string }>
    )
      .filter((info) => typeof info.mountPath === "string")
      .map((info) => ({
        id: info.id,
        mountPath: info.mountPath as string,
      }));
    sendEvent(rewriteFileChangedPath(event, opts.projectDir, mounts));
  }
  let driverStarted = false; // Lazy start — driver initializes on first prompt command
  // The in-flight start, shared by concurrent callers. A flag cannot guard the
  // start because it is raised after the await; see
  // `_devlog/notes/2026-08-20-driver-start-memoization.md`.
  let driverStartInFlight: Promise<void> | null = null;
  // Serializes prompt turns against out-of-band driver swaps: a swap defers to
  // an in-flight turn, and a prompt mid-swap binds to the fresh driver.
  const driverSwapGate = new DriverSwapGate();
  // Holds user turns off the driver for as long as a compaction occupies it.
  const compactionGate = new CompactionGate();
  // FIFO tail for tool-change restarts. `beginSwap` overwrites rather than
  // queues, so two overlapping recreates would double-dispose and leak a live
  // driver — chaining keeps them one at a time. See `enqueueDriverRestart`.
  let driverRestartChain: Promise<void> = Promise.resolve();
  let driver = secretsMode === "env" ? agentSession!.driver : (undefined as any);
  let secretsProvisioned = secretsMode === "env"; // env mode starts ready

  /**
   * Adopt the recreated `agentSession.driver`, killing the outgoing one first —
   * `dispose()` frees resources but not the driver, and a leaked live driver
   * double-streams every turn. All mid-session recreate sites must route here.
   */
  function swapDriver(): void {
    const incoming = agentSession.driver;
    if (driver && driver !== incoming) {
      try {
        driver.removeAllListeners("agent-event");
        driver.removeAllListeners("error");
        driver.kill();
      } catch (err) {
        log(
          `[serve] failed to retire previous driver: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    }
    driver = incoming;
    normalizer.reset();
    driverStarted = false;
    // Clear alongside the flag, so a caller whose start this swap superseded
    // starts the incoming driver instead of awaiting the abandoned promise.
    driverStartInFlight = null;
  }

  // ---------------------------------------------------------------------------
  // Compaction orchestrator
  // ---------------------------------------------------------------------------

  /** Local seq counter for durable events. */
  let eventSeq = 0;
  /**
   * Last tier-1 resume snapshot (driver session id + model + capability
   * signature) emitted via a `runtime_session` event. Tracks all three so we
   * re-emit on any drift, but not every turn. See RuntimeSessionEvent.
   */
  let lastEmittedRuntimeSession: RuntimeSessionSnapshot | undefined;
  /** True while a compaction turn is in progress -- suppresses event forwarding. */
  let compacting = false;
  /**
   * Which compaction armed the suppression above. Shared across orchestrator
   * generations, unlike `gateHold`: a driver swap builds a new orchestrator, so
   * a stale instance's end callback must not un-suppress under the live one.
   */
  let suppressedBy: CompactionHold | null = null;
  let compactionOrchestrator: CompactionOrchestrator | null = null;

  const compactionConfig = wsConfig.compaction;

  /** Build (or rebuild) the compaction orchestrator for the current driver. */
  function rebuildCompactionOrchestrator(): void {
    if (!driver) return;
    // Scoped to this orchestrator: a driver swap builds a fresh one with its own
    // `_isCompacting`, so two compactions can briefly overlap and the older one
    // settling must not open the gate under the newer.
    let gateHold: CompactionHold | null = null;
    compactionOrchestrator = new CompactionOrchestrator({
      driver,
      config: compactionConfig,
      onCompactionStart: (trigger) => {
        compacting = true;
        gateHold = compactionGate.begin();
        suppressedBy = gateHold;
        sendEvent({ type: "status", phase: "compacting" } as AgentEvent);
        log(`[serve] compaction started (trigger=${trigger})`);
      },
      onSnapshot: (snapshot) => {
        sendEvent(snapshot as AgentEvent);
      },
      onCompactionAttempt: (event) => {
        sendEvent(event as AgentEvent);
      },
      onCompactionEnd: () => {
        // Only the compaction that armed the suppression may lift it.
        if (suppressedBy !== gateHold) return;
        compacting = false;
        suppressedBy = null;
        sendEvent({ type: "status", phase: "idle" } as AgentEvent);
      },
      onCompactionSettled: () => {
        compactionGate.settle(gateHold);
        gateHold = null;
      },
      onLog: (line) => log(`[serve] ${line}`),
      // Phase 3: stamp every CompactionAttemptEvent with the registry's
      // current signature so wake-time tier-1 selection has the input it
      // needs. The registry exists for the entire serve lifetime, so this
      // closure is safe across orchestrator rebuilds.
      getCurrentCapabilitySignature: () => computeCapabilitySignature(capabilityRegistry.list()),
    });
  }

  if (secretsMode === "env" && driver) {
    rebuildCompactionOrchestrator();
  }

  // ---------------------------------------------------------------------------
  // Active flow executions (Phase 4 of the flow-connector extraction)
  // ---------------------------------------------------------------------------
  //
  // MVP allows one active flow per session (decision F14). Each entry holds
  // the FlowAdapter handle + the connectorId used as the bus's queue key.
  // Phase 4 dropped the FlowOrchestrator: SessionStimulusBus is the sole
  // turn driver now, with FlowAdapter signalling on every mutation through
  // its `useBusForStimulus: true` wiring (see `factory-assets/connectors/flow/
  // adapter.ts`).

  interface ActiveFlow {
    runId: string;
    flowDef: FlowDefinition;
    adapter: FlowAdapter;
    handle: ConnectorHandle;
    /** Bus queue key — matches `connectorId` passed to `flowAdapter.connect`. */
    connectorId: string;
    /** Whether hydrate compatibility preparation changed the host snapshot. */
    hydratedStateChanged?: boolean;
  }

  const activeFlows = new Map<string, ActiveFlow>();
  let removeCodexFlowTools: (() => void) | undefined;

  /**
   * Per-session stimulus bus — built lazily on the first flow start so the
   * driver is guaranteed to exist by the time we wire `driveTurn`. The
   * adapter calls `bus.signal(connectorId, ...)` on every mutation; the
   * bus coalesces and invokes `driveTurn` to run one agent turn.
   */
  let stimulusBus: SessionStimulusBus | null = null;
  /** Runner-side default per-flow turn timeout (4 hours). */
  const FLOW_TURN_TIMEOUT_MS = 4 * 60 * 60 * 1000;

  /** Shorthand for the single MVP active flow. Returns null if none. */
  function getOnlyActiveFlow(): ActiveFlow | null {
    const iter = activeFlows.values().next();
    return iter.done ? null : iter.value;
  }

  // Flow-specific readiness gate (Protocol 3.7). Drained by createActiveFlow so
  // a mutate that lost the wake race to the bootstrap `hydrate` applies instead
  // of failing with `Connector not found: "flow"`.
  const flowConnectorReadiness = createFlowConnectorReadiness(() => getOnlyActiveFlow() !== null);
  const flowMutateConnectorWaitMs = resolveFlowMutateConnectorWaitMs();

  /**
   * Start the session's driver once and install its persistent event
   * forwarding — the single path for every caller, concurrent or not.
   *
   * Memoizes the start rather than guarding it with a flag, and snapshots the
   * driver it started so a swap landing mid-start cannot wire the forwarder
   * onto a driver nobody started. Both races, and why each one silently kills
   * the session, are in `_devlog/notes/2026-08-20-driver-start-memoization.md`.
   *
   * Raises `driverStarted` only once the listeners are wired, so observing it
   * true guarantees a forwarding driver.
   */
  async function ensureDriverStarted(): Promise<void> {
    // Each pass either starts the current driver or loses it to a swap. Bounded
    // because repeated swaps during repeated starts is pathological, and
    // failing loudly beats spinning.
    for (let attempt = 0; attempt < DRIVER_START_ATTEMPTS; attempt += 1) {
      if (driverStarted) return;
      if (!driverStartInFlight) {
        const starting = driver;
        const inFlight: Promise<void> = (async () => {
          await starting.start();
          // Superseded: commit nothing. `swapDriver` has already cleared the
          // memo, so the caller's next pass starts the incoming driver.
          if (driver !== starting) return;
          starting.on("agent-event", (event: unknown) => {
            const normalized = normalizer.normalize(event as never);
            for (const e of normalized) forwardNormalizedEvent(e);
          });
          starting.on("error", (err: Error) => {
            log(`[serve] driver error: ${err.message}`);
            sendEvent({ type: "error", message: err.message, fatal: true });
          });
          driverStarted = true;
        })().catch((err: unknown) => {
          // Only if still ours — a swap may have installed a newer start.
          if (driverStartInFlight === inFlight) driverStartInFlight = null;
          // Superseded starts commit nothing, however they ended: a retired
          // driver's failure is not the incoming driver's, so retry against it.
          if (driver !== starting) return;
          throw err;
        });
        driverStartInFlight = inFlight;
      }
      await driverStartInFlight;
      if (driverStarted) return;
    }
    throw new Error(`driver start superseded by a swap ${DRIVER_START_ATTEMPTS} times running`);
  }

  /** Start the session's existing driver once and install its persistent event forwarding. */
  async function ensureFlowDriverStarted(): Promise<void> {
    await ensureDriverStarted();
  }

  /**
   * Build (or rebuild) the per-session SessionStimulusBus. The bus's
   * `driveTurn` callback wraps the active driver's `prompt(...)` and waits
   * for `agent_end`. It also rebuilds the full orchestrator prompt around
   * the bus-emitted stimulus fragment so the agent sees the same five-
   * section flow context the legacy orchestrator produced.
   *
   * The bus is created on first use and reused across flows in the same
   * session. When the driver is recreated (mid-session skill deploy or
   * resume), the bus's closure over `driver` updates because we mutate the
   * `driver` binding in place — hence the late-binding `driver` access
   * below rather than capturing the current reference.
   */
  function getOrCreateStimulusBus(): SessionStimulusBus {
    if (stimulusBus) return stimulusBus;
    stimulusBus = createSessionStimulusBus({
      driveTurn: async (promptFragment: string, metas: Array<Record<string, unknown>>) => {
        // Locate the targeted flow from the metas so we can rebuild the
        // full prompt with the correct flow definition + state. The bus
        // queue is keyed by connectorId, so all metas in one batch share
        // the same flow.
        const lastMeta = metas[metas.length - 1] ?? {};
        const runId = typeof lastMeta.runId === "string" ? lastMeta.runId : null;
        const entry = runId ? activeFlows.get(runId) : getOnlyActiveFlow();
        if (!entry) {
          serverLog.warn("driveTurn fired with no active flow", { runId });
          return;
        }

        // Most-specific typed stimulus in the batch wins over a coalesced
        // trailing generic `state_changed` (the adapter's auto-signal).
        const stimulus = stimulusFromMetas(metas);
        // The fragment passed in is the adapter's `renderStimulusPrompt(stim)`.
        // We discard it and rebuild the full orchestrator prompt below; the
        // structured stimulus already encodes everything the fragment did.
        void promptFragment;

        await entry.adapter.drainRuntimeNodes(entry.handle);
        const execution = entry.adapter.getExecution(entry.handle);
        if (execution.done || execution.status === "failed" || execution.status === "cancelled") {
          return;
        }
        if (!entry.adapter.shouldDriveAgentTurn(entry.handle, stimulus)) return;

        await ensureFlowDriverStarted();

        await driveOrchestratorTurn({
          flow: entry.flowDef,
          execution,
          stimulus,
          emit: sendEvent,
          deliver: (prompt) => awaitFlowAgentEnd(prompt, FLOW_TURN_TIMEOUT_MS),
        });
      },
      log: serverLog,
    });
    return stimulusBus;
  }

  /**
   * Send a prompt to the active driver and resolve when the agent emits
   * `agent_end` (or reject on `error`). Used by the stimulus bus's
   * `driveTurn` callback. Mirrors the legacy orchestrator's wait helper
   * but uses a scoped per-turn listener so the persistent forwarding
   * listener installed at driver-start time is left intact.
   */
  function awaitFlowAgentEnd(prompt: string, timeoutMs: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const onEvent = (event: BridgeAgentEvent) => {
        if (event.type === "agent_end") done();
        else if (event.type === "error") {
          const msg =
            (event as { error?: string; message?: string }).error ??
            (event as { message?: string }).message ??
            "Agent error";
          done(new Error(msg));
        }
      };
      const done = (err?: Error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        // EventEmitter.removeListener / .off — bridge drivers extend
        // node:events EventEmitter so this is always available.
        (driver as { off?: (e: string, h: (...a: unknown[]) => void) => void }).off?.(
          "agent-event",
          onEvent as unknown as (...a: unknown[]) => void,
        ) ??
          (
            driver as {
              removeListener: (e: string, h: (...a: unknown[]) => void) => void;
            }
          ).removeListener("agent-event", onEvent as unknown as (...a: unknown[]) => void);
        if (err) reject(err);
        else resolve();
      };
      const timer = setTimeout(
        () => done(new Error(`Flow turn timed out after ${timeoutMs / 1000}s`)),
        timeoutMs,
      );
      driver.on("agent-event", onEvent);
      driver.prompt(prompt).catch(done);
    });
  }

  /**
   * The ConnectorManager the flow connector lives on. Lazily created because a
   * session may declare no connectors in skaile.yaml and still need a home for
   * the FlowAdapter. Call only after any pending recreate — it repoints the var.
   */
  function ensureResourceManagerForFlow(): ConnectorManager {
    if (!resourceManager) {
      registerBuiltinConnectors();
      resourceManager = new ConnectorManager(opts.projectDir, {
        catalogEntries: getSessionCatalogEntries(agentSession),
        tokenMediator: connectorTokenMediator,
      });
      log("[serve] created ConnectorManager for flow execution (no other connectors declared)");
    }
    return resourceManager;
  }

  /**
   * Restart a running claude-sdk driver so a flow connector registered after it
   * started is in its tool list (e.g. the user chatted before clicking Start, or
   * this is a second run in the same session). Returns whether it restarted;
   * `swapDriver()` clears `driverStarted` and rebinds `driver`.
   *
   * No longer gated on `buildSdkConnectorTools` returning non-null as it once
   * was: that returns null only when the Agent SDK import fails, which cannot
   * happen while the live driver IS the claude-sdk driver.
   */
  async function recreateDriverForFlowTools(): Promise<boolean> {
    if (!driverStarted || agentSession?.driverType !== "claude-sdk") return false;
    const prevSessionId = driver.runtimeSessionId;
    log(
      `[serve] restarting driver to register flow connector tools (resume=${prevSessionId ?? "none"})`,
    );
    await recreateAgentSession({ resumeSessionId: prevSessionId });
    return true;
  }

  /**
   * Instantiate a FlowAdapter for a flow definition and register it as the
   * active flow. Wires the adapter's onChange to emit state_changed via
   * `sendEvent`, and connects it to the per-session stimulus bus so every
   * mutation kicks an agent turn (Phase 4 of the flow-connector extraction).
   */
  async function createActiveFlow(
    flowDef: FlowDefinition,
    seedOrExecution:
      | { seed: { runId: string; startedBy: string; autonomousMode?: boolean } }
      | { execution: FlowExecution },
  ): Promise<ActiveFlow> {
    const bus = getOrCreateStimulusBus();
    const nodeExecutor = new SessionNodeExecutor({ workspaceDir: opts.projectDir });
    // Same credential path the session driver used: `sessionConfig` already
    // carries the resolved apiKey/cloud/cloudConfig/cloudSecrets the platform
    // provisioned for THIS session (`buildSessionFromInit` → `buildSessionConfig`)
    // — read them off it rather than re-resolving anything.
    const managedSubprompts = sessionConfig.managedCodex?.subpromptModels;
    if (sessionConfig.managedCodex && !managedSubprompts) throw new Error("AI_PROFILE_INVALID");
    const subpromptRunner = new SessionSubpromptRunner({
      driverType: agentSession!.driverType,
      provider: agentSession!.provider,
      sessionModel: agentSession!.model,
      workspaceDir: opts.projectDir,
      apiKey: sessionConfig.apiKey,
      cloud: sessionConfig.cloud,
      cloudConfig: sessionConfig.cloudConfig,
      cloudSecrets: sessionConfig.cloudSecrets,
      ...(managedSubprompts
        ? {
            modelTierOverrides: {
              small: managedSubprompts.small,
              default: agentSession!.model,
              deep: managedSubprompts.deep,
            },
            availableModels: managedSubprompts.available,
            createIsolatedDriver: async (model: string | undefined) => {
              const parentPrivate = sessionConfig.managedCodex!.runtime.privateDirectory;
              const privateDirectory = await mkdtemp(join(dirname(parentPrivate), ".subprompt-"));
              try {
                const child = await createManagedCodexSubpromptDriver(
                  sessionConfig,
                  model,
                  opts.projectDir,
                  privateDirectory,
                );
                return {
                  driver: child,
                  dispose: () => rm(privateDirectory, { recursive: true, force: true }),
                };
              } catch (error) {
                await rm(privateDirectory, { recursive: true, force: true });
                throw error;
              }
            },
          }
        : {}),
    });
    const inlineSubFlowRunner = new SessionInlineSubFlowRunner({
      resolveFlow: (flowId) => loadFlow(resolveFlowPath(flowId, opts.projectDir)),
      nodeExecutor,
      subpromptRunner,
      driveAgentTurn: async ({ flow, execution, stimulus }) => {
        await ensureFlowDriverStarted();
        await driveOrchestratorTurn({
          flow,
          execution,
          stimulus,
          emit: sendEvent,
          deliver: (prompt) => awaitFlowAgentEnd(prompt, FLOW_TURN_TIMEOUT_MS),
        });
      },
    });
    const baseOptions = {
      stimulusBus: bus,
      useBusForStimulus: true,
      nodeExecutor,
      subpromptRunner,
      inlineSubFlowRunner,
    };
    const options =
      "execution" in seedOrExecution
        ? { flow: flowDef, execution: seedOrExecution.execution, ...baseOptions }
        : { flow: flowDef, seed: seedOrExecution.seed, ...baseOptions };
    const seedRunId =
      "execution" in seedOrExecution ? seedOrExecution.execution.runId : seedOrExecution.seed.runId;
    const connectorId = `flow:${seedRunId}`;

    // Connect via ConnectorManager so the FlowAdapter's tools become
    // visible to buildSdkConnectorTools below. Direct `new FlowAdapter()`
    // would leave the adapter outside the manager's connectors map and
    // the SDK driver would never see the flow tools. `setUpFlowConnector`
    // owns the recreate-before-connect ordering (workspaces#484).
    const { manager, handle } = await setUpFlowConnector<ConnectorManager>({
      recreateForFlowTools: recreateDriverForFlowTools,
      resolveManager: ensureResourceManagerForFlow,
      connect: (m) =>
        m.connect({
          id: connectorId,
          driver: "flow",
          access: "read-write" as const,
          options,
        }),
    });
    const adapter = manager.get(connectorId).connector as unknown as FlowAdapter;

    const runId = adapter.getExecution(handle).runId;
    const hydratedStateChanged =
      "execution" in seedOrExecution &&
      JSON.stringify(adapter.getExecution(handle)) !== JSON.stringify(seedOrExecution.execution);

    // Wire the adapter's onChange so the host's flow-state mirror stays in sync.
    adapter.onStateChange(handle, (state) => {
      sendEvent({
        type: "state_changed",
        store: `flow:${state.runId}`,
        state: state as unknown as Record<string, unknown>,
      });
    });

    // Finding #2 (workspaces#396): `connect()` seeds the initial execution but
    // does NOT fire `onChange`, so on a fresh `op:start` the host never received
    // a `state_changed` until the first mutation. Emit it now, right after
    // `onStateChange` is registered, so hosts get the initial snapshot at start
    // and can drop their eager-persist workaround. Idempotent latest-wins on the
    // host side ⇒ a duplicate of the first mutation's snapshot is harmless.
    // The hydrate path normally restores a snapshot the host already holds.
    // Publish it only when compatibility preparation changed that snapshot.
    if ("seed" in seedOrExecution || hydratedStateChanged) {
      sendEvent({
        type: "state_changed",
        store: `flow:${runId}`,
        state: adapter.getExecution(handle) as unknown as Record<string, unknown>,
      });
    }

    // Test seam (workspaces#396): expose a no-LLM flow driver hook so the echo
    // driver's scripted-flow mode can advance nodes deterministically. Not part
    // of any wire protocol; production drivers ignore it.
    (driver as unknown as { config: { flowTestDriver?: unknown } }).config.flowTestDriver = {
      getState: () => adapter.getAgentExecution(handle),
      execute: (op: string, args: Record<string, unknown>) => adapter.executeOp(handle, op, args),
      mandatoryGates: () => adapter.getMandatoryAgentApprovalNodeIds(handle),
    };

    // Rebuild the SDK MCP server tools so the agent can call the new flow
    // connector's tools. Built against `manager` — the one that survived any
    // recreate above — because every tool handler closes over it.
    if (agentSession?.driverType === "claude-sdk") {
      try {
        const server = await buildSdkConnectorTools(manager, { z: _z });
        // workspaces#399: expose the flow connector's agent operations as
        // first-class callable tools (`mcp__skaile-flow__start_node`, …). The
        // generic connector server never emits per-op tools (and omits
        // `connector_exec` under shell access), so without this the agent has
        // no way to invoke start_node/complete_node/etc. that the orchestrator
        // prompt tells it to call.
        const flowServer = await buildSdkFlowTools(manager, connectorId, { z: _z });
        if (server) {
          (driver as any).config.mcpServers = flowServer
            ? { "skaile-connectors": server, "skaile-flow": flowServer }
            : { "skaile-connectors": server };
          log(
            `[serve] rebuilt MCP connector tools for flow ${connectorId}` +
              (flowServer ? " (+ skaile-flow agent tools)" : ""),
          );
        } else {
          log(`[serve] buildSdkConnectorTools returned null for flow ${connectorId}`);
        }
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        log(`[serve] failed to rebuild MCP tools for flow: ${errMsg}`);
      }
    }

    if (agentSession?.driverType === "codex" && !driver.acceptsReplyAsPrompt) {
      removeCodexFlowTools?.();
      removeCodexFlowTools = registerCodexFlowTools(capabilityRegistry, manager, connectorId);
    }

    // Announce the run's connector. Unconditional: on the recreate path the
    // session's own emit ran before it existed, and on the cold path nothing
    // announced it at all — the gateway learned of it only on a later reconnect.
    emitAvailability();

    const entry: ActiveFlow = {
      runId,
      flowDef,
      adapter,
      handle,
      connectorId,
      hydratedStateChanged,
    };
    const pruned = registerActiveFlow(activeFlows, runId, entry);
    if (pruned.length > 0) serverLog.debug("pruned terminal flow runs", { runIds: pruned });
    // Release mutates parked on the wake race — the run is addressable now.
    flowConnectorReadiness.drain();
    return entry;
  }

  // Pruning policy: a finished run stays in `activeFlows` until the next run
  // registers (see `registerActiveFlow`), so host ops aimed at it still resolve
  // through the bare-"flow" rewrite and its terminal snapshot still reaches a
  // host that reconnects. The `start` guard filters on status instead of
  // presence. The connector itself is never disconnected here — hosts do that
  // via `remove_resource` (the adapter's `start` op also reuses it in-place by
  // replacing the execution).

  // ---------------------------------------------------------------------------
  // Refresh-flag watcher (Step 9a — wake-mid-401 hardening)
  // ---------------------------------------------------------------------------
  //
  // The Tier-2 git credential-helper script touches
  // `<projectDir>/.skaile/refresh-request` when it detects a stale credentials
  // file (mtime > triggerAgeSec). The runner watches that path via `fs.watch`
  // and dispatches `request_access_token { reason: 'retry-401' }` for every
  // git mount with `exposeAccessToken: true` AND `auth: 'backend'`. The mount
  // driver's `refreshExposedCredential` method does the actual mediation +
  // file rewrite via `atomicReplaceCredential`, mirroring the proactive 5-min
  // pre-expiry refresh timer.
  //
  // Pre-create the flag file: `fs.watch` on a non-existent path throws on
  // Linux (ENOENT) and silently misses events on macOS until the file is
  // created. Mode 0660 — owner+group rw so the helper script (running as
  // the agent uid in the skaile group) can `: > "$FLAG_FILE"` it.
  //
  // Spec: `_devlog/specs/2026-05-07-unified-credential-mediation.md` Step 9a.

  const refreshFlagPath = join(opts.projectDir, ".skaile", "refresh-request");
  try {
    mkdirSync(join(opts.projectDir, ".skaile"), { recursive: true });
    if (!existsSync(refreshFlagPath)) {
      writeFileSync(refreshFlagPath, "", { mode: 0o660, flag: "a" });
    }
  } catch (err) {
    serverLog.warn("failed to pre-create refresh-flag — wake-mid-401 disabled", {
      error: err instanceof Error ? err.message : String(err),
    });
  }

  let lastRefreshAt = 0;
  const REFRESH_DEBOUNCE_MS = 2_000;

  let refreshFlagWatcher: FSWatcher | null = null;
  try {
    refreshFlagWatcher = fsWatch(refreshFlagPath, (event) => {
      if (event !== "change") return;
      const now = Date.now();
      if (now - lastRefreshAt < REFRESH_DEBOUNCE_MS) return;
      lastRefreshAt = now;
      // Dispatch is extracted into `refresh-flag-dispatcher.ts` so the logic
      // can be unit-tested without spinning up the full WebSocket server.
      void dispatchRefreshToExposedGitMounts(resourceManager, serverLog);
    });
  } catch (err) {
    serverLog.warn("fs.watch on refresh-flag failed — wake-mid-401 disabled", {
      error: err instanceof Error ? err.message : String(err),
    });
  }

  // ---------------------------------------------------------------------------
  // Fleet-health flag watcher (fleet read-only health gate, consume side)
  // ---------------------------------------------------------------------------
  //
  // The platform fleet-health poll writes its verdict per connector into
  // `<projectDir>/.skaile/fleet-health/<connectorId>` (body: healthy | degraded |
  // error). The runner watches that directory and forwards each verdict to the
  // ConnectorManager's `reportFleetHealth`, which trips/lifts the read-only gate
  // on the live fleet bind (catching the *silently-empty* degradation the local
  // probe cannot). Mirrors the refresh-flag watcher above.

  const fleetHealthDir = join(opts.projectDir, ".skaile", "fleet-health");
  try {
    // Group-writable + setgid (0o2770) so the platform backend (different uid,
    // same `skaile` gid) can WRITE the verdict flag the runner READS. chmod also
    // repairs a 0o755 dir left by an older runner; mkdir's mode is umask-masked.
    mkdirSync(fleetHealthDir, { recursive: true, mode: 0o2770 });
    chmodSync(fleetHealthDir, 0o2770);
  } catch (err) {
    serverLog.warn(
      "failed to pre-create/chmod fleet-health dir — platform health signal may be degraded",
      {
        error: err instanceof Error ? err.message : String(err),
      },
    );
  }

  let fleetHealthWatcher: FSWatcher | null = null;
  try {
    fleetHealthWatcher = fsWatch(fleetHealthDir, (_event, fileName) => {
      // Accept both `change` and `rename`: on Linux the FIRST write to a new
      // flag file fires `rename` (inotify IN_CREATE), not `change`, so filtering
      // to `change` would drop the platform's initial verdict on a newly-degraded
      // mount — exactly the silent-degradation case this signal exists to catch.
      // dispatchFleetHealthFlag is idempotent and the gate dedups, so re-reading
      // the same file on either event is safe.
      if (!fileName) return;
      void dispatchFleetHealthFlag(fleetHealthDir, fileName.toString(), resourceManager, serverLog);
    });
  } catch (err) {
    serverLog.warn("fs.watch on fleet-health dir failed — platform health signal disabled", {
      error: err instanceof Error ? err.message : String(err),
    });
  }

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  async function cleanup() {
    if (managedHost) managedBootstrapClosed = true;
    // A runner that is tearing down must not tell a reconnecting gateway it is initialized.
    initAcknowledged = false;
    // Bound the wait on sessionReady — in provisioned mode the session may
    // never be built (no platform has connected yet), and in env mode it has
    // already resolved. Either way we must not block shutdown on it.
    try {
      await Promise.race([
        sessionReady,
        new Promise<void>((resolve) => {
          const t = setTimeout(resolve, 2000);
          t.unref?.();
        }),
      ]);
    } catch {
      /* session may have failed to build */
    }
    try {
      if (managedHost) await driver?.dispose();
      else driver?.kill();
    } catch {}
    aiCredentialRefreshScheduler.cancel();
    // Reject any pending capability round-trips so the bridge surfaces tool
    // errors instead of hanging forever after the session shuts down.
    for (const [callId, pending] of pendingCapabilityCalls) {
      clearTimeout(pending.timer);
      pending.reject(new Error(`Session terminated before capability_result (callId=${callId})`));
    }
    pendingCapabilityCalls.clear();
    try {
      refreshFlagWatcher?.close();
    } catch {
      /* best-effort — watcher may already be closed */
    }
    refreshFlagWatcher = null;
    try {
      fleetHealthWatcher?.close();
    } catch {
      /* best-effort — watcher may already be closed */
    }
    fleetHealthWatcher = null;
    if (agentSession) {
      try {
        await agentSession.dispose();
      } catch {
        /* best-effort — mount unmount/connector disconnect can fail */
      }
    }
    try {
      // Remove materialized cloud credential files (e.g. the Vertex SA JSON)
      // so a torn-down session leaves no secret bytes in the workspace.
      cleanupCloudCredentialFiles(opts.projectDir);
    } catch {
      /* best-effort */
    }
  }

  // ---------------------------------------------------------------------------
  // Command handling
  // ---------------------------------------------------------------------------

  /**
   * The command envelope `handleCommand` accepts. Narrowed per-case below so
   * each extracted `handle<Command>` helper takes its own command variant.
   */
  type CommandEnvelope = AgentCommand | import("@skaile/workspaces/types").SessionInitCommand;

  /**
   * Await session readiness, emitting a fatal `error` event and returning
   * `false` when the session failed to build. Returning `true` means callers
   * may safely touch `driver` / `resourceManager` / `agentSession`. Shared by
   * every command handler that previously inlined the same try/await/catch.
   */
  async function awaitSessionReadyOrFail(): Promise<boolean> {
    try {
      await sessionReady;
      return true;
    } catch (err) {
      sendEvent({
        type: "error",
        message: `Session not available: ${(err as Error).message}`,
        fatal: true,
      });
      return false;
    }
  }

  type SessionInitCommand = import("@skaile/workspaces/types").SessionInitCommand;

  /**
   * Register the client-side capabilities carried in a `session_init`
   * envelope (was step 3 of the inline handler). Pure-runtime audience caps
   * (host.*) are filtered out — they are invoked outbound via
   * `capabilityRegistry.invokeRemote()`, not stored as LLM-callable stubs.
   */
  function registerInitClientCapabilities(
    incoming: NonNullable<SessionInitCommand["capabilities"]>,
  ): void {
    for (const wire of incoming) {
      // Audience-scoped runtime caps (host.*) are not registered as
      // client capabilities — they are invoked outbound via
      // capabilityRegistry.invokeRemote(). Filter them out here so the
      // registry doesn't store them as LLM-callable stubs.
      if (wire.audience && !wire.audience.includes("llm") && !wire.audience.includes("user")) {
        // Pure runtime audience — keep around in a separate map for
        // outbound invocation (handled by registry.setRemoteInvoker
        // wiring, not by `register`).
        continue;
      }
      const stub = {
        ...wire,
        inputZod: { parse: (v: unknown) => v } as unknown as import("zod").ZodTypeAny,
        outputZod: undefined,
        handler: buildClientCapabilityHandler(wire),
      };
      capabilityRegistry.register(stub, "client");
    }
  }

  /**
   * Provision the long-lived secrets carried in a `session_init` envelope
   * (was step 5). The Claude Code credentials JSON goes to disk; everything
   * else lands in an `InMemorySecretProvider` (assigned to `secretProvider`,
   * flipping `secretsProvisioned`). Returns the parsed AI-credential
   * `expiresAt` (or null) so the caller can arm the proactive refresh timer.
   */
  async function provisionInitSecrets(
    secrets: SessionInitCommand["secrets"],
  ): Promise<string | null> {
    let initialAiCredentialExpiresAt: string | null = null;
    if (secrets) {
      const ccdValue = secrets[CLAUDE_CODE_CREDENTIALS_KEY];
      if (ccdValue) {
        const home = process.env.HOME ?? homedir();
        if (home) {
          try {
            await writeClaudeCodeCredentialsFile(home, ccdValue);
            log(
              `[serve] wrote Claude Code credentials file to ${home}/.claude/.credentials.json (mode 0600)`,
            );
            // The credentials blob is what turns connector fetching on — the
            // guard lands in the same directory, right behind it.
            await guardClaudeAiConnectors(home, serverLog);
          } catch (err) {
            const errMsg = err instanceof Error ? err.message : String(err);
            log(`[serve] failed to write Claude Code credentials file: ${errMsg}`);
          }
        }
        // Parse the expiry so the proactive refresh timer can prevent
        // the next 401 instead of relying on the reactive retry path.
        // Scheduling happens once aiProviderConfigId is stashed below.
        initialAiCredentialExpiresAt = extractClaudeAiOauthExpiresAt(ccdValue);
      }
      const otherSecrets = Object.fromEntries(
        Object.entries(secrets).filter(([k]) => k !== CLAUDE_CODE_CREDENTIALS_KEY),
      );
      const provider = new InMemorySecretProvider();
      provider.provision(otherSecrets);
      secretProvider = provider;
      secretsProvisioned = true;
    }
    return initialAiCredentialExpiresAt;
  }

  /**
   * Build the agent session from a `session_init` envelope (was step 6).
   * Replaces the v2 provision_secrets + configure handshake: resolves the
   * provider API key from the envelope secrets, builds `sessionConfig`,
   * creates the session (only when one does not yet exist), and wires the
   * outbound capability invoker and the runner.* capabilities.
   *
   * This is the slow, connector/MCP-heavy part of init. The caller
   * (`handleSessionInit`) runs it in the background AFTER emitting
   * `session_init_ack`, and owns the session-ready gate — on success it
   * resolves `sessionReady` once the post-build configure has also run; on
   * failure it rejects the gate. This function therefore neither resolves nor
   * rejects the gate itself; it throws on error so the caller can reject.
   */
  async function buildSessionFromInit(
    secrets: SessionInitCommand["secrets"],
    fallbackConnectorDeclarations?: ConnectorDeclaration[],
  ): Promise<{ needsWireFallbackRecreate: boolean; needsWireAgentConfigRecreate: boolean }> {
    let apiKeyFromSecrets: string | undefined;
    let cloudSecrets: Record<string, string> | undefined;
    const { cloud: initCloud } = resolveEffectiveCloudForSession();
    if (!managedCodex && initCloud === "default") {
      const effectiveProvider = opts.provider ?? wireAgentConfig?.provider ?? "anthropic";
      const envKey = providerEnvKey(effectiveProvider);
      apiKeyFromSecrets = secrets?.[envKey];
    } else if (!managedCodex) {
      // Non-default clouds skip the single-key pull: their credentials arrive
      // as an env bundle picked out of the same secrets map (with the Vertex
      // SA JSON materialized to a 0600 file, never passed through raw).
      ({ cloudSecrets } = resolveCloudSecretBundle({
        cloud: initCloud,
        secrets,
        projectDir: opts.projectDir,
      }));
    }
    sessionConfig = buildSessionConfig(
      secretProvider,
      apiKeyFromSecrets,
      cloudSecrets,
      fallbackConnectorDeclarations,
    );
    let needsWireFallbackRecreate = false;
    const agentSessionExistedBeforeThisInit = !!agentSession;
    if (!agentSession) {
      if (managedHost && managedBootstrapClosed) throw new Error("CODEX_RUNTIME_UNSUPPORTED");
      agentSession = await createAgentSession(sessionConfig);
      if (managedHost && managedBootstrapClosed) {
        await agentSession.dispose();
        throw new Error("CODEX_RUNTIME_UNSUPPORTED");
      }
      emitSystemPromptComposed(agentSession, sendEvent);
      resourceManager = agentSession.resourceManager;
      driver = agentSession.driver;
      rebuildCompactionOrchestrator();
      registerSessionComposition(agentSession);

      // Snapshot the declared/cold-start MCP set before the first emit so those
      // servers report live even on claude-sdk (they're baked into the driver).
      captureBakedMcpServers();
      emitResourcesAvailable();
      agentSession.startWatching();

      // The fresh session was built from the wire-aware sessionConfig, so the
      // wire agentConfig (if any) is already applied — record it as applied.
      appliedWireAgentConfigKey = wireAgentConfig ? JSON.stringify(wireAgentConfig) : undefined;

      log("[serve] session built from session_init envelope");
    } else if ((fallbackConnectorDeclarations?.length ?? 0) > 0 && !agentSession.resourceManager) {
      // env-mode eager boot (no skaile.yaml, broker shape) built the session with
      // zero declarations — signal the caller's consolidated recreate site to
      // rebuild. See _devlog/notes/2026-07-17-env-mode-wire-fallback-recreate.md.
      log(
        `[serve] session_init: recreating eagerly-built session to apply ${fallbackConnectorDeclarations!.length} wire connector declaration(s) (no disk declarations)`,
      );
      needsWireFallbackRecreate = true;
    }

    // Eagerly-built (pre-init) sessions never saw the wire agentConfig; fold a
    // rebuild into the caller's consolidated recreate site. The applied-key
    // guard makes a reconnect-replayed identical envelope a no-op.
    const wireKey = wireAgentConfig ? JSON.stringify(wireAgentConfig) : undefined;
    let needsWireAgentConfigRecreate = false;
    if (agentSessionExistedBeforeThisInit && wireKey && wireKey !== appliedWireAgentConfigKey) {
      log(
        `[serve] session_init: recreating eagerly-built session to apply wire agentConfig (driver=${wireAgentConfig!.driver ?? "(default)"} model=${wireAgentConfig!.model ?? "(default)"})`,
      );
      needsWireAgentConfigRecreate = true;
      appliedWireAgentConfigKey = wireKey;
    }

    // Wire on EVERY session_init, not just the create branch — env-mode eager boot
    // arrives with agentSession already built and would otherwise get no invoker and
    // no runner.* caps. Why it is safe here: _devlog/notes/2026-07-17-env-mode-wire-fallback-recreate.md.
    wireSessionCapabilities({
      capabilityRegistry,
      remoteCapabilityInvoker,
      getResourceManager: () => resourceManager,
      getPreMintedSecrets: () => preMintedSecrets,
      emitResourcesAvailable,
      driverPicksUpToolsLive,
      getOrCreateMcpManager: () => agentSession?.getOrCreateExternalMcpManager() ?? null,
      getCompactionOrchestrator: () => compactionOrchestrator,
      getProjectDir: () => opts.projectDir,
      attachSkillInstance,
      registerLiveAttachedMcp,
      scheduleDriverRestartForToolChanges,
      log,
    });
    return { needsWireFallbackRecreate, needsWireAgentConfigRecreate };
  }

  /**
   * Dispose + recreate the agent session, repointing every reader at the new one.
   * The ONLY sanctioned way to recreate — open-coding dispose+create strands the
   * readers on a disconnected manager that reports zero resources forever. Not
   * exception-safe by design. Why: `_devlog/notes/2026-07-17-recreate-resource-manager-repoint.md`.
   */
  async function recreateAgentSession(overrides: Partial<AgentSessionConfig>): Promise<void> {
    // Register this recreate as the in-flight swap so concurrent prompts defer
    // to it (they bind to the fresh driver instead of one being disposed). The
    // deferred settles in `finally`, so a failed recreate cannot block prompts.
    let markSwapDone!: () => void;
    driverSwapGate.beginSwap(
      new Promise<void>((resolve) => {
        markSwapDone = resolve;
      }),
    );
    try {
      // Never kill a driver mid-prompt: wait for any live turn to settle first.
      // The staged change applies on the next turn — the tolerance the skill
      // restart path already documents.
      await driverSwapGate.awaitActiveTurn();
      await recreateAgentSessionInner(overrides);
    } finally {
      markSwapDone();
    }
  }

  async function recreateAgentSessionInner(overrides: Partial<AgentSessionConfig>): Promise<void> {
    // Pre-dispose: tells the session's own manager from a live lazily-created one.
    const disposedManager = agentSession.resourceManager;
    const disposeStartedAt = Date.now();
    await agentSession.dispose();
    if (managedHost && managedBootstrapClosed) throw new Error("CODEX_RUNTIME_UNSUPPORTED");
    const createStartedAt = Date.now();
    agentSession = await createAgentSession({ ...sessionConfig, ...overrides });
    if (managedHost && managedBootstrapClosed) {
      await agentSession.dispose();
      throw new Error("CODEX_RUNTIME_UNSUPPORTED");
    }
    logPhase("session_rebuild", disposeStartedAt, {
      disposeMs: createStartedAt - disposeStartedAt,
      createMs: Date.now() - createStartedAt,
      resuming: !!overrides.resumeSessionId,
    });
    emitSystemPromptComposed(agentSession, sendEvent);
    swapDriver();
    rebuildCompactionOrchestrator();
    // Re-bake: a server hot-added before this restart is part of the fresh driver,
    // so it self-heals to live on the next emit.
    captureBakedMcpServers();
    // The capability getters and readResource close over this var; without the
    // repoint they read the disposed manager.
    const repointed = pickRepointedManager(
      agentSession.resourceManager,
      resourceManager,
      disposedManager,
    );
    // Known gap: a live lazy sharedState manager displaced by the session's own.
    // Silent resource loss is what made the original bug expensive — say so.
    if (
      resourceManager !== null &&
      resourceManager !== disposedManager &&
      repointed !== resourceManager
    ) {
      log(
        "[serve] WARNING: recreate displaced a lazily-created sharedState resource manager — registered shared-state stores were dropped and must be re-registered",
      );
    }
    resourceManager = repointed;
    // Resume watching, then re-announce so the gateway learns the live
    // resources (it never re-subscribes on its own after a recreate).
    agentSession.startWatching();
    emitAvailability();
  }

  /**
   * Dispose + recreate the agent session during `session_init`, threading the
   * staged tier-1 resume hint (when present) into the new driver so the SDK
   * conversation resumes. Shared by the tier-1 resume and subagents-recreate
   * branches so a later branch cannot clobber the resume hint.
   */
  async function recreateSessionForInit(pendingResumeSessionId: string | undefined): Promise<void> {
    await recreateAgentSession({
      ...(aiProviderConfigId ? { aiProviderConfigId } : {}),
      // Conditional, unlike the skill path's unconditional pass: here an absent
      // hint must fall through to sessionConfig's, which the platform may have set.
      ...(pendingResumeSessionId ? { resumeSessionId: pendingResumeSessionId } : {}),
    });
  }

  /**
   * Register the `session_init` shared-state stores as hot-plug connectors
   * (was the `cfg.sharedState` block). Lazily creates a ConnectorManager when
   * none exists. Returns `false` when ConnectorManager creation failed and the
   * caller should return early (after the `configured` event was emitted);
   * `true` otherwise.
   */
  async function applyInitSharedState(
    sharedState: NonNullable<SessionInitCommand["sharedState"]>,
  ): Promise<boolean> {
    if (!ensureResourceManagerForSharedState()) {
      sendEvent({ type: "configured" });
      return false;
    }
    // Register each shared state store as a hot-plug connector and wire
    // its change callback to forward state_changed events to the client.
    let storesRegistered = 0;
    for (const stateConfig of sharedState) {
      if (await registerSharedStateStore(stateConfig)) storesRegistered++;
    }
    await rebuildMcpToolsForNewStores(storesRegistered);
    return true;
  }

  /**
   * Lazy-create a ConnectorManager for sharedState when one doesn't exist yet
   * (the session may have no connectors in skaile.yaml, but the platform still
   * needs to register shared state stores). Returns `false` on creation
   * failure; the caller emits `configured` and bails.
   */
  function ensureResourceManagerForSharedState(): boolean {
    if (resourceManager) return true;
    try {
      registerBuiltinConnectors();
      resourceManager = new ConnectorManager(opts.projectDir, {
        catalogEntries: getSessionCatalogEntries(agentSession),
        tokenMediator: connectorTokenMediator,
      });
      log("[serve] created ConnectorManager for sharedState (no connectors were declared)");
      return true;
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      log(`[serve] failed to create ConnectorManager: ${errMsg}`);
      return false;
    }
  }

  /**
   * Register one shared-state store as a hot-plug connector and wire its change
   * callback to forward state_changed events. Returns `true` when a NEW store
   * was registered (so the caller can count it toward the MCP-tools rebuild);
   * `false` when the store already existed or registration failed.
   */
  async function registerSharedStateStore(
    stateConfig: NonNullable<SessionInitCommand["sharedState"]>[number],
  ): Promise<boolean> {
    try {
      if (resourceManager.has(stateConfig.id)) {
        // Store already exists (e.g. backend reconnected after restart).
        // Re-emit current state so the backend's stateCache is repopulated.
        try {
          const snapshot = await resourceManager.executeOp(stateConfig.id, "get", {});
          const state = snapshot ? JSON.parse(snapshot) : {};
          sendEvent({
            type: "state_changed",
            store: stateConfig.id,
            state,
          });
          log(
            `[serve] sharedState ${stateConfig.id} already registered — re-emitted current state`,
          );
        } catch {
          log(
            `[serve] sharedState ${stateConfig.id} already registered — skipping (could not read state)`,
          );
        }
        return false;
      }
      const handle = await resourceManager.connect({
        id: stateConfig.id,
        driver: stateConfig.adapter,
        access: "read-write" as const,
        options: {
          ...((stateConfig.definition as Record<string, unknown> | undefined) ?? {}),
          initial: stateConfig.initialState ?? {},
        },
      });
      // Wire onStoreChange (xstate-store specific) to emit state_changed events
      const entry = resourceManager.get(stateConfig.id);
      if (typeof entry.connector.onStoreChange === "function") {
        entry.connector.onStoreChange(handle, (snapshot: Record<string, unknown>) => {
          sendEvent({
            type: "state_changed",
            store: stateConfig.id,
            state: snapshot,
          });
        });
      }
      log(`[serve] sharedState registered: ${stateConfig.id} (${stateConfig.adapter})`);
      // Emit initial state immediately so subscribers don't have to wait for the first mutation.
      sendEvent({
        type: "state_changed",
        store: stateConfig.id,
        state: stateConfig.initialState ?? {},
      });
      return true;
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      log(`[serve] sharedState registration failed for ${stateConfig.id}: ${errMsg}`);
      sendEvent({
        type: "error",
        message: `sharedState ${stateConfig.id}: ${errMsg}`,
        fatal: false,
      });
      return false;
    }
  }

  /**
   * Rebuild MCP connector tools so the agent can access newly registered
   * stores. This must happen before the driver starts (lazy start on first
   * prompt). No-op when no new stores were added, the driver already started,
   * or the driver is not claude-sdk.
   */
  async function rebuildMcpToolsForNewStores(storesRegistered: number): Promise<void> {
    if (!(storesRegistered > 0 && !driverStarted && agentSession?.driverType === "claude-sdk")) {
      return;
    }
    try {
      const server = await buildSdkConnectorTools(resourceManager, { z: _z });
      if (server) {
        (driver as any).config.mcpServers = { "skaile-connectors": server };
        log(`[serve] rebuilt MCP connector tools (${storesRegistered} new store(s))`);
      } else {
        log(`[serve] buildSdkConnectorTools returned null`);
      }
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      log(`[serve] failed to rebuild MCP tools: ${errMsg}`);
    }
  }

  /**
   * Apply mid-session skill catalog changes from a `session_init` envelope
   * (was the `cfg.aiResources` block). Deploys / removes catalog entries on
   * disk, then restarts the driver (with conversation resume) so the SDK
   * re-discovers `.claude/skills/` and emits `skills_changed`.
   */
  async function applyInitAiResources(
    aiResources: NonNullable<SessionInitCommand["resolvedConfig"]["aiResources"]>,
  ): Promise<void> {
    const catalogActions = aiResources.filter((a) => a.action);
    if (catalogActions.length === 0) return;
    log(`[serve] aiResources catalog actions: ${catalogActions.length}`);
    try {
      const skillsDir = join(sessionConfig.projectDir ?? process.cwd(), ".claude", "skills");

      // Resolve ai-assets root from runner's own environment
      const assetsRoot =
        process.env.SKAILE_ASSETS_ROOT ??
        (await findAiAssetsRoot(sessionConfig.projectDir ?? process.cwd())) ??
        "/app/ai-assets";

      // Serial: each action is a recursive copy, and running them concurrently
      // would only deepen the queue on a mount that may already be struggling.
      for (const res of catalogActions) {
        await applyCatalogAction(res, { assetsRoot, skillsDir });
      }

      await restartDriverForSkillChanges(catalogActions);
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      log(`[serve] aiResources handling failed: ${errMsg}`);
      sendEvent({
        type: "error",
        message: `aiResources: ${errMsg}`,
        fatal: false,
      });
    }
  }

  /**
   * Deploy or remove a single catalog entry on disk (one `aiResources` action
   * from a `session_init` envelope). Mirrors the deploy/remove branches of the
   * former inline loop; logs the outcome.
   */
  async function applyCatalogAction(
    res: NonNullable<SessionInitCommand["resolvedConfig"]["aiResources"]>[number],
    paths: { assetsRoot: string; skillsDir: string },
  ): Promise<void> {
    if (res.action === "add" && res.entry) {
      const entry = res.entry as Record<string, unknown>;
      const result = await deployCatalogEntry(entry as any, {
        assetsRoot: paths.assetsRoot,
        skillsDir: paths.skillsDir,
        context: res.context,
      });
      if (result.success) {
        log(`[serve] deployed skill: ${(entry.name as string) ?? res.name}`);
      } else {
        log(
          `[serve] failed to deploy skill ${(entry.name as string) ?? res.name}: ${result.error}`,
        );
      }
    } else if (res.action === "remove") {
      const removed = await undeployCatalogEntry(res.name, paths.skillsDir);
      log(`[serve] ${removed ? "removed" : "not found"} skill: ${res.name}`);
    }
  }

  /**
   * Live-attach a single SKILL asset enabled mid-session. The platform has
   * already materialized it to `<projectDir>/.skaile/assets/skill/<name>/`; we
   * stage that one skill into `.claude/skills/` (the same path cold boot uses)
   * and fast-restart the driver so the SDK rediscovers it. Mirrors the
   * mcp-server / connector live-attach branches. Best-effort — never throws;
   * `skill_attach_failed` covers both the path-resolve and staging phases.
   */
  async function attachSkillInstance(
    name: string,
  ): Promise<
    | { ok: true; id: string; live: boolean; toolCount: number }
    | { ok: false; code: string; message: string }
  > {
    try {
      const { skillsDir } = resolveDriverPaths({
        driver:
          opts.driver ??
          wsConfig.agent_config?.default?.driver ??
          wireAgentConfig?.driver ??
          "claude-sdk",
      });
      // `declared: [name]` is an explicit single-skill allowlist — a principled
      // per-attach bypass of the skaile.yaml-derived strict gate (the staging
      // filter IS the gate; survives strict mode). Rationale: PR #170 thread.
      const staged = await stageMaterializedSkills(opts.projectDir, skillsDir, {
        declared: [name],
      });
      if (staged.length === 0) {
        return {
          ok: false,
          code: "skill_not_materialized",
          message: `no materialized skill matching "${name}" under .skaile/assets/skill`,
        };
      }
      log(`[serve] runner.attach_instance staged skill(s): ${staged.join(", ")}`);
      // Capture before the restart: when the driver hasn't started yet the
      // restart is a no-op and the skill is discovered on the first prompt
      // (not live yet), so `live` must reflect the pre-restart driver state.
      const live = driverStarted;
      // Scheduled, not awaited — awaiting blew the 30s capability cap once per
      // wake-delivered skill. Rationale on the helper.
      scheduleDriverRestartForSkillChanges(
        staged.map((s) => ({ name: s, action: "add" as const })),
      );
      // toolCount: skills are system-prompt fragments, not tool-bearing servers.
      return { ok: true, id: name, live, toolCount: 0 };
    } catch (err) {
      return {
        ok: false,
        code: "skill_attach_failed",
        message: err instanceof Error ? err.message : String(err),
      };
    }
  }

  /**
   * Restart the driver so it re-bakes its tool set, resuming with conversation
   * history. Covers both surfaces the claude-sdk driver fixes at creation:
   * skills discovered from `.claude/skills/` and the composed `mcpServers`.
   * No-op (logs only) when the driver has not started yet — the fresh state is
   * picked up on the first prompt.
   */
  async function restartDriverForToolChanges(reason: string): Promise<void> {
    if (!driverStarted) {
      log(`[serve] ${reason} applied before driver start — picked up on the first prompt`);
      return;
    }
    // Capture the current session ID so we can resume with conversation history.
    const prevSessionId = driver.runtimeSessionId;
    log(`[serve] restarting driver to pick up ${reason} (resume=${prevSessionId ?? "none"})`);
    // Unconditional, unlike recreateSessionForInit: no live conversation means
    // clear sessionConfig's stale hint, not resume the wrong one.
    await recreateAgentSession({ resumeSessionId: prevSessionId });
  }

  /** Queue a restart behind any already-queued one and await this one's turn. */
  function enqueueDriverRestart(reason: string): Promise<void> {
    const next = driverRestartChain.then(() => restartDriverForToolChanges(reason));
    // Swallow only on the tail so one failure doesn't poison the queue; the
    // returned promise still rejects for the caller that asked for this restart.
    driverRestartChain = next.catch(() => {});
    return next;
  }

  /**
   * Queue a restart without waiting for it. The platform caps a runner
   * capability call at 30s and a rebuild routinely exceeds that; `beginSwap`
   * arms synchronously inside `recreateAgentSession`, so the next turn already
   * defers to the swap and the caller can honestly report the tools as live.
   */
  function scheduleDriverRestartForToolChanges(reason: string): void {
    void enqueueDriverRestart(reason).catch((err) => {
      log(
        `[serve] background driver restart for ${reason} failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    });
  }

  /**
   * Restart the driver for skill catalog changes, then emit `skills_changed`.
   * Accepts the minimal `{ name, action }` shape so both the `session_init`
   * aiResources path and the live `runner.attach_instance` skill path can drive it.
   */
  async function restartDriverForSkillChanges(
    catalogActions: ReadonlyArray<{ name: string; action?: "add" | "remove"; entry?: unknown }>,
  ): Promise<void> {
    // Nothing observed the old skill set before the driver started, so a
    // pre-start change is not an announcement — keep the emit behind the restart.
    const restarted = driverStarted;
    await enqueueDriverRestart("skill changes");
    if (!restarted) return;

    sendEvent({
      type: "skills_changed",
      skills: catalogActions.map((a) => ({
        name: ((a.entry as Record<string, unknown>)?.name as string) ?? a.name,
        action: a.action ?? "add",
      })),
    });
  }

  /**
   * Queue a skill-catalog restart without waiting for it — the scheduling
   * sibling of {@link scheduleDriverRestartForToolChanges}, with the same
   * rationale: the platform caps a runner capability call at 30s, a rebuild
   * routinely exceeds that, and `beginSwap` arms synchronously inside
   * `recreateAgentSession` so the next turn already defers to the swap. The
   * `skills_changed` announcement still rides the restart.
   */
  function scheduleDriverRestartForSkillChanges(
    catalogActions: ReadonlyArray<{ name: string; action?: "add" | "remove"; entry?: unknown }>,
  ): void {
    void restartDriverForSkillChanges(catalogActions).catch((err) => {
      // `warn`, not the info-level `log()` alias: the caller already got
      // `ok: true` and `skills_changed` is skipped on rejection, so this line
      // is the only trace — and prod filters info.
      serverLog.warn(
        `[serve] background driver restart for skill changes failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    });
  }

  let runtimeRejected = false;

  /** Protocol v3 deterministic boot envelope handler (was the `session_init` case). */
  async function handleSessionInit(
    cmd: Extract<CommandEnvelope, { type: "session_init" }>,
  ): Promise<void> {
    // Protocol v3 deterministic boot envelope. Replaces the v2
    // configure + provision_secrets + initial request_access_token round
    // trips with one upfront message carrying identity, resolved config,
    // pre-minted credentials, secrets, capabilities, shared state,
    // active flows, resume hints, and logging config.
    //
    // Spec: _devlog/specs/2026-05-10-deterministic-session-bootstrap.md.

    const initReceivedAt = Date.now();
    // A new init supersedes the recorded acknowledgement; the replay reflects
    // the new one only once its ack is emitted.
    const thisInit = ++initGeneration;
    initAcknowledged = false;

    // 1. Protocol major-equality
    if (cmd.protocolVersion.major !== PROTOCOL_VERSION.major) {
      log(
        `[serve] incompatible protocol: agent=${PROTOCOL_VERSION.major} platform=${cmd.protocolVersion.major}`,
      );
      sendEvent({
        type: "incompatible_protocol",
        agentVersion: PROTOCOL_VERSION,
        platformVersion: cmd.protocolVersion,
      });
      await cleanup();
      teardownProcessHandlers();
      disposeLogStore();
      return;
    }

    // The client contract alone does not establish adapter/executable readiness.
    try {
      validateCodexInit(cmd, supportedRuntimeFeatures, runtimeIdentity ?? undefined);
      const wantsManaged = cmd.resolvedConfig.agentConfig?.driver === "codex";
      if (managedHost && !wantsManaged) throw new Error("AI_PROFILE_INVALID");
      if (managedHost && wantsManaged) {
        if (
          managedBootstrapClosed ||
          (agentSession && !managedInitFingerprint) ||
          (cmd.secrets && CLAUDE_CODE_CREDENTIALS_KEY in cmd.secrets)
        )
          throw new Error("AI_PROFILE_INVALID");
        const profile = cmd.resolvedConfig.agentConfig!;
        if (
          (opts.driver && opts.driver !== "codex") ||
          (opts.provider && opts.provider !== "openai") ||
          (opts.model && opts.model !== profile.model)
        )
          throw new Error("AI_PROFILE_INVALID");
        const initialCredential = parseCodexCredential(cmd.credentials.ai);
        const binding = bindManagedCodexInit(managedHost, cmd, async (input, signal) => {
          signal.throwIfAborted();
          if (initialCredential.kind !== "codex-subscription")
            throw new Error("AI_PROFILE_INVALID");
          const pinned = { ...initialCredential, credentialVersion: input.credentialVersion };
          const result = await remoteCapabilityInvoker(
            "host.refresh_credential",
            parseCodexRefreshInput(input, pinned),
            undefined,
            signal,
          );
          signal.throwIfAborted();
          return parseCodexRefreshResult(result, pinned);
        });
        const credential = binding.runtime.credential;
        const fingerprint = JSON.stringify([
          cmd.identity,
          // A repeated init is idempotent only for the same host capability contract.
          // Keep all policy fields, not only the narrower resume signature.
          cmd.capabilities ?? [],
          profile,
          credential.configId,
          credential.kind,
          credential.kind === "codex-subscription" ? credential.chatgptAccountId : null,
        ]);
        if (managedInitFingerprint) {
          if (managedInitFingerprint !== fingerprint) throw new Error("AI_PROFILE_INVALID");
          if (managedInitComplete)
            emitInitAck({
              type: "session_init_ack",
              capabilitySignature: computeCapabilitySignature(capabilityRegistry.list()),
            });
          return;
        }
        managedInitFingerprint = fingerprint;
        managedCodex = binding;
      }
    } catch {
      runtimeRejected = true;
      sendEvent({
        type: "error",
        fatal: true,
        category: "config",
        message: "Unsupported runtime: managed Codex initialization is unavailable or invalid",
      });
      await cleanup();
      teardownProcessHandlers();
      disposeLogStore();
      await transport.close();
      return;
    }

    // 2. Identity. Future successive session_init calls must match this
    // containerId; mismatch shuts the runner down.
    if (runtimeIdentity && runtimeIdentity.containerId !== cmd.identity.containerId) {
      log(
        `[serve] session_init_mismatch: expected=${runtimeIdentity.containerId} received=${cmd.identity.containerId}`,
      );
      sendEvent({
        type: "session_init_mismatch",
        expectedContainerId: runtimeIdentity.containerId,
        receivedContainerId: cmd.identity.containerId,
      });
      await cleanup();
      teardownProcessHandlers();
      disposeLogStore();
      return;
    }
    runtimeIdentity = cmd.identity;

    // 3. Register client-side capabilities from the envelope.
    registerInitClientCapabilities(cmd.capabilities ?? []);
    sendEvent({
      type: "protocol_info",
      version: PROTOCOL_VERSION,
      ...(managedHost ? { runtimeFeatures: supportedRuntimeFeatures } : {}),
    });

    // 4. Pre-minted credentials → in-process PreMintedSecretProvider.
    preMintedSecrets = new PreMintedSecretProvider(cmd.credentials);

    // 5. Long-lived secrets → in-memory secret provider (replaces
    // provision_secrets). The Claude Code credentials JSON is handled
    // separately — it goes to disk, not the secret provider.
    const initialAiCredentialExpiresAt = await provisionInitSecrets(cmd.secrets);
    if (managedCodex) secretsProvisioned = true;

    // 5a. Stash aiProviderConfigId for the auth-error retry handler. Cheap;
    // kept on the fast path (before the ack) so the refresh timer is armed
    // even while the heavy build is still in flight.
    if (cmd.resolvedConfig.aiProviderConfigId) {
      aiProviderConfigId = cmd.resolvedConfig.aiProviderConfigId;
      log(`[serve] stashed aiProviderConfigId=${aiProviderConfigId}`);
    }

    // Disk-first gate: any on-disk agent-config profile disables the wire value.
    // An envelope that omits agentConfig keeps the previously applied value —
    // otherwise a later init would silently rebuild sessionConfig on boot
    // defaults and a mid-session recreate would flip the live driver.
    wireAgentConfig = managedCodex
      ? cmd.resolvedConfig.agentConfig
      : diskProfileHasAgentSettings(profile)
        ? undefined
        : (cmd.resolvedConfig.agentConfig ?? wireAgentConfig);

    // 5b. Arm the proactive AI-credential refresh timer. Needs
    // aiProviderConfigId in scope (set above) AND an `expiresAt`
    // parsed from the initial credentials blob (step 5). Static PATs
    // and standalone sessions short-circuit to no-op inside the
    // scheduler.
    if (!managedCodex && aiProviderConfigId && initialAiCredentialExpiresAt) {
      aiCredentialRefreshScheduler.schedule(initialAiCredentialExpiresAt);
    }

    // 6. Ack before the slow connector/MCP bring-up (step 7) so a cold container can't
    // blow the platform's session_init_ack budget; the deferred build resolves the
    // `sessionReady` gate every session-dependent command awaits. Ack signature is informational.
    // On a WAKE with a requested tier-1 resume, stamp a proactive transcript-existence probe so
    // the platform can synchronously downgrade a `jsonl_lost` resume; the reactive
    // `resume_failed{jsonl_lost}` path stays the safety net. Container-scoped CLAUDE_CONFIG_DIR.
    const resumeProbe =
      !managedCodex && cmd.resume?.sessionId
        ? await probeResumeTranscript(cmd.resume.sessionId)
        : undefined;
    if (!managedCodex)
      emitInitAck({
        type: "session_init_ack",
        capabilitySignature: computeCapabilitySignature(capabilityRegistry.list()),
        ...(resumeProbe ? { resumeProbe } : {}),
      });

    // 7. Kick off the heavy build + post-build configure in the background.
    // Success resolves `sessionReady`; any failure surfaces a fatal `error`
    // event and rejects `sessionReady` so awaiting commands fail fast instead
    // of wedging.
    const buildSession = async (): Promise<void> => {
      // Build session using the resolved config (replaces the v2 handshake). The
      // wire declarations are a disk-first fallback for no-yaml workspaces; the
      // returned flag folds an env-mode rebuild into the single recreate site below.
      const firstBuildStartedAt = Date.now();
      const { needsWireFallbackRecreate, needsWireAgentConfigRecreate } =
        await buildSessionFromInit(cmd.secrets, resolvedConfigToDeclarations(cmd.resolvedConfig));
      // Always a no-resume build: the hint reaches the session only via the
      // recreate below, because the honor/reject signature needs this build's
      // wired capability registry.
      logPhase("session_build", firstBuildStartedAt, {
        resumeRequested: !!cmd.resume?.sessionId,
      });

      // 8. Adapt the rest of the configure logic onto envelope fields.
      const cfg = {
        aiResources: cmd.resolvedConfig.aiResources,
        subagents: cmd.resolvedConfig.subagents,
        sharedState: cmd.sharedState,
        resumeSessionId: cmd.resume?.sessionId,
        expectedCapabilitySignature: cmd.resume?.expectedCapabilitySignature,
        aiProviderConfigId: cmd.resolvedConfig.aiProviderConfigId,
      };
      log(
        `[serve] configure cfg keys=${Object.keys(cfg).join(",")} sharedState=${cfg.sharedState ? cfg.sharedState.length : "undefined"} resourceManager=${resourceManager ? "set" : "null"}`,
      );

      // `aiProviderConfigId` was already stashed in step 7 above. The legacy
      // configure-handler block re-stashed `cfg.aiProviderConfigId` here for
      // the standalone configure path; on session_init the field is sourced
      // from the same `cmd.resolvedConfig.aiProviderConfigId` so the second
      // assignment was redundant and has been removed.
      //
      // Spec: `_devlog/specs/2026-05-07-unified-credential-mediation.md`
      // § "Wire `aiProviderConfigId` end-to-end".

      // Configure/wake re-arm. The fast-path armed the proactive timer at step
      // 5b; this revives it if a prior fire died (terminal failure / exhausted
      // retry cap) or went stale, so a re-connecting (or never-hibernated)
      // container does not arm exactly once and then stop refreshing. Idempotent
      // and a no-op for static PATs / a healthy pending timer.
      if (!managedCodex && aiProviderConfigId) {
        aiCredentialRefreshScheduler.ensureArmed();
      }

      // ── Tier-1 native SDK resume (Phase 3 of the resume cascade) ──────
      //
      // The platform sets `cfg.resumeSessionId` (and optionally
      // `cfg.expectedCapabilitySignature`) when wake-time tier selection
      // chose tier 1. We:
      //   1. Compute the current registry signature post-cap-registration.
      //   2. If a signature was supplied and it doesn't match — drop the
      //      resume hint and emit `resume_failed { signature_mismatch }`.
      //   3. Otherwise, dispose + recreate the driver with the supplied
      //      session id so the first prompt resumes the SDK conversation.
      //
      // Spec: `_devlog/specs/2026-05-05-session-resume-restart-design.md`
      // The resume hint is staged here and merged into any subsequent
      // driver recreate within this configure call. Without staging, a
      // later branch (e.g. `cfg.subagents`) would clobber the resume by
      // recreating the driver from `sessionConfig` without the hint.
      let pendingResumeSessionId: string | undefined;
      const cfgResumeSessionId = cfg.resumeSessionId;
      const cfgExpectedSig = cfg.expectedCapabilitySignature;
      if (cfgResumeSessionId) {
        const currentSig = computeCapabilitySignature(capabilityRegistry.list());
        if (cfgExpectedSig && cfgExpectedSig !== currentSig) {
          log(
            `[serve] tier-1 resume rejected: signature mismatch (expected=${cfgExpectedSig.slice(0, 12)} current=${currentSig.slice(0, 12)})`,
          );
          sendEvent({
            type: "resume_failed",
            resumeSessionId: cfgResumeSessionId,
            reason: "signature_mismatch",
          });
        } else {
          log(`[serve] tier-1 resume attempted: resumeSessionId=${cfgResumeSessionId}`);
          sendEvent({
            type: "resume_attempted",
            resumeSessionId: cfgResumeSessionId,
            capabilitySignature: currentSig,
          });
          pendingResumeSessionId = cfgResumeSessionId;
        }
      }

      // Tier-1 resume, subagents, and the env-mode wire-declaration fallback share
      // ONE dispose + recreate — all three are already folded into `sessionConfig`.
      // Caveats: _devlog/notes/2026-07-17-env-mode-wire-fallback-recreate.md.
      if (
        pendingResumeSessionId ||
        cfg.subagents ||
        needsWireFallbackRecreate ||
        needsWireAgentConfigRecreate
      ) {
        // Logged once per ACTUAL recreate — the per-cause announcements above
        // can fire several times for the same consolidated rebuild.
        log("[serve] session_init: consolidated session recreate");
        const recreateStartedAt = Date.now();
        await recreateSessionForInit(pendingResumeSessionId);
        logPhase("session_recreate", recreateStartedAt, {
          resuming: !!pendingResumeSessionId,
        });
      }
      if (cfg.sharedState) {
        const handled = await applyInitSharedState(cfg.sharedState);
        // Runs inside the background build closure, so a bare `return` would leave
        // `sessionReady` unresolved forever — throw so the catch below rejects the
        // gate and emits a fatal error.
        if (!handled) throw new Error("session_init: shared-state setup failed");
      }

      // No envelope-level flow rehydration: `session_init` never carries active
      // flows. A host restores a persisted run after init with a single
      // `connector_mutate { id: "flow", op: "hydrate", payload: { state, flow } }`,
      // which auto-creates the connector on this cold container (Protocol 3.5).
      // See `MIGRATION-flow-connector.md`.

      // Handle aiResources (mid-session skill changes via catalog)
      if (cfg.aiResources) {
        await applyInitAiResources(cfg.aiResources);
      }

      // Build complete (including the slow connector/MCP bring-up and the
      // post-build configure). Release the readiness gate so queued prompts
      // and resource ops proceed against a fully-built session.
      logPhase("session_ready", initReceivedAt, {
        ready: true,
        resumeRequested: !!cmd.resume?.sessionId,
      });
      if (managedCodex) {
        if (managedBootstrapClosed) throw new Error("CODEX_RUNTIME_UNSUPPORTED");
        try {
          await ensureDriverStarted();
        } catch (error) {
          if (
            !(error instanceof CodexNativeStateUnavailable) ||
            !pendingResumeSessionId ||
            managedBootstrapClosed
          )
            throw error;
          // The failed start joins native shutdown and releases its lease. Session
          // disposal below also joins every resource before one same-binding retry.
          await recreateAgentSession({ resumeSessionId: undefined });
          if (cfg.sharedState && !(await applyInitSharedState(cfg.sharedState)))
            throw new Error("session_init: shared-state setup failed");
          if (cfg.aiResources) await applyInitAiResources(cfg.aiResources);
          await ensureDriverStarted();
          if (managedBootstrapClosed || !driverStarted)
            throw new Error("CODEX_RUNTIME_UNSUPPORTED");
          sendEvent({
            type: "resume_failed",
            resumeSessionId: pendingResumeSessionId,
            reason: "native_state_unavailable",
          });
        }
        if (managedBootstrapClosed || !driverStarted) throw new Error("CODEX_RUNTIME_UNSUPPORTED");
        managedInitComplete = true;
        emitInitAck({
          type: "session_init_ack",
          capabilitySignature: computeCapabilitySignature(capabilityRegistry.list()),
        });
      }
      resolveSessionReady();
    };

    void buildSession().catch((err: unknown) => {
      const errMsg = err instanceof Error ? err.message : String(err);
      // A runner whose session never built must not replay "initialized": the
      // pre-build ack already went out, and only a fresh wake can recover. A
      // build a later init has superseded owns no acknowledgement to clear.
      if (thisInit === initGeneration) initAcknowledged = false;
      // The producing half of the failing-path rule: a build that burns minutes
      // and then throws must report that wall-clock even when no prompt queued
      // behind it to report `ready: false` on its own.
      logPhase("session_ready", initReceivedAt, { ready: false });
      log(`[serve] session_init failed to build session: ${errMsg}`);
      sendEvent({ type: "error", message: errMsg, fatal: true });
      rejectSessionReady(err instanceof Error ? err : new Error(errMsg));
      if (managedHost) {
        runtimeRejected = true;
        void cleanup().finally(async () => {
          teardownProcessHandlers();
          disposeLogStore();
          await transport.close();
        });
      }
    });
  }

  const awaitCompactionIdle = (what: string): Promise<boolean> =>
    guardTurnAgainstCompaction({ gate: compactionGate, what, log, sendEvent });

  /** Free-chat prompt handler (was the `prompt` case). Lazy-starts the driver. */
  async function handlePrompt(cmd: Extract<CommandEnvelope, { type: "prompt" }>): Promise<void> {
    // Receipt-ack FIRST — every gate below can drop the prompt (the readiness
    // gate silently), and a host with no ack cannot tell "dropped" from
    // "still thinking". Says nothing about the turn running; see PromptAckEvent.
    if (cmd.messageId) sendEvent({ type: "prompt_ack", messageId: cmd.messageId });
    // A prompt queued during a wake parks here for the whole build; this is the
    // delay the user sees as a stalled turn.
    const promptReceivedAt = Date.now();
    const sessionIsReady = await awaitSessionReadyOrFail();
    // Reported on both outcomes: a build that hangs and then rejects is the
    // longest stall there is, and it must not be the one case with no timing.
    logPhase("prompt_gate_wait", promptReceivedAt, { ready: sessionIsReady });
    if (!sessionIsReady) return;
    if (!secretsProvisioned) {
      log("[serve] prompt received before secrets provisioned — rejecting");
      sendEvent({
        type: "error",
        message: "Secrets not yet provisioned — send provision_secrets first",
        fatal: false,
      });
      return;
    }
    log(`[serve] prompt: ${cmd.prompt.slice(0, 80)}...`);

    // Bind to the fresh driver: if a swap (skill/flow restart) is mid-flight,
    // wait it out before reading `driverStarted`/`driver` below.
    await driverSwapGate.awaitSwap();

    // Same idea for the other occupant of this driver: a compaction turn. Held
    // here rather than at the dispatch below so the flow path is covered too,
    // and so the `compacting` phase stays on screen instead of being overwritten
    // with `thinking` for a turn that is not running yet.
    if (!(await awaitCompactionIdle("prompt"))) return;

    // Pick up any declared skill the platform materialized after boot (a
    // freshly-provisioned session whose first materialize landed late) and
    // restart the driver for newly-appeared skills before this turn runs.
    await restageMaterializedSkills(true);

    // Route free-chat user messages through the SessionStimulusBus
    // when a flow is active — every turn then includes the flow
    // context, and the agent decides whether to advance the flow
    // or just answer the user. The original prompt path below
    // runs only when no flow is active. Sender attribution comes
    // from the envelope layer in hosts that track it; the bare
    // prompt command carries none today so we use a placeholder.
    const activeFlow = getOnlyActiveFlow();
    if (activeFlow) {
      sendEvent({ type: "status", phase: "thinking" });
      try {
        const bus = getOrCreateStimulusBus();
        const userStimulus: TurnStimulus = {
          kind: "user_message",
          text: cmd.prompt,
          senderId: "user",
        };
        await bus.signal(activeFlow.connectorId, {
          promptFragment: renderStimulusPrompt(userStimulus),
          meta: {
            kind: "user_message",
            runId: activeFlow.runId,
            text: cmd.prompt,
            senderId: "user",
          },
        });
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        log(`[serve] flow user-message turn failed: ${errMsg}`);
        sendEvent({ type: "error", message: `Flow turn failed: ${errMsg}`, fatal: false });
      } finally {
        // driveTurn can decline the stimulus (a finished run still lingers in
        // activeFlows) and then nothing closes the `thinking` above; after a
        // real turn the driver's agent_end already went idle, so this is a no-op.
        sendEvent({ type: "status", phase: "idle" });
      }
      return;
    }

    // Shared with the flow path, and deliberately not an inline `if
    // (!driverStarted)` here: prompts reach this line concurrently, so the
    // start and the listener wiring have to be memoized rather than guarded.
    const driverStartStartedAt = Date.now();
    let driverStartOk = false;
    try {
      await ensureDriverStarted();
      driverStartOk = true;
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      log(`[serve] driver start failed: ${errMsg}`);
      sendEvent({ type: "error", message: `Driver start failed: ${errMsg}`, fatal: true });
      return;
    } finally {
      // In `finally` so a start that burns time and then throws still reports it.
      logPhase("driver_start", driverStartStartedAt, { ok: driverStartOk });
    }

    sendEvent({ type: "status", phase: "thinking" });

    // Mark the turn only at the real dispatch so a swap defers to it. Set earlier
    // (e.g. before restaging skills) it could self-deadlock a same-handler restart.
    const endTurn = driverSwapGate.beginTurn();
    try {
      await driver.promptInput({ text: cmd.prompt, attachments: cmd.attachments });
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      log(`[serve] prompt failed: ${errMsg}`);
      sendEvent({ type: "error", message: `Prompt failed: ${errMsg}`, fatal: true });
    } finally {
      endTurn();
    }
  }

  /** Reply-to-question handler (was the `reply` case). */
  async function handleReply(cmd: Extract<CommandEnvelope, { type: "reply" }>): Promise<void> {
    if (!(await awaitSessionReadyOrFail())) return;
    log(`[serve] reply: ${cmd.answer.slice(0, 80)}...`);

    // When the driver is paused on an interactive question (e.g. the Claude SDK
    // AskUserQuestion tool), deliver the answer into that paused turn instead of
    // starting a new prompt. Routing it as a fresh prompt is what made the
    // onboarding assistant re-ask the same question: the SDK never received the
    // structured answer, so the question stayed unanswered. answerQuestion
    // resumes the in-flight turn (it does not await a new one), so we return.
    //
    // `routeReply` answers BEFORE awaiting the swap gate and only gates the
    // fall-through; see its JSDoc for why inverting that wedges the session.
    const routed = await routeReply({
      getDriver: () => driver,
      awaitSwap: () => driverSwapGate.awaitSwap(),
      answer: cmd.answer,
      question: cmd.question,
      requestId: cmd.requestId,
    });
    if (routed === "answered") return;
    if (!driver.acceptsReplyAsPrompt) return;

    // Past the paused-question fast path this is a fresh turn on the shared
    // driver, so it races a compaction exactly as a prompt does. Answering a
    // genuinely paused question stays ahead of the gate: that turn predates any
    // compaction, which only fires once a turn has finished.
    if (!(await awaitCompactionIdle("reply"))) return;

    const endTurn = driverSwapGate.beginTurn();
    try {
      await driver.prompt(cmd.answer);
    } catch (err) {
      sendEvent({
        type: "error",
        message: `Reply failed: ${err instanceof Error ? err.message : String(err)}`,
        fatal: false,
      });
    } finally {
      endTurn();
    }
  }

  /** Cancel/abort handler (was the `cancel` case). */
  async function handleCancel(): Promise<void> {
    if (!(await awaitSessionReadyOrFail())) return;
    log("[serve] cancel");
    try {
      await driver.abort();
    } catch (err) {
      log(`[serve] cancel failed: ${err}`);
    }
  }

  /** Resource request handler (was the `resource_request` case). */
  async function handleResourceRequestCommand(
    cmd: Extract<CommandEnvelope, { type: "resource_request" }>,
  ): Promise<void> {
    if (!(await awaitSessionReadyOrFail())) return;
    // Route to mounts handler if the ID matches a mounted mount
    if (resourceManager?.has(cmd.connectorId)) {
      await handleMountResourceRequest(cmd, resourceManager, sendEvent);
      return;
    }
    if (!resourceManager) {
      sendEvent({
        type: "resource_response",
        requestId: cmd.requestId,
        resourceId: cmd.connectorId,
        operation: cmd.operation,
        error: "No resources available — no mounts or connectors configured",
      });
      return;
    }
    void handleResourceRequest(cmd, resourceManager, sendEvent);
  }

  /**
   * Generic connector mutation dispatch (the `connector_mutate` case).
   *
   * The body lives in the exported {@link executeConnectorMutate} so the
   * acknowledgement contract — including the paths that used to be swallowed
   * into a warn — is testable without booting a server.
   */
  async function handleConnectorMutate(
    cmd: Extract<CommandEnvelope, { type: "connector_mutate" }>,
  ): Promise<void> {
    await executeConnectorMutate(cmd, {
      sessionReady,
      sendEvent,
      assertResourcesReady: () => {
        if (!resourceManager) throw new Error("connector_mutate before resources ready");
      },
      hasConnector: (id) => resourceManager?.has?.(id) ?? false,
      isFlowConnector: (id) =>
        resourceManager?.has?.(id) === true &&
        (resourceManager.get(id).connector as { name?: string } | undefined)?.name === "flow",
      executeOp: (id, op, args) => {
        if (!resourceManager) throw new Error("connector_mutate before resources ready");
        return resourceManager.executeOp(id, op, args);
      },
      flowState: () => {
        const active = getOnlyActiveFlow();
        return {
          hasActiveFlow: activeFlows.size > 0,
          hasRunningFlow: hasNonTerminalFlow(activeFlows.values()),
          activeFlow: active ? { runId: active.runId, connectorId: active.connectorId } : null,
        };
      },
      createActiveFlow: async (flowDef, seedOrExecution) => {
        const entry = await createActiveFlow(flowDef, seedOrExecution);
        return {
          runId: entry.runId,
          connectorId: entry.connectorId,
          hydratedStateChanged: entry.hydratedStateChanged,
        };
      },
      waitForFlowConnector: (timeoutMs, isSatisfied) =>
        flowConnectorReadiness.wait(timeoutMs, isSatisfied),
      connectorWaitMs: flowMutateConnectorWaitMs,
      signalStimulus: (target, stimulus) => {
        // Fire-and-forget: turn 1 may run long or park at a gate, so the
        // acknowledgement must not block on it.
        void getOrCreateStimulusBus()
          .signal(target.connectorId, {
            promptFragment: renderStimulusPrompt(stimulus),
            meta: { ...(stimulus as unknown as Record<string, unknown>), runId: target.runId },
          })
          .catch((err) => {
            serverLog.warn("flow turn kick failed", {
              kind: stimulus.kind,
              runId: target.runId,
              error: err instanceof Error ? err.message : String(err),
            });
          });
      },
      log: serverLog,
    });
  }

  /** Generic connector query dispatch (was the `connector_query` case). */
  async function handleConnectorQuery(
    cmd: Extract<CommandEnvelope, { type: "connector_query" }>,
  ): Promise<void> {
    // Gate on session readiness — see handleConnectorMutate.
    try {
      await sessionReady;
    } catch (err) {
      sendEvent({
        type: "error",
        message: `Session not available: ${(err as Error).message}`,
        fatal: true,
      });
      return;
    }
    let ok = true;
    let result: string | undefined;
    let error: string | undefined;
    try {
      if (!resourceManager) throw new Error("connector_query before resources ready");
      // Same bare-"flow"-id rewrite as connector_mutate so hosts can
      // address the flow connector by its driver id without knowing
      // the per-run connectorId format.
      let dispatchId = cmd.id;
      if (!resourceManager.has?.(cmd.id) && cmd.id === "flow") {
        const onlyActive = getOnlyActiveFlow();
        if (onlyActive) {
          dispatchId = onlyActive.connectorId;
        }
      }
      result = await resourceManager.executeOp(dispatchId, cmd.op, cmd.payload ?? {});
    } catch (err) {
      ok = false;
      error = err instanceof Error ? err.message : String(err);
    }
    sendEvent({
      type: "connector_query_response",
      requestId: cmd.requestId,
      ok,
      ...(result !== undefined ? { result } : {}),
      ...(error !== undefined ? { error } : {}),
    });
  }

  /**
   * Compute the `debug_result` payload for a `debug` query (was the inner
   * switch of `handleDebug`). Pure introspection over the live driver /
   * session / resource manager — never throws the per-case work; the caller
   * wraps the whole call in a try/catch for defence in depth.
   */
  async function computeDebugResult(
    cmd: Extract<CommandEnvelope, { type: "debug" }>,
  ): Promise<unknown> {
    switch (cmd.query) {
      case "tools": {
        const mcpServers = Object.keys((driver as any)?.config?.mcpServers ?? {});
        const allowedTools = (driver as any)?.config?.allowedTools ?? null;
        return { mcpServers, allowedTools, driverStarted };
      }
      case "connectors": {
        return resourceManager?.listConnectors?.() ?? [];
      }
      case "mcp": {
        const servers = Object.entries((driver as any)?.config?.mcpServers ?? {}).map(([name]) => ({
          name,
        }));
        return { servers };
      }
      case "config": {
        // Read model from the live driver so changes via
        // reconfigure_agent are reflected in the debug panel.
        const liveModel = driver?.getModel() ?? agentSession?.model;
        return {
          driver: agentSession?.driverType,
          model: liveModel,
          provider: agentSession?.provider,
          maxTurns,
          secretsMode,
          driverStarted,
          projectDir: opts.projectDir,
          versions: getFrameworkVersions(),
        };
      }
      case "state": {
        const storeId = cmd.args?.store as string | undefined;
        if (storeId) {
          try {
            const snapshot = await resourceManager?.executeOp(storeId, "get", {});
            return snapshot ? JSON.parse(snapshot) : null;
          } catch (err) {
            return { error: err instanceof Error ? err.message : String(err) };
          }
        }
        const all = resourceManager?.listConnectors?.() ?? [];
        return { stores: all.map((c: { id: string }) => c.id) };
      }
      case "commands": {
        const commands = driver?.getSlashCommands?.() ?? [];
        return { commands, driverType: agentSession?.driverType };
      }
      default:
        return { error: `Unknown debug query: ${(cmd as any).query}` };
    }
  }

  /** Debug introspection handler (was the `debug` case). */
  async function handleDebug(cmd: Extract<CommandEnvelope, { type: "debug" }>): Promise<void> {
    if (!(await awaitSessionReadyOrFail())) return;
    serverLog.debug(`[serve] debug query=${cmd.query}`);
    let result: unknown;
    try {
      result = await computeDebugResult(cmd);
    } catch (err) {
      result = { error: err instanceof Error ? err.message : String(err) };
    }
    sendEvent({ type: "debug_result", query: cmd.query, data: result });
  }

  /** Graceful shutdown handler (was the `shutdown` case). Never returns. */
  async function handleShutdown(): Promise<void> {
    log("[serve] shutdown requested");
    await cleanup();
    await transport.close();
    teardownProcessHandlers();
    disposeLogStore();
    process.exit(0);
  }

  /** Capability round-trip result handler (was the `capability_result` case). */
  function handleCapabilityResult(
    cmd: Extract<CommandEnvelope, { type: "capability_result" }>,
  ): void {
    const matched = resolveCapabilityResult(pendingCapabilityCalls, cmd);
    if (!matched) {
      log(
        `[serve] capability_result for unknown callId=${cmd.callId} (timed out or already resolved); dropping`,
      );
    }
  }

  /** Mid-session capability registration handler (was the `capability_register` case). */
  function handleCapabilityRegister(
    cmd: Extract<CommandEnvelope, { type: "capability_register" }>,
  ): void {
    for (const raw of cmd.capabilities) {
      const wire = raw;
      capabilityRegistry.register(
        {
          ...wire,
          inputZod: { parse: (v: unknown) => v } as unknown as import("zod").ZodTypeAny,
          outputZod: undefined,
          handler: buildClientCapabilityHandler(wire),
        },
        "client",
      );
    }
  }

  /** Mid-session capability deregistration handler (was the `capability_deregister` case). */
  function handleCapabilityDeregister(
    cmd: Extract<CommandEnvelope, { type: "capability_deregister" }>,
  ): void {
    for (const name of cmd.names) {
      capabilityRegistry.deregister(name);
    }
  }

  /** Capability approval-decision handler (was the `capability_approve` case). */
  function handleCapabilityApprove(
    cmd: Extract<CommandEnvelope, { type: "capability_approve" }>,
  ): void {
    // Protocol v2.1 — the platform forwards the user's decision for any
    // capability declared `requiresApproval: true`. The platform itself
    // gates dispatch (it parks the `capability_invoked` until the user
    // responds), so:
    //
    //   - `approved`: the platform proceeds to dispatch and replies with
    //     `capability_result` through the normal path. We only audit-log
    //     here; rejecting/resolving the pending call would race the
    //     subsequent result.
    //   - `rejected`: the platform did NOT dispatch. Reject the pending
    //     call so the LLM sees a tool error explaining the user denied
    //     the action.
    if (cmd.decision === "rejected") {
      const matched = rejectCapabilityOnApprovalDeny(
        pendingCapabilityCalls,
        cmd.callId,
        cmd.feedback,
      );
      if (!matched) {
        log(
          `[serve] capability_approve(rejected) for unknown callId=${cmd.callId} (already resolved or timed out); dropping`,
        );
      } else {
        log(
          `[serve] capability_approve(rejected) callId=${cmd.callId} decidedBy=${cmd.decidedBy}${cmd.feedback ? ` feedback=${JSON.stringify(cmd.feedback)}` : ""}`,
        );
      }
    } else {
      log(
        `[serve] capability_approve(approved) callId=${cmd.callId} decidedBy=${cmd.decidedBy} (awaiting capability_result)`,
      );
    }
  }

  async function handleCommand(cmd: CommandEnvelope) {
    if (runtimeRejected || (managedHost && managedBootstrapClosed)) return;
    // v3: the access_token_response ingress is removed — refresh-on-401
    // rides on the host.refresh_credential capability invocation now.
    // SessionInitCommand is delivered alongside AgentCommand on the same
    // transport — type the parameter as the envelope union so the switch
    // can handle both.

    // Protocol v3 — inbound runner-cap invocation from the platform.
    // `capability_invoked` is modelled as an agent→client event, so it is not
    // in the AgentCommand union; the platform sends it as a command to drive an
    // audience:'runtime' cap. Mirror of the runner→platform invoker.
    const inbound = cmd as { type?: string; callId?: string; name?: string; input?: unknown };

    // A broadcast that a sibling already rotated the shared credential: re-pull
    // the fresh token from the host instead of each container force-refreshing
    // (the storm). When the broadcast names a different `configId` the same
    // re-pull moves this running session to that seat.
    if (isCredentialRotatedSignal(cmd)) {
      // Managed Codex refreshes through its dedicated account-bound callback.
      if (managedCodex) return;
      await handleCredentialRotatedSignal({
        configId: readCredentialRotatedConfigId(cmd),
        repull: async (configId) =>
          (
            await aiProviderConfigSwitcher.repull({
              trigger: "credentialRotated",
              ...(configId ? { configId } : {}),
            })
          ).mint,
        log,
        logError: (line: string) => serverLog.error(line),
      });
      return;
    }

    if (inbound.type === "capability_invoked") {
      await dispatchRunnerCapabilityInvocation(
        { callId: inbound.callId ?? "", name: inbound.name ?? "", input: inbound.input },
        {
          invoke: (name, input) => capabilityRegistry.invoke(name, input, { sessionId }),
          emit: (event) => sendEvent(event as unknown as AgentEvent),
        },
      );
      return;
    }
    switch (cmd.type) {
      case "session_init":
        await handleSessionInit(cmd);
        break;
      case "prompt":
        await handlePrompt(cmd);
        break;
      case "reply":
        await handleReply(cmd);
        break;
      case "cancel":
        await handleCancel();
        break;
      // v3: compact + lifecycle removed — reachable via runner.lifecycle capability
      // (the v3 `runner.lifecycle` cap takes `{ phase: 'hibernate' | 'close' | 'compact' }`).
      case "resource_request":
        await handleResourceRequestCommand(cmd);
        break;
      // v3: state_action removed — reachable via the runner.set_state capability.

      // ── Generic connector dispatch (Protocol 2.2) ──────────────────────
      // The 6 typed flow commands (`start_flow`, `approve_flow_node`,
      // `provide_flow_input`, `cancel_flow`, `set_flow_autonomous_mode`,
      // `retry_flow_node`) were removed in Phase 4 of the flow-connector
      // extraction. Hosts now drive flow execution exclusively through
      // `connector_mutate` against the `flow` connector. See
      // `MIGRATION-flow-connector.md` for the per-op mapping table.
      case "connector_mutate":
        await handleConnectorMutate(cmd);
        break;
      case "connector_query":
        await handleConnectorQuery(cmd);
        break;
      case "debug":
        await handleDebug(cmd);
        break;

      // -----------------------------------------------------------------------
      // -----------------------------------------------------------------------

      // v3: add_resource, remove_resource, remount_mount, set_log_level,
      // reconfigure_agent removed from AgentCommand union.
      // Reachable now via the `runner.set_log_level` and `runner.reconfigure`
      // capability invocations (audience: ['runtime']) — the platform calls
      // them through invokeRunnerCapability on the gateway. Handler bodies
      // live in the runner.* capability registrations.

      case "shutdown":
        await handleShutdown();
        break;

      // Protocol v2 — capability round-trip results from the platform.
      case "capability_result":
        handleCapabilityResult(cmd);
        break;

      // Protocol v2 — additional capability lifecycle commands. The platform
      // can register / deregister capabilities mid-session (e.g. when a user
      // installs an extension after `configure`). Approval routing is reserved
      // for a later phase; we accept the command shape for forward
      // compatibility and log it.
      case "capability_register":
        handleCapabilityRegister(cmd);
        break;
      case "capability_deregister":
        handleCapabilityDeregister(cmd);
        break;
      case "capability_approve":
        handleCapabilityApprove(cmd);
        break;
    }
  }

  // Wire transport commands to handler
  transport.onCommand((cmd) => {
    void handleCommand(cmd);
  });

  // A gateway that re-adopts this container after its own restart lost the ack it held in
  // memory and would re-wake the session — replayed ahead of the turn status. The signature
  // is live (the registry may have moved since the ack); the probe belongs to the init alone.
  transport.onConnect(() => {
    // A rejected gate is permanent whichever init's build rejected it, so the runner
    // can never serve a turn: "initialized" would keep a host on a dead container.
    if (!initAcknowledged || sessionReadyOutcome === "rejected") return;
    serverLog.debug("[serve] replaying session_init_ack on connect");
    // Built by allowlist so a per-init field added to the ack later cannot ride along.
    sendEvent({
      type: "session_init_ack",
      capabilitySignature: computeCapabilitySignature(capabilityRegistry.list()),
      replayed: true,
    });
  });

  // The gateway seeds every re-adopted session entry as idle. Replaying the
  // turn state first makes that seed correct within one round-trip instead of
  // until the next status event, and heals a `finished` lost while detached.
  transport.onConnect(() => {
    sendEvent(turnStatus.snapshot());
  });

  // Register before listen: the first authenticated client needs feature admission before init.
  transport.onConnect(() => {
    if (managedHost && !managedBootstrapClosed)
      sendEvent({
        type: "protocol_info",
        version: PROTOCOL_VERSION,
        runtimeFeatures: supportedRuntimeFeatures,
      });
  });

  // Start listening (no-op for transports with no startup phase).
  await transport.listen();

  // Start filesystem/connector watchers AFTER the transport is listening.
  // Watching can be slow on large directory trees (e.g. node_modules over
  // Docker bind mounts) and must not block the WebSocket readiness check.
  if (secretsMode === "env" && agentSession!) {
    agentSession!.startWatching();
  }

  // Emit available resources + cached slash commands to connected clients.
  // Fired on each client connect (the platform connects after the server
  // starts listening) and again after a session recreate, whose new
  // resourceManager would otherwise never be announced to the gateway.
  function emitAvailability(): void {
    if (resourceManager || agentSession?.getExternalMcpManager()) {
      const driverLive = agentSession?.driverType !== "claude-sdk";
      const payload = buildResourcesAvailablePayload(
        resourceManager,
        agentSession?.getExternalMcpManager() ?? null,
        driverLive,
        bakedMcpServerIds,
      );
      log(
        `[serve] emitting resources_available: ${payload.mounts.length} mount(s), ${payload.connectors.length} connector(s)`,
      );
      sendEvent(payload);
    }
    // Re-emit cached slash commands if the driver has already discovered them
    const commands = driver?.getSlashCommands?.() ?? [];
    if (commands.length > 0) {
      log(`[serve] emitting commands_available: ${commands.length} command(s)`);
      sendEvent({ type: "commands_available", commands });
    }
  }
  // Fires on client connect for WebSocket and the in-memory test pair; the
  // in-process transport registers the handler but never invokes it.
  transport.onConnect(() => {
    emitAvailability();
  });

  // Replay current store snapshots so a subscriber attaching after early host-op
  // mutations (the cold-spawn warmup window) converges to the present state.
  // Runs after emitAvailability; idempotent latest-wins on the host side.
  async function replayStoreSnapshots(): Promise<void> {
    if (!resourceManager) return;
    for (const event of await buildStoreSnapshotReplay(resourceManager, activeFlows.values())) {
      sendEvent(event);
    }
  }
  transport.onConnect(() => {
    replayStoreSnapshots().catch((err) => {
      serverLog.warn("store snapshot replay failed", {
        error: err instanceof Error ? err.message : String(err),
      });
    });
  });

  if (secretsMode === "env") {
    log(
      `[serve] driver=${agentSession!.driverType} model=${agentSession!.model ?? "(default)"} provider=${agentSession!.provider ?? "(default)"} maxTurns=${maxTurns ?? "(default)"}`,
    );
  } else {
    log(`[serve] secrets=provisioned — session will initialize after provision_secrets`);
  }
  log(`[serve] project=${opts.projectDir}`);

  installProcessHandlers();

  /**
   * Register the graceful-shutdown signal handlers. The uncaught-error safety
   * net is installed far earlier, beside the LogStore bootstrap; these must
   * wait because {@link initiateShutdown} closes over `cleanup` / `transport` /
   * `log` / `disposeLogStore`.
   *
   * Signal handling must be safe against:
   *   - Shutdown while session is still initializing (cleanup bounds its own wait).
   *   - Hanging mount unmount / connector disconnect / driver kill (deadline timer force-exits).
   *   - User impatience (second SIGINT/SIGTERM force-exits immediately).
   */
  function installProcessHandlers(): void {
    // Startup succeeded, so a stray async error should no longer be fatal.
    const stopSafetyNet = installProcessSafetyNet({ log, exitOnBrokenPipe });
    teardownProcessHandlers = () => {
      stopSafetyNet();
      stopOutputWatch();
    };

    const GRACEFUL_SHUTDOWN_TIMEOUT_MS = 5000;
    let shuttingDown = false;

    async function initiateShutdown(signal: "SIGINT" | "SIGTERM"): Promise<void> {
      if (shuttingDown) {
        log(`[serve] ${signal} received again — forcing exit`);
        process.exit(130);
      }
      shuttingDown = true;
      log(`[serve] ${signal} received — shutting down (press Ctrl-C again to force)`);

      const forceExitTimer = setTimeout(() => {
        log(`[serve] graceful shutdown exceeded ${GRACEFUL_SHUTDOWN_TIMEOUT_MS}ms — forcing exit`);
        process.exit(130);
      }, GRACEFUL_SHUTDOWN_TIMEOUT_MS);
      forceExitTimer.unref?.();

      try {
        await cleanup();
      } catch (err) {
        log(`[serve] cleanup error: ${(err as Error).message}`);
      }
      try {
        await transport.close();
      } catch (err) {
        log(`[serve] transport close error: ${(err as Error).message}`);
      }

      clearTimeout(forceExitTimer);
      log("[serve] shutdown complete");
      teardownProcessHandlers();
      disposeLogStore();
      process.exit(0);
    }

    process.on("SIGTERM", () => {
      void initiateShutdown("SIGTERM");
    });
    process.on("SIGINT", () => {
      void initiateShutdown("SIGINT");
    });
  }
}
